export default {
    en: {
        intake: {
            screeningUi: {
                estimate: "The questions adapt to your answers and selected topics, so the length can vary. You can return to continue from your saved progress.",
                answeredCount: "Questions answered: {count}",
                adaptiveLength: "The remaining questions depend on your answers.",
                topicContext: "About: {topics}",
                clarificationContext: "Help us clarify which topic is closest to your experience.",
                emptyChoices: "This question could not be prepared correctly. Your previous answers are saved. Reload this question or go back.",
                r8GuideTitle: "Help understanding this question",
                r8GuideIntro: "This question lists several different situations. Answer about your current situation; you are not being asked to make a medical diagnosis.",
                r8GuideUncertain: "If an expression is unclear or you do not know whether it applies, you can choose ‘I’m not sure’. This activates a review path; it does not by itself mean an immediate crisis. Restarting does not automatically remove an existing restriction.",
                retryQuestion: "Reload this question",
                retryingQuestion: "Reloading this question…",
                retryQuestionFailed: "This question could not be reloaded. Your previous answers are saved. Please try again or go back.",
                backQuestion: "Go back",
                wellbeingContext: "About your overall wellbeing, across your selected topics",
                historyContext: "About your past care and current circumstances",
                safetyContext: "About safety and the appropriate next step",
                preferencesContext: "About your availability and therapist preferences",
                serviceContext: "About this service and its participants",
                optionalPreference: "Optional — you can continue without selecting a preference.",
                flexibilityHelp: "All weekdays does not mean all hours. This question asks whether times outside your selected windows may also work.",
                pastCareHelp: "Tell us about specialist help you have received. We will ask separately about any care you are receiving now.",
                currentCareHelp: "This asks about support you receive now, separately from past treatment, so that any ongoing care can be taken into account.",
                recallTwoWeeks: "Over the past two weeks",
                screening: "How you have been feeling",
                ageLabel: "How old are you?",
                safetyIntro: "Please answer these questions about your current situation. Your answers help identify an appropriate next step. If this service is not suitable for your situation, we will show you a safer next step. Answers in this section are recorded confidentially.",
                restart: "Start again",
                restartConfirm: "Start a new intake? Your questionnaire progress and therapist selection will be reset. Existing bookings will remain available.",
                unavailable: "This form is temporarily unavailable. Please try again later.",
            },
            matchUi: {
                refreshMessages: "Refresh messages",
                restartNeeded: "This questionnaire needs to be completed again before we can show reliable recommendations.",
                refreshNeeded: "These recommendations need to be refreshed. Please try again.",
                count: "Available recommendations: {count}",
                empty: "No recommendations are available to choose right now. You can contact support for the next step.",
                declineAll: "None of these are suitable",
                declineHint: "You can ask support for help choosing a therapist. No booking or payment is required.",
                supportIntro: "Send this form to request help choosing a therapist. Your request is recorded after successful submission.",
                supportBody: "None of the suggested therapists are suitable. Please help me with the next step. Recommendation reference: {reference}",
            },
            bookingUi: {
                timezone: "Times are shown in {zone}.",
                loading: "Loading available times…",
                loadError: "We could not load the available times. Please try again.",
                retry: "Try again",
                empty: "There are no available times right now. You can choose another therapist.",
                preparingHold: "Checking and holding your selected time…",
                paymentUnavailable: "Payment is temporarily unavailable. Please try again later.",
            },
            title: "Let’s start with a little about your situation",
            intro: "This helps us put you on the right path.",
            progressLabel: "Onboarding progress",
            progressMeta: "Step {step} of {total}",
            serviceTitle: "Participants and service path",
            stepLabels: {
                participants: "Participants",
                topics: "Topics",
                priority: "Priority",
                severity: "Impact intensity",
                complexity: "History",
                safety: "Safety screen",
                crisis: "Safety response",
                availability: "Time & preference",
                review: "Review",
                match: "Match",
                profile: "Therapist profile",
                slots: "Session time",
                checkout: "Checkout",
                consent: "Consent",
                baseline: "Starting point",
                letter: "Letter",
            },
            u1Title: "Which part of life is most involved?",
            u1Legend: "Where has this difficulty affected your life most?",
            u1: {
                mood: "Mood and feelings",
                body: "Body and sleep",
                rel: "Relationships",
                work: "Work / study",
                other: "Other",
            },
            u2Title: "Which experiences feel closest to what you are going through these days?",
            u2Legend: "Which experiences feel closest to what you are going through these days?",
            u2: {
                a: "Worry and tension",
                b: "Low mood",
                c: "Relationship conflict",
                d: "Something else",
            },
            u3Title: "Among these options, which is closer to your experience?",
            u3Legend: "Among these options, which is closer to your experience?",
            u3: {
                yes: "It is close",
                partial: "Somewhat",
                no: "No",
            },
            u3Submit: "Confirm topic",
            intAgeTitle: "Participant age",
            intAgeLegend: "Enter the participant’s age",
            unsupportedTitle: "This combination needs another path",
            unsupportedBody: "You can change the participant combination or continue on another path.",
            unsupportedAction: "Edit participants",
            safeCoupleTitle: "Safety in the relationship",
            safeCouple: {
                coupleFear: "In this relationship, are you afraid of the other person’s reaction, or do you not feel safe speaking freely about the relationship?",
                coupleHarm: "In the past six months, has there been threat, severe control, sexual coercion, or physical harm in the relationship?",
            },
            safeFamilyTitle: "Safety in the family",
            safeFamily: {
                familyFear: "Is any family member at home afraid of someone else, or unable to speak freely and safely?",
                familyConcern: "Is there an active concern about violence, abuse, harm to self/others, or a child’s safety?",
            },
            safeYouthTitle: "Child / teen safety",
            safeYouth: {
                youthTalk: "Has the child/teen recently talked about dying, not being here, or harming themselves, or shown behavior like that?",
                youthConcern: "Is there an active concern about violence, abuse, serious neglect, running away, substance use, or danger to others?",
            },
            safeAcuteTitle: "Current states",
            p3Title: "Do these topics usually show up together?",
            p3Legend: "Do these topics usually show up together, or separately?",
            sevPerNeedTitle: "Impact of this topic on life",
            sevPerNeedSubmit: "Save and continue",
            sevPerNeed: {
                frequency: "For “{topic}”: in the past two weeks, how often was this present?",
                function: "For “{topic}”: how hard has it made daily tasks, self-care, work/study, or connection?",
                trend: "For “{topic}”: in recent weeks, what was the main trend?",
            },
            branch: {
                shellTitle: "Topic module",
                shellLead: "Continue to the topic-specific questions for your path.",
                submit: "Save and continue",
                scale: {
                    NONE: "Almost none",
                    LOW: "Low",
                    MED: "Medium",
                    HIGH: "High",
                },
                yesNo: {
                    yes: "Yes",
                    no: "No",
                },
                modules: {
                    anger: {
                        title: "Anger and reactions",
                        q1: "How often do the reactions happen?",
                        q2: "Have they caused serious consequences?",
                    },
                    anx: {
                        title: "Anxiety and fear",
                        q1: "How often does this anxiety usually show up?",
                        q2: "Because of it, do you avoid situations, people, or tasks?",
                    },
                    bipolar: {
                        title: "Sleep and energy shifts",
                        q1: "In these periods, how much have reduced sleep, higher energy, or sudden decisions affected your life?",
                        q2: "Was this change sudden compared with your usual state, and did others notice it too?",
                    },
                    couple: {
                        title: "Couple relationship",
                        q1: "In the past four weeks, how often have arguments or cut-offs happened?",
                        q2: "After conflict, how long does it usually take to reconnect acceptably?",
                    },
                    dep: {
                        title: "Mood",
                        q1: "How present has this state been in daily life recently?",
                        q2: "How hard has it made routine tasks, self-care, or connection?",
                    },
                    eating: {
                        title: "Food and body",
                        q1: "How much does this occupy your mind?",
                        q2: "Has it affected physical health, energy, or daily capacity?",
                    },
                    family: {
                        title: "Family relationships",
                        q1: "How often do tension or cut-offs happen?",
                        q2: "After family conflict, how well can you return to ordinary connection?",
                        q3: "How disrupted have essential family tasks become?",
                    },
                    health: {
                        title: "Health and pain",
                        q1: "How much has this affected mood, sleep, focus, or relationships?",
                        q2: "Is active medical follow-up needed or unfinished?",
                    },
                    identity: {
                        title: "Self and identity",
                        q1: "How much has this affected choices and relationships?",
                        q2: "How often do self-criticism or shame repeat?",
                    },
                    neuro: {
                        title: "Attention and development",
                        q1: "In how many settings is this problem seen?",
                        q2: "How much has it caused disorganization, falling behind, or performance drop?",
                    },
                    ocd: {
                        title: "Repetitive thoughts and behaviors",
                        q1: "How much time do the thoughts or behaviors take in a day?",
                        q2: "If you cannot follow them, how high does your anxiety go?",
                    },
                    parent: {
                        title: "Parent consultation about a child",
                        q1: "How often does the issue happen?",
                        q2: "In how many settings is it seen?",
                        q3: "How much has it disrupted family daily life or your relationship with the child?",
                        q4: "Does the child/teen also need direct assessment or treatment?",
                    },
                    perinatal: {
                        title: "Pregnancy and after",
                        q1: "Which period is this closer to?",
                        q2: "How much has it affected mood, sleep, safety, or daily capacity?",
                    },
                    psychosis: {
                        title: "Unusual experiences",
                        q1: "Recently, have you seen or heard things others do not notice, or held beliefs that feel very unusual to others?",
                    },
                    relationship: {
                        title: "Relationship",
                        q1: "How much has this affected overall mood, focus, or functioning?",
                        q2: "Has it become a present crisis?",
                    },
                    sexual: {
                        title: "Sexual health and intimacy",
                        q1: "How much has this affected relationship, intimacy, or mental state?",
                        q2: "Is there pain, coercion, medical concern, or strong avoidance?",
                    },
                    sleep: {
                        title: "Sleep",
                        q1: "How many nights a week does this sleep problem happen?",
                        q2: "The next day, how much does it affect energy, focus, or functioning?",
                    },
                    substance: {
                        title: "Addictive habits",
                        q1: "How hard has controlling this behavior become?",
                        q2: "Which areas has it affected?",
                    },
                    trauma: {
                        title: "Grief and trauma",
                        q1: "How much has this experience affected sleep, focus, relationships, or feeling safe?",
                        q2: "Are you still in a situation or relationship that creates unsafety?",
                    },
                    work: {
                        title: "Work and burnout",
                        q1: "How much has your performance changed?",
                        q2: "What does the burnout feel most like?",
                    },
                    youth: {
                        title: "Child mood and behavior",
                        q1: "How often is concerning mood or behavior seen?",
                        q2: "Where is it seen?",
                        q3: "How much has it affected school, friendships, sleep, food, or daily tasks?",
                        q4: "Compared with before, have you noticed a clear drop or change?",
                    },
                },
            },
            disp: {
                accountSupportAction: "Help with my account",
                accountSupportNote: "Support can help with your account or using the website. This is not emergency help, a clinical referral, or a callback request; do not wait for a support reply to use the help resources above. Do not include screening answers or clinical details.",
                accountSupportBody: "I need practical help with my account and how to use the website. Please tell me how to continue.",
                sharedIntro: "The usual therapist suggestion path does not continue in this situation. Use verified resources.",
                opsQueued: "Support request recorded for this account path.",
                more: {
                    title: "Let's clarify one answer to continue",
                    intro: "One answer isn't clear enough for a safe routine therapist suggestion. Continue where you left off — this is not a full restart.",
                    action: "Complete answers",
                    clarifySafety: {
                        title: "Let's clarify one answer for a safe suggestion",
                        intro: "You chose ‘I’m not sure’ about one or more current situations. This means clarification and review are needed, rather than a confirmed immediate crisis. You can review the question to correct a misunderstanding. An existing booking restriction still requires the care team’s review; changing an answer or restarting does not automatically clear it.",
                        action: "Review safety question",
                        helpAction: "If you're really unsure, see ways to get help",
                    },
                    terminalFinalized: {
                        title: "This screening can't continue from here",
                        intro: "This intake is already closed and can't be edited. Start a new screening, or contact support if you need help with this account.",
                        action: "Start a new screening",
                        supportHint: "If you need manual help with this account path, contact support.",
                    },
                },
                unsupported: {
                    title: "This combination needs another path",
                    intro: "This participant and age combination isn't supported for routine matching. Change who participates or their ages, or continue on another path.",
                    action: "Edit participants",
                },
                private: {
                    title: "Answer in a safe space",
                    action: "Back to safety questions",
                },
                specialist: {
                    title: "Guide to getting the right help",
                    action: "See ways to get help",
                    reviewTitle: "Your screening answers",
                    reviewAction: "Review my answers",
                    editAction: "Edit the last answer",
                    restartAction: "Start a new screening",
                    reviewUnavailable: "These answers cannot be edited here. You can start a new screening or get help using the options below.",
                },
                ops: {
                    title: "Coordination is needed",
                    action: "Open support form",
                },
            },
            clinical: {
                disclaimer: "Answers are not a diagnosis; they are only used to continue the screening path.",
                submit: "Save answers",
                likert: {
                    "0": "Not at all",
                    "1": "Several days",
                    "2": "More than half the days",
                    "3": "Nearly every day",
                },
                yesNo: {
                    yes: "Yes",
                    no: "No",
                },
                cssrsTiming: {
                    M3: "In the past 3 months",
                    Y1: "In the past year",
                    OLDER: "Longer ago",
                    NA: "Not applicable",
                },
                phq2: {
                    title: "How you have felt over the past two weeks",
                    i0: "Little interest or pleasure in doing things",
                    i1: "Feeling down, depressed, or hopeless",
                },
                phq9: {
                    title: "Continuing the mood check",
                    i0: "Little interest or pleasure in doing things",
                    i1: "Feeling down, depressed, or hopeless",
                    i2: "Trouble falling or staying asleep, or sleeping too much",
                    i3: "Feeling tired or having little energy",
                    i4: "Poor appetite or overeating",
                    i5: "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
                    i6: "Trouble concentrating on things, such as reading the newspaper or watching television",
                    i7: "Moving or speaking so slowly that other people could have noticed — or the opposite: being so fidgety or restless that you have been moving around a lot more than usual",
                    i8: "Thoughts that you would be better off dead, or of hurting yourself in some way",
                },
                gad2: {
                    title: "Worry over the past two weeks",
                    i0: "Feeling nervous, anxious, or on edge",
                    i1: "Not being able to stop or control worrying",
                },
                gad7: {
                    title: "Continuing the worry check",
                    i0: "Feeling nervous, anxious, or on edge",
                    i1: "Not being able to stop or control worrying",
                    i2: "Worrying too much about different things",
                    i3: "Trouble relaxing",
                    i4: "Being so restless that it is hard to sit still",
                    i5: "Becoming easily annoyed or irritable",
                    i6: "Feeling afraid as if something awful might happen",
                },
                cssrs: {
                    title: "A few questions about safety",
                    i0: "In the past month, have you wished you were dead, or wished you could go to sleep and not wake up?",
                    i1: "In the past month, have you actually had any thoughts of killing yourself?",
                    i2: "Have you been thinking about how you might do this?",
                    i3: "Have you had these thoughts and had some intention of acting on them?",
                    i4: "Have you started to work out or worked out the details of how to kill yourself? Do you intend to carry out this plan?",
                    i5: "Have you ever done anything, started to do anything, or prepared to do anything to end your life?",
                    i6: "How long ago did you last do this?",
                },
            },
            participantLegend: "Who will take part in the sessions?",
            participantHelp: "Choose the option closest to your situation.",
            participantRequired: "Complete this item.",
            participantHelpAria: "More about {name}",
            participantHints: {
                SELF: "Individual sessions for me",
                COUPLE: "Me and my partner",
                CHILD: "A child under 12 with an adult present",
                TEEN: "A teen aged 12 to 17",
                PARENT: "About my child",
                FAMILY: "More than two family members",
            },
            participantHelpText: {
                SELF: "Only you attend the sessions. The individual path focuses on your needs and goals.",
                COUPLE: "You and your partner attend together. Scheduling and consent from both matter.",
                CHILD: "For a child under 12. An adult usually joins, and the age is the child’s.",
                TEEN: "For a teen aged 12 to 17. Later questions match this age group.",
                PARENT: "A parent gets advice about their child without the child present. The child’s age is recorded.",
                FAMILY: "More than two family members attend. Age mix is asked later.",
            },
            continue: "Continue",
            ageSelf: "Enter the related age",
            ageRelated: "Enter the related age",
            ageChild: "Enter the child’s age",
            ageCoupleLegend: "Enter both ages",
            ageSelfLabel: "Your age",
            agePartnerLabel: "Partner’s age",
            agePlaceholder: "e.g. 28",
            agePartnerPlaceholder: "e.g. 30",
            ageRequired: "Enter a valid age.",
            ageRange: "Age must be between 1 and 120.",
            ageTeenRange: "Teen age must be between 12 and 17.",
            ageChildRange: "Child age must be 11 or under.",
            formIncomplete: "Complete the required items to continue.",
            trustPath: "Used only to continue this account’s path.",
            topicsTitle: "Topics you need help with",
            topicsLegend: "Which topics matter for getting help?",
            topicsHelp: "Open a category and choose one to {max} topics you want help with. If you are unsure, select “I’m not sure” instead.",
            topicsCount: "{count} of 6 selected",
            topicsUnsurePath: "“Not sure” path",
            topicsUnsureHint: "If the labels don’t fit, a few guiding questions follow.",
            topicsMin: "Choose at least one topic or “Not sure”.",
            topicsMax: "Choose up to six topics.",
            saving: "Saving…",
            priorityTitle: "Main priority",
            priorityIntro: "When several topics are selected, this shows which change would matter most, or whether the topics depend on each other.",
            priorityLegend: "If only one of these topics improved in the next few weeks, which improvement would make the biggest difference in your life?",
            linkedLegend: "Do these topics usually show up together, or separately?",
            linked: {
                together: "Usually together",
                separate: "Separately",
                unsure: "I don’t know",
            },
            fieldRequired: "Complete this item.",
            priorityConnected: "I’m not sure; they’re connected",
            priorityRequired: "Choose a starting point.",
            severityTitle: "Impact on daily life",
            severityIntro: "Record how strongly the main issue affects daily life, from low to very high.",
            intensityLegend: "For “{topic}”: if zero means almost no effect and ten means it is very hard to bear, how intense is it right now?",
            intensityLegendGeneric: "If zero means almost no effect and ten means it is very hard to bear, how intense is this right now?",
            intensityScaleAria: "Intensity from 0 to 10",
            frequencyLegend: "In the past two weeks, how often has this been present?",
            frequency: {
                NEVER: "Not at all",
                FEW_DAYS: "A few days",
                MORE_THAN_HALF: "More than half the days",
                ALMOST_DAILY: "Almost every day",
            },
            functionLegend: "How hard has it made daily tasks, self-care, work/study, or relating?",
            functionDifficulty: {
                NONE: "Almost none",
                LOW: "A little",
                MEDIUM: "Moderate",
                HIGH: "A lot",
            },
            recentTrendLegend: "In recent weeks, what has the trend mostly been?",
            recentTrend: {
                BETTER: "Better",
                SAME: "About the same",
                WORSE: "Worse",
                VOLATILE: "Highly variable",
            },
            severityFocusConnected: "the topics you chose",
            severityFocus: "About {focus}",
            severityLegend: "In the past two weeks, how much has this affected daily life?",
            severityRequired: "Choose one option.",
            severity: {
                LOW: "A little; I can still do most things",
                MEDIUM: "Moderate; some parts have gotten harder",
                HIGH: "A lot; several parts of my life are disrupted",
                VERY_HIGH: "Very much; ordinary tasks are hard for me",
            },
            complexityTitle: "Duration, scope, and history",
            complexityIntro: "Record how long this has been present, which life areas are involved, and prior care.",
            historyDurationLegend: "How long has this been with you?",
            historyDuration: {
                LT_1M: "Less than 1 month",
                M1_3: "1 to 3 months",
                M3_12: "3 to 12 months",
                GT_1Y: "More than 1 year",
                CHILDHOOD: "Since childhood/adolescence",
            },
            priorHelpLegend: "Have you already gotten specialized help for this?",
            priorHelp: {
                none: "Not yet",
                therapy: "Psychotherapy/counseling",
                psych: "Psychiatry",
                other: "Other help",
            },
            priorOutcomeLegend: "If you had therapy or counseling before, how did it feel to you?",
            priorOutcome: {
                helped: "It helped",
                mixed: "Mixed",
                not: "It did not help",
                na: "Not applicable",
            },
            currentCareLegend: "Are you currently seeing a psychotherapist, psychiatrist, or another doctor for this?",
            currentCare: {
                none: "No",
                therapist: "Psychotherapist",
                psychiatrist: "Psychiatrist",
                doctor: "Another doctor",
            },
            needsCoordinationLegend: "For starting new treatment, does the therapist need to coordinate with another specialist?",
            needsCoordination: {
                yes: "Yes",
                no: "No",
                unsure: "Not clear yet",
            },
            involvedDomainsLegend: "Are several life areas or more people than usual for this kind of session actively involved right now?",
            involvedDomains: {
                work: "Work/study",
                home: "Home",
                health: "Health",
                others: "Other people",
                none: "No / that is usual",
            },
            goalAlignmentLegend: "How aligned are the people who will be in the session about the main treatment goal?",
            goalAlignment: {
                aligned: "Aligned",
                partial: "Somewhat",
                not: "Strong disagreement",
                self_only: "Only me",
            },
            caregiverAvailableLegend: "Is there a trusted adult who can follow the child/teen treatment plan?",
            caregiverAvailable: {
                yes: "Yes",
                no: "No",
                na: "Not applicable",
            },
            durationLegend: "How long has this been with you?",
            impactLegend: "How many parts of your life has it affected?",
            trendLegend: "How has it gone in the past month?",
            complexityRequired: "Choose one option for each question.",
            duration: {
                WEEKS: "A few weeks",
                MONTHS_1_6: "One to six months",
                MONTHS_6_12: "Six months to a year",
                YEARS: "More than a year",
            },
            impact: {
                ONE: "Only one part",
                SEVERAL: "Several parts; like work, sleep, or relationships",
                MOST: "Almost all the important parts",
            },
            trend: {
                BETTER: "It has gotten better",
                SAME: "It has stayed about the same",
                WORSE: "It has gotten harder",
            },
            safetyTitle: "Safety screen",
            safetyIntro: "Answers are confidential and are not used for therapist suggestion or general analysis.",
            safetySubmit: "Save safety answers",
            safetyRedirectCrisis: "Opening the safety guide…",
            safetyContinueOk: "Continuing…",
            yesNo: {
                yes: "Yes",
                no: "No",
            },
            safetyScreen: {
                wishDeadLegend: "In the past month, have you wished you were dead, or that you could go to sleep and not wake up?",
                ideationLegend: "In the past month, have you actually thought about ending your life?",
                methodLegend: "Have you thought about how you might do this?",
                intentLegend: "Have you had these thoughts and also had some intention of acting on them?",
                planLegend: "Have you worked out details or started planning, and intended to carry it out?",
                behaviorLegend: "Have you ever done anything, started to do anything, or prepared to do anything to end your life?",
                lastTimeLegend: "When was the last time?",
                acuteStatesLegend: "Is any of these present right now?",
                domesticThreatLegend: "In a relationship or at home, does anyone scare, control, threaten, coerce, or harm you?",
            },
            safetyLastTime: {
                '3m': "In the past 3 months",
                year: "In the past year",
                older: "Longer ago",
                na: "Not applicable",
            },
            safetyAcute: {
                none: "No / none",
                mania: "Severe sleeplessness and unusual energy",
                psychosis: "Intense unusual experiences",
                intox: "Active dangerous intoxication/use",
                medical: "Medical/nutritional instability",
            },
            safetyDomestic: {
                no: "No",
                yes: "Yes",
                unsure: "Not sure",
            },
            safetyLegend: "Is any of these true for you right now?",
            safetyHelp: "You can choose more than one. Your answer is confidential and used for safe care.",
            safetyMin: "Choose at least one option.",
            safety: {
                SELF_HARM: "Thoughts of harming yourself or ending your life",
                HARM_OTHER: "Thoughts of harming someone else",
                VIOLENCE: "Violence, threats, or feeling unsafe at home",
                PSYCHOSIS: "Seeing or hearing things others don’t experience",
                MANIA: "Several sleepless nights with unusual energy or behavior",
                MEDICAL: "A physical or substance situation that may need urgent help",
                NONE: "None of these is present",
            },
            crisisTitle: "Safety response",
            crisisSafetyTitle: "Your safety comes first right now",
            crisisIntro: "The usual therapist-suggestion path does not continue in this situation. Use verified resources.",
            crisisHeading: "It’s better not to stay alone right now.",
            crisisBody: "Your answer suggests you may need more urgent help. Roobroo is not emergency care and will not suggest a therapist at this stage.",
            crisisCall: "Call 123",
            crisisEdit: "Change my answer",
            crisisSeeHelp: "See urgent help options",
            crisisContactCall: "Call",
            crisisContact: {
                social: "Social emergency 123",
                addiction: "National addiction / crisis line 1480",
                emergency: "Emergency 115",
            },
            emergencyAria: "Emergency numbers",
            emergencyLead: "This path is not for urgent or emergency situations.",
            emergencyIfIran: "If you are in Iran and there is immediate danger:",
            emergency: {
                social: "Social emergency",
                health: "Deputy of Health",
                rahava: "Rahava crisis line",
                police: "Police",
            },
            availabilityTitle: "Days, times, and preferences",
            daysLegend: "Which days usually work better for a session?",
            timesLegend: "Which times of day work better for you?",
            slotsLegend: "Across the week, which days and time ranges usually work for a session?",
            flexibilityLegend: "If nothing fits your ideal time, how much can you move the session?",
            flexibility: {
                low: "A little",
                mid: "Moderately",
                high: "A lot",
            },
            genderLegend: "Is the therapist’s gender necessary for starting and continuing treatment?",
            genderPref: {
                any: "No preference",
                woman: "Necessary: woman",
                man: "Necessary: man",
            },
            livedExperienceLegend: "Does the therapist need specific lived experience or knowledge of one of these areas?",
            livedExperience: {
                none: "No",
                lgbt: "Identity and orientation",
                faith: "Cultural/religious background",
                chronic: "Chronic illness",
                migration: "Migration",
            },
            livedExperienceNeedLegend: "How necessary is that knowledge for you?",
            livedExperienceNeed: {
                soft: "Soft preference",
                hard: "Necessary",
                na: "Not applicable",
            },
            dayShort: {
                SAT: "Saturday",
                SUN: "Sunday",
                MON: "Monday",
                TUE: "Tuesday",
                WED: "Wednesday",
                THU: "Thursday",
                FRI: "Friday",
            },
            slot: {
                MORNING: "Morning",
                NOON: "Noon",
                AFTERNOON: "Afternoon",
                EVENING: "Evening",
            },
            availabilityRequired: "Choose at least one day, one time, and a therapist preference.",
            reviewAnswers: "Review answers",
            day: {
                SAT: "Saturdays",
                SUN: "Sundays",
                MON: "Mondays",
                TUE: "Tuesdays",
                WED: "Wednesdays",
                THU: "Thursdays",
                FRI: "Fridays",
            },
            timeOfDay: {
                MORNING: "Morning",
                NOON: "Noon",
                AFTERNOON: "Afternoon",
                EVENING: "Evening",
            },
            gender: {
                FEMALE: "A woman therapist",
                MALE: "A man therapist",
                NO_PREFERENCE: "No preference",
            },
            path: {
                SELF: "Individual path",
                COUPLE: "Couples path",
                CHILD: "Child path",
                TEEN: "Teen path",
                PARENT: "Parent consultation path",
                TOGETHER: "Teen path",
                FAMILY: "Family path",
            },
            reviewTitle: "Review answers",
            reviewIntro: "Review your answers; you can change any section.",
            reviewPath: "Path",
            reviewTopics: "Topics",
            reviewStart: "Starting point",
            reviewWhen: "Preferred time",
            reviewGender: "Therapist gender",
            reviewEdit: "Edit",
            reviewConnected: "The topics are connected",
            reviewGroupPath: "Participants and path",
            reviewGroupTopics: "Topics and priority",
            reviewGroupSeverity: "Intensity and history",
            reviewGroupSafety: "Safety",
            reviewGroupSchedule: "Time and preferences",
            reviewAgeSelf: "age {age}",
            reviewAgeChild: "age {age}",
            reviewAgePartner: "partner age {age}",
            reviewPriorityValue: "priority: {topic}",
            reviewIntensityValue: "intensity {value}",
            reviewTrendValue: "trend {value}",
            reviewPriorHelpValue: "prior help: {value}",
            reviewGenderValue: "gender: {value}",
            reviewSafetyClear: "Screening completed · raw answers not shown",
            reviewSafetyFlagged: "Screening completed · safety path opened",
            reviewEmpty: "—",
            reviewPrivacyNote: "Raw safety answers and clinical scores are not shown in this review.",
            listJoin: " · ",
            privacyTitle: "What is shown to the therapist?",
            privacyBody: "Only what is needed to start therapy and keep care safe. Sensitive safety answers are not shown on the therapist suggestion card.",
            findMatches: "See suggestions",
            matchingTitle: "We're preparing your suggestions",
            matchingIntro: "Next you'll see therapists and the fee for each session, then choose one.",
            matchingRetry: "Try again",
            matchingStepPath: "Right path",
            matchingStepSkill: "Qualification and capacity",
            matchingStepTimes: "Available times",
            recommendationNotActive: "This suggestion is no longer active. Run match again to choose another therapist.",
            slotOrQuoteStale: "That time or price is no longer available. Please choose another live slot.",
            rematchAction: "Match again",
            matchesTitle: "Therapist suggestions",
            matchesIntro: "You can see the reason for each suggestion, the fee, and real times before you choose.",
            matchesUnderstood: "The suggested therapists fit your answers in qualification, capacity, and available times.",
            recommendBadge: "Roobroo’s first suggestion",
            whyMatch: "What is this suggestion based on?",
            nearestSlot: "Nearest time",
            sessionPrice: "Fee per session",
            toman: "Toman",
            selectTherapist: "Choose a time and book",
            selectTimeAndBook: "Choose a time and book",
            matchReflect: "Based on your answers, we considered these topics for suggestions:",
            whyThisSuggestion: "Why this suggestion?",
            verifiedTherapist: "Verified therapist",
            sessionsHeldBadge: "+{count} successful sessions held",
            sessionsHeldBadgeWords: {
                t1: "+eighty-five successful sessions held",
                t2: "+one hundred ten successful sessions held",
                t3: "+one hundred forty successful sessions held",
            },
            sessionOnlineHour: "One-hour online session",
            priceNote: "A different fee is not a ranking of therapy quality; it relates to contract level and session length.",
            sessionsProofLabel: "Successful sessions held",
            sessionsProofAria: "Session history",
            approachLabel: "Approach: {approach}",
            bookNearestWith: "Book the nearest time with {name}",
            viewAllSlots: "See all open times",
            sessionFactsAria: "Session details",
            capabilityAria: "Competence areas and approach",
            matchExpiredTitle: "Suggestions need a refresh",
            matchExpiredBody: "These suggestions have expired. Your onboarding choices are kept, and you can get fresh suggestions.",
            matchInvalidTitle: "That suggestion is no longer bookable",
            matchInvalidBody: "This suggestion is no longer available to book.",
            matchRefreshAction: "Get fresh suggestions",
            matchGapTitle: "No suitable option right now",
            matchGapBody: "No eligible therapist with an open time was found right now. Trying the same search again will not create new availability.",
            matchGapRequestAction: "Request follow-up to book a session",
            matchGapBackAction: "Back to screening",
            matchGapRequestDone: "Follow-up request recorded.",
            matchRetryableTitle: "Temporary matching problem",
            matchRetryableBody: "A temporary dependency failed. You can try matching again.",
            matchRetryableAction: "Try again",
            matchNoslotTitle: "No suitable time found",
            matchNoslotBody: "You can check other times or adjust your preferences. This does not mean you are disqualified.",
            matchNoslotOtherTimesAction: "Check other times",
            matchNoslotEditPrefsAction: "Edit time preferences",
            rematchOfferTitle: "Want to look at another option?",
            rematchCurrentTherapist: "Current therapist: {name}",
            rematchOfferReason: "If you want, you can review alternatives. The offer alone does not change your current therapy or booking.",
            rematchBulletReject: "You can decline the suggestion.",
            rematchBulletUnchanged: "Until you confirm, your current care stays unchanged.",
            rematchAcceptAction: "Request a therapist change",
            rematchDeclineAction: "Not now",
            rematchDecideTitle: "Decide about changing therapist",
            rematchDecideQuestion: "Which path do you want?",
            rematchSeeOthers: "I want to see another option",
            rematchStayCurrent: "I will continue with my current therapist",
            rematchStayHint: "I decline the change suggestion",
            rematchRunTitle: "Looking at new options",
            rematchReviewTitle: "Therapist change request",
            rematchManualReviewPending: "Your therapist-change request is recorded for staff review. Your current care remains active. Booking and credit arrangements need to be reviewed before a replacement booking can be made. No response deadline is promised.",
            rematchReviewRefresh: "Refresh request status",
            rematchSupportAction: "Contact support",
            rematchBookingInProgress: "A replacement booking is already in progress. Continue from your sessions or ask support before starting another booking.",
            rematchStatusUnavailable: "The therapist-change status could not be loaded. Refresh or ask support for help. Your current care has not been changed.",
            rematchNoneActive: "There is no active therapist-change offer. You can continue your current care or contact support.",
            rematchDecisionFailed: "Your decision could not be confirmed. Try again; your current care has not been changed.",
            rematchSafetyBlocked: "The latest screening did not allow routine rematching. Follow the intake guidance or ask support about your account. Your current care remains in place.",
            rematchRunStatus: "Reviewing alternative options…",
            rematchCheckKeep: "Current care stays until a new booking",
            rematchCheckEligible: "Only eligible, bookable options",
            rematchCheckRetry: "If something fails, you can try again",
            rematchCheckStatusAction: "Check new suggestion status",
            rematchExcluded: "Matching is not available for this account right now.",
            rematchRunFail: "Could not refresh suggestions. Try again.",
            rematchRunDone: "New options are ready.",
            rematchSafetyReviewPending: "A required safety review is still pending. Your current care remains in place and matching will not run until that review is complete.",
            rematchValueReviewPending: "Your therapist change needs a payment or credit review. Your previous care is not closed automatically. Ask support before making another payment or booking.",
            rematchFailTitle: "No alternative found this time",
            rematchFailBulletNoSafe: "No safe alternative was recorded this time.",
            rematchFailBulletUnchanged: "Your current care stays unchanged.",
            rematchFailBulletRetryLater: "You can try again later if you need to.",
            rematchBackToCareAction: "Back to current care",
            rematchRetryAction: "Try again",
            capability: {
                anxiety: "Anxiety",
                sleep: "Sleep",
                work: "Work and burnout",
                mood: "Mood",
                trauma: "Trauma",
                adult: "Adult",
            },
            approach: {
                cbt: "Cognitive behavioral",
                integrative: "Integrative",
                psychodynamic: "Psychodynamic",
            },
            viewProfile: "View profile",
            pricingTitle: "Why are session fees different?",
            pricingBody: "A different fee is not a ranking of therapy quality; it relates to contract level and session length.",
            matchesFootnote: "The order shows how well they match the answers in this path; it is not a ranking of therapists as good or bad.",
            whyTitle: "Basis for suggesting {name}",
            evidenceTitle: "Technical view",
            whyNote: "This text is built from approved pieces based on the client’s answers and that therapist’s real evidence; claims without data are not added.",
            close: "Close",
            therapistSelected: "Selected. Open the profile to continue, or wait for booking times next.",
            profileTitle: "Suggested therapist profile",
            profileAboutTitle: "About the way of working",
            profileAboutBody: "The first sessions focus on clarifying the issue, a shared goal, and building a path that makes sense. The therapy process is adjusted with your feedback.",
            profileWhyTitle: "Why suggested for your path?",
            profileCredentialsTitle: "Professional information",
            profileCredentialLicense: "Verified license and professional identity",
            profileCredentialTopic: "Valid, versioned topic qualification",
            profileCredentialCapacity: "Capacity to take a case at the current level",
            profilePerSession: "Per session",
            profileViewSlots: "See times",
            profileSlotsSoon: "Times will open on the next step. Booking is not connected yet.",
            profileTagAdult: "Adult",
            profileTagAnxiety: "Anxiety and day-to-day pressure",
            profileTagFa: "Persian",
            slotsTitle: "Choose a session time",
            slotsIntro: "Choose a time. We will confirm a temporary hold before payment.",
            slotsDateLegend: "Choose the session day",
            slotsTimeLegend: "Available times",
            slotsTimezoneNote: "All times are shown in your local timezone.",
            slotsTzLabel: "Time zone",
            slotsTzValue: "Tehran (UTC+3:30)",
            slotsMonthNext: "Next month",
            slotsMonthPrev: "Previous month",
            slotsMonthOther: "Demo month only",
            slotsMonthNavDemo: "Only the sample month has open slots in this prototype.",
            slotsMonthShort: "Mordad",
            slotsCalAria: "Session calendar",
            slotsCalLegendAria: "Calendar legend",
            slotsCalLegendAvail: "Days with open slots",
            slotsCalLegendEmpty: "No slots",
            slotsCalLegendSelected: "Selected day",
            slotsDow: "Sa Su Mo Tu We Th Fr",
            slotsDayAvailable: "Day {day}, open slots",
            slotsDayEmpty: "Day {day}, no slots",
            slotsPickDay: "a day",
            slotsTimesForDate: "Open times for {date}",
            slotsFilterAria: "Time of day filters",
            slotsFilter: {
                morning: "Morning",
                day: "Afternoon",
                night: "Evening",
            },
            slotsListAria: "Available times",
            slotsEmptyPeriod: "No open slots in this period.",
            slotsPolicyNote: "Cancellation or rescheduling terms are shown only when the current approved policy is available.",
            slotsPriceSuffix: "per session",
            slotsContinue: "Continue and book",
            slotsBackProfile: "Back to profile",
            slotsSavedNote: "Time saved. Checkout and payment come next — not connected yet.",
            holdTitle: "This time is temporarily held for you",
            holdHint: "The hold lasts about 10 minutes",
            holdSummaryAria: "Hold summary",
            holdSummaryTime: "Time",
            holdSummaryTz: "Time zone",
            holdSummaryAmount: "Amount",
            holdSummaryPayMethod: "Payment method",
            holdPayMethodValue: "Secure online gateway",
            holdContinuePay: "Continue to payment",
            holdChangeTime: "Change time",
            conflictTitle: "This time is no longer available",
            conflictBody: "The time you chose can no longer be booked. Your amount and preferences are kept; you can pick a fresh open slot.",
            conflictSummaryAria: "Conflict summary",
            conflictChosenTime: "Chosen time",
            conflictStatusLabel: "Status",
            conflictStatus: "No longer available",
            conflictPickOther: "Choose another time",
            holdExpTitle: "The hold has expired",
            holdExpBody: "The 10-minute hold on this time has ended. To continue, choose an open slot again.",
            holdExpSummaryAria: "Expired hold summary",
            holdExpPrevTime: "Previous time",
            holdExpPayStatusLabel: "Payment status",
            holdExpPayStatus: "Not paid yet",
            holdExpPickOther: "Choose another time",
            rescheduleTitle: "Change session time",
            rescheduleSummaryAria: "Reschedule summary",
            rescheduleCurrent: "Current time",
            rescheduleDeadline: "Change deadline",
            rescheduleDeadlineValue: "Policy not supplied",
            rescheduleExtraPay: "Extra payment",
            rescheduleExtraPayValue: "Not defined",
            rescheduleAltsLegend: "Alternative times",
            rescheduleAltsAria: "Alternative times",
            rescheduleConfirm: "Save new time",
            rescheduleDismiss: "Cancel",
            rescheduleSavedNote: "New time saved.",
            rescheduleNoSlots: "No open times right now. Try again later.",
            rescheduleSlotsFail: "Could not load alternative times.",
            rescheduleSaveFail: "Could not save the new time.",
            cancelSessionTitle: "Review session cancellation",
            cancelSessionSummaryAria: "Cancellation summary",
            cancelSessionWhen: "Session",
            cancelSessionRemaining: "Time remaining",
            cancelSessionRemainingValue: "\u2014",
            cancelSessionCredit: "Credit effect",
            cancelSessionCreditValue: "Value outcome not defined",
            cancelSessionNote: "Unpaid booking cancellation has no refund or credit effect. Paid/value-changing cancellation remains blocked until an approved policy exists.",
            cancelSessionPartialNote: "Booked sessions cannot be cancelled here yet. Contact support, or cancel an unpaid journey reservation if you have one.",
            cancelSessionConfirm: "Cancel unpaid booking",
            cancelSessionKeep: "Keep session",
            cancelSessionSavedNote: "Reservation cancelled.",
            cancelSessionSaveFail: "Could not cancel this reservation.",
            bookingSpaceTherapist: "Therapist space",
            bookingSpaceSupport: "Support space",
            bkDismiss: "Cancel",
            bkStubSaved: "Saved in this prototype (not connected yet).",
            bkWeekDay: {
                sat: "Saturday",
                sun: "Sunday",
                mon: "Monday",
                tue: "Tuesday",
                wed: "Wednesday",
            },
            bkT02Title: "Edit weekly schedule",
            bkT02WeekAria: "Weekly availability",
            bkT02Off: "On leave",
            bkT02Ranges: "{n} ranges",
            bkT02NoRange: "No ranges",
            bkT02Policy: "Overlaps and affected booked sessions are checked before save.",
            bkT02Save: "Review and save schedule",
            bkT03Title: "Add leave",
            bkT03From: "From date",
            bkT03To: "To date",
            bkT03FromDefault: "22 Mordad 1404",
            bkT03ToDefault: "25 Mordad 1404",
            bkT03Reason: "Operational reason",
            bkT03ReasonDefault: "Planned leave",
            bkT03Affected: "Affected bookings",
            bkT03AffectedValue: "2 sessions · clients need notice",
            bkT03Blocked: "Blocked slots",
            bkT03BlockedValue: "8 slots in range",
            bkT03Save: "Save leave",
            bkT04Title: "Cancel session as therapist",
            bkT04Client: "Client",
            bkT04ClientValue: "M. Rezaei",
            bkT04Session: "Session",
            bkT04SessionValue: "18 Mordad · Monday 18:30",
            bkT04Credit: "Credit",
            bkT04CreditValue: "Full refund to client",
            bkT04Save: "Cancel and refund credit",
            bkTOnceTitle: "Add one-off hours",
            bkTOnceDate: "Date",
            bkTOnceStart: "Start",
            bkTOnceEnd: "End",
            bkTOnceDateDefault: "21 Mordad 1404",
            bkTOnceStartDefault: "14:00",
            bkTOnceEndDefault: "16:00",
            bkTOncePolicy: "This range is outside the weekly schedule and applies only that day. Overlaps are checked.",
            bkTOnceSave: "Save one-off hours",
            bkS01Title: "Book for a client",
            bkS01Client: "Client",
            bkS01ClientValue: "N. Ahmadi · identity verified",
            bkS01Therapist: "Therapist",
            bkS01Slot: "Slot",
            bkS01SlotValue: "19 Mordad · Tuesday 10:00",
            bkS01Amount: "Credit / amount",
            bkS01AmountValue: "480,000 toman · payable",
            bkS01Consent: "Consent and proxy reason",
            bkS01ConsentDefault: "Booking at the client’s request via support with explicit consent",
            bkS01Policy: "The same eligibility, slot, credit, and payment rules as self-service apply.",
            bkS01Save: "Review client booking",
            bkS01BackQueue: "Back to queue",
            bkS02Title: "Booking and scheduling queue",
            bkS02QueueAria: "Support queue",
            bkS02Open: "Open booking request",
            bkS02Item: {
                a: {
                    name: "N. Ahmadi",
                    badge: "No slot",
                    detail: "Cause: no free capacity · Owner: support · Due: today 18:00 · Action: proxy booking",
                },
                b: {
                    name: "M. Karimi",
                    badge: "Time conflict",
                    detail: "Cause: slot taken concurrently · Last event: 12 minutes ago · Action: suggest alternatives",
                },
                c: {
                    name: "Sara Mohammadi",
                    badge: "Near SLA",
                    detail: "Cause: schedule change request · Owner: support · Action: proxy schedule change",
                },
            },
            bkS03Title: "Change schedule on behalf of therapist",
            bkS03Therapist: "Therapist",
            bkS03Authority: "Authority proof",
            bkS03AuthorityValue: "Ticket #4182 · written approval",
            bkS03Before: "Before",
            bkS03BeforeValue: "Monday 18:00–21:00",
            bkS03After: "After",
            bkS03AfterValue: "Monday 17:00–20:00",
            bkS03Reason: "Change reason",
            bkS03ReasonDefault: "Therapist request to shift evening range",
            bkS03Save: "Review and save change",
            checkoutTitle: "Finish your booking",
            checkoutSuccessTitle: "Payment successful",
            checkoutHoldTitle: "Your session time is on hold",
            checkoutHoldExpired: "The hold expired. Go back and choose a time again.",
            checkoutPayTitle: "Secure payment",
            checkoutPayIntro: "After payment you will complete the consent form and a few short first-session prep questions.",
            checkoutPayCard: "Online card payment",
            checkoutPayAction: "Pay",
            checkoutPayActionAmount: "Pay {amount} {currency}",
            checkoutOrderTitle: "Booking summary",
            checkoutOrderDate: "Date",
            checkoutOrderTime: "Time",
            checkoutOrderSession: "Session",
            checkoutOrderAmount: "Amount",
            checkoutSessionValue: "Online · {minutes} minutes",
            checkoutSessionOnline: "Online",
            checkoutProcessingTitle: "Confirming payment…",
            checkoutProcessingBody: "Do not close this page.",
            checkoutBookedTitle: "Your session is booked",
            checkoutBookedSummary: "{date}, {time} with {name}",
            checkoutBookingCode: "Booking code",
            checkoutPrepare: "Prepare for the first session",
            checkoutDashboard: "Go to dashboard",
            checkoutPrepareSoon: "Consent and prep questions come next — not connected yet.",
            checkoutMockNote: "This is a mock payment. No money is charged.",
            pay01Title: "Review booking and payment method",
            pay01HoldHint: "Time hold remaining",
            pay01SummaryAria: "Booking summary",
            pay01MethodLegend: "Payment method",
            pay01MethodGateway: "Online gateway",
            pay01MethodGatewayHint: "Secure card payment",
            pay01MethodPackage: "Use package credit",
            pay01MethodPackageHint: "If enough balance is available",
            pay01Continue: "Continue to payment",
            pay02Title: "Payment in progress",
            pay02Body: "Confirming payment with the gateway…",
            pay02SummaryAria: "Payment status summary",
            pay02Ref: "Booking reference",
            pay02BookingStatus: "Booking status",
            pay02BookingActive: "Use server booking state",
            pay02Check: "Check payment status",
            pay03Title: "Payment successful",
            pay03SummaryAria: "Payment confirmation",
            pay03SessionTime: "Session time",
            pay03AmountConfirmed: "Confirmed amount",
            pay03Receipt: "Receipt number",
            pay03Note: "Confirmation is shown only when the server booking state is CONFIRMED.",
            pay03Prepare: "Prepare for the first session",
            pay03Calendar: "Add to calendar",
            pay03CalendarSoon: "Calendar export is not connected yet.",
            payFailTitle: "Payment failed",
            payFailBody: "Payment was not confirmed and the session is not firmly booked. You can try again before the hold expires.",
            payFailSummaryAria: "Failed payment summary",
            payFailConfirmStatus: "Confirmation status",
            payFailConfirmValue: "Use server status",
            payFailRetry: "Try payment again",
            payMismatchTitle: "Payment needs review",
            payMismatchBody: "The confirmed amount does not match the expected amount. Until this is resolved, the booking stays under review.",
            payMismatchSummaryAria: "Mismatch summary",
            payMismatchExpected: "Expected amount",
            payMismatchConfirmed: "Confirmed amount",
            payMismatchReceipt: "Receipt",
            payMismatchFollow: "Follow up on payment",
            payLateTitle: "Payment arrived after the hold ended",
            payLateBody: "Payment was confirmed, but after the time hold ended, so the booking is not firm yet and a credit or refund path opens.",
            payLateSummaryAria: "Late payment summary",
            payLatePayment: "Payment",
            payLatePaymentValue: "Confirmed",
            payLateBooking: "Booking",
            payLateBookingValue: "Not firm",
            payLateAction: "Action",
            payLateActionValue: "Financial outcome unresolved",
            payLateNewSlot: "Choose a new time",
            payFollowSoon: "Support will follow up shortly.",
            payFailRetryPending: "Payment link was not returned. Try again or open payments.",
            payFailRetryError: "Could not restart payment.",
            boundary01Title: "Informed therapy consent",
            consentWithdrawalFailed: "Your consent could not be withdrawn. Please try again.",
            boundary01Reassure: "Your booking and payment are confirmed. To unlock session access, read and confirm the consent form.",
            boundary01MetaTitle: "Title",
            boundary01MetaVersion: "Version",
            boundary01DocName: "Informed therapy consent",
            boundary01Body1: "I understand that the online counseling/psychotherapy session is held on Roobroo and that session content is confidential, except where the law or safety requires otherwise.",
            boundary01Body2: "I understand participation is voluntary and I can reschedule or cancel according to the cancellation policy.",
            boundary01Body3: "I understand Roobroo is not an emergency substitute and in a crisis I should use local urgent help.",
            boundary01Body4: "By accepting, the current version of this document is recorded for my account.",
            boundary01Accept: "I have read this version of the consent form and privacy notice, have the opportunity to ask questions, and agree to receive online care within the described scope.",
            boundary01Confirm: "Confirm consent",
            boundary01Decline: "Do not accept",
            boundary02Title: "Readiness questions",
            boundary02Optional: "This step is optional and does not affect matching, consent or session entry.",
            boundary02Retry: "Try again",
            boundary02ReturnHome: "Back to my portal",
            boundary02ScaleHint: "0 = very low · 10 = very high",
            boundary02: {
                importance: "From zero to ten, how important is changing this situation for you right now?",
                confidence: "From zero to ten, how confident are you in your ability to work on this?",
                expectancy: "From zero to ten, how helpful do you think therapy can be for you right now?",
                support: "Is there someone in your life you can count on when you really need it?",
            },
            boundary02Support: {
                yes: "Yes, usually",
                sometimes: "Sometimes, not always",
                no: "Not right now",
                skip: "Prefer not to say",
            },
            boundary02Submit: "Save answers",
            boundary02Later: "I will finish later",
            boundary03Title: "Optional letter to your future self",
            boundary03Privacy: "This letter is stored only for you and is visible only in your own account panel. Filling it in is optional.",
            boundary03Label: "Write to your future self: why did you start this path?",
            boundary03Placeholder: "If you like, write a few lines here…",
            boundary03Save: "Save for myself",
            boundary03Skip: "Skip",
            letterVoiceTitle: "A voice letter for yourself",
            letterVoiceUnavailable: "Voice letters are currently unavailable. You can write a letter or continue without one.",
            letterVoiceOptional: "A letter is optional. You can enter your session without recording one.",
            letterVoiceBack: "Back to the letter",
            letterVoicePrivacy: "Recording stays on your device until you save. In this preview no file is uploaded.",
            letterVoiceStart: "Start recording",
            letterVoiceIdle: "Tap to start recording",
            letterVoiceRecording: "Recording… tap again to stop",
            letterVoicePlay: "Playback",
            letterVoiceDiscard: "Delete take",
            letterVoiceWrite: "Write as text",
            letterVoiceCaptured: "A local take was kept in this preview.",
            letterVoicePlayHint: "Playback is local-only in this preview.",
            letterVoiceEmpty: "No take recorded yet.",
            letterVoiceDiscarded: "Take discarded.",
            readyLockTitle: "Entry is still locked",
            readyLockBody: "The session is confirmed, but entry stays locked until every server blocker below is cleared. The join link is not shown.",
            readyLockSummaryAria: "Entry gate summary",
            readyLockSession: "Confirmed session",
            readyLockSessionValue: "Tuesday 28 Esfand · 17:30",
            readyLockBlocker: "Current blocker",
            readyLockBlockerValue: "Consent not confirmed",
            readyLockAction: "Related action",
            readyLockActionValue: "Complete informed consent",
            readyLockCta: "Complete consent",
            readyLockHome: "Back to dashboard",
            readyOkTitle: "You are ready to join",
            readyOkBody: "All prerequisites are ready. In the allowed window you can check your device and join the session.",
            readyOkSummaryAria: "Session readiness summary",
            readyOkSession: "Session",
            readyOkSessionValue: "First session with your therapist",
            readyOkDate: "Date",
            readyOkDateValue: "Tuesday 28 Esfand",
            readyOkTime: "Local time",
            readyOkTimeValue: "17:30 · Tehran",
            readyOkCta: "Check device and join",
            readyOkHome: "Dashboard",
            clientHomeTitle: "Continue your care",
            clientHomeBody: "Check your session readiness or continue from your portal home.",
            clientHomeBodyMatched: "Your therapist is assigned. Continue to readiness, or open your portal home.",
            clientHomeDoctor: "Therapist: {name}",
            clientHomeCta: "Enter readiness",
            clientHomePortalCta: "Open my portal",
            sessionCheckTitle: "Device check (preview)",
            sessionCheckBody: "Device-check preview before the online session — the full page arrives in the Online Session section.",
            sessionCheckBack: "Back",
            onlineSession: {
                checkTitle: "Audio and video check",
                checkBody: "Before you enter, check sound and video. If you have not granted access, recovery guidance stays on this page.",
                checkMic: "Microphone",
                checkMicOk: "Ready",
                checkMicBad: "Blocked or unavailable",
                checkCam: "Camera",
                checkCamOk: "Ready",
                checkCamBad: "Blocked or unavailable",
                checkPending: "Checking…",
                checkNetBad: "Offline",
                noSession: "No upcoming session was found to join.",
                joinReady: "Join link available",
                joinPending: "Waiting to open",
                waitEnterRoom: "Enter session",
                waitReadyStatus: "Your session is ready to join.",
                waitNotReady: "Not open yet — keep waiting or try again shortly.",
                waitStartNow: "Now",
                waitStartDays: "Days: {days} · hours: {hours} · minutes: {minutes}",
                openJoinLink: "Open session link",
                roomConnLinked: "External room link ready",
                roomOpenedStatus: "Session link opened in a new tab.",
                noJoinLink: "No Skype/Alocam link on this session yet.",
                reconnectFail: "Could not refresh the join link.",
                endFeedbackDone: "Already completed",
                endFeedbackOrs: "ORS pending",
                endFeedbackSrs: "SRS pending",
                checkSpeaker: "Speaker",
                checkSpeakerOk: "Tested",
                checkNet: "Connection",
                checkNetOk: "Stable",
                checkPreviewBadge: "Your preview",
                checkPreviewLabel: "Test image",
                checkPreviewWaitingTitle: "Camera preview",
                checkPreviewWaitingBody: "Allow camera and microphone to see your video here.",
                checkEnterWait: "Enter waiting room",
                checkRetry: "Retry camera and mic",
                checkHelp: "Error and permission help",
                checkHelpStatus: "Allow camera and microphone for this site, then tap retry. You can still enter the waiting room.",
                checkInsecure: "Camera and mic need a secure page (https or localhost).",
                checkUnsupported: "This browser cannot access camera or microphone.",
                waitTitle: "Waiting for the session to start",
                waitBody: "You are waiting for the session to start. Arriving early does not mean the session has begun.",
                waitMetaAria: "Waiting room details",
                waitTherapist: "Therapist",
                waitTherapistValue: "Joining",
                waitTime: "Session time",
                waitTimeValue: "17:30 · Tehran",
                waitStart: "Start status",
                waitStartValue: "Not started yet",
                waitCheckStart: "Check if session started",
                waitLeave: "Leave",
                waitSupport: "Support",
                roomTitle: "Online session room",
                roomTherapistAria: "Therapist video",
                roomTherapistBadge: "Therapist",
                roomLive: "Connected",
                roomTherapistLabel: "Therapist video",
                roomSelfAria: "Your video",
                roomSelfBadge: "You",
                roomSelfLabel: "Preview",
                roomControlsAria: "Session controls",
                roomMicIcon: "Mic",
                roomCamIcon: "Cam",
                roomEndIcon: "End",
                roomMicOn: "Mic on",
                roomMicOff: "Mic off",
                roomCamOn: "Camera on",
                roomCamOff: "Camera off",
                roomEndControl: "Leave session",
                roomMetaAria: "Connection details",
                roomConn: "Connection",
                roomConnValue: "Stable",
                roomSupport: "Support",
                roomSupportValue: "Available",
                roomLeave: "Leave session",
                roomReconnectHint: "If connection drops",
                roomHelp: "Urgent help",
                reconnectTitle: "Connection lost",
                reconnectBody: "Connection was lost. We are trying to reconnect; this does not end the session.",
                reconnectMetaAria: "Reconnect details",
                reconnectConn: "Connection",
                reconnectConnValue: "Temporary drop",
                reconnectMic: "Microphone",
                reconnectMicValue: "Preserved",
                reconnectCam: "Camera",
                reconnectCamValue: "Preserved",
                reconnectAv: "Microphone / camera",
                reconnectAvValue: "Preserved",
                reconnectRetry: "Try reconnecting",
                reconnectSupport: "Support",
                reconnectLeave: "Safe leave",
                endTitle: "Session ended",
                endBody: "Your session has ended. Please leave feedback and book your next session if you want.",
                endMetaAria: "Session end details",
                endStatus: "End status",
                endStatusValue: "Recorded",
                endFeedback: "Client feedback",
                endFeedbackValue: "Ready to submit",
                endFeedbackCta: "Submit session feedback",
                endDashboard: "Back to dashboard",
                endBookNext: "Book next session",
                outcomePreviewTitle: "Session feedback (preview)",
                outcomePreviewBody: "Session feedback (SRS) preview — the full page will arrive in Treatment Measurement.",
                outcomeBack: "Back",
                supportStatus: "Support will contact you shortly.",
            },
            treatment: {
                srsTitle: "Session feedback",
                srsIntro: "Feedback for this session is only visible to your assigned therapist. The midpoint is not a final answer; confirm each item separately. No live score is shown.",
                srs1Legend: "Relationship",
                srs1Sub: "How much did you feel heard and understood today?",
                srs2Legend: "Topics and goals",
                srs2Sub: "Did you work on something that mattered to you?",
                srs3Legend: "Session approach",
                srs3Sub: "How well did today’s method fit your needs?",
                srs4Legend: "Overall",
                srs4Sub: "Overall, how was this session?",
                srsSubmit: "Submit feedback",
                srsLater: "Later",
                orsTitle: "Path assessment",
                orsIntro: "Please mark how you have been over the past few days on each line. The midpoint is only a starting point and is not recorded until you confirm. No number is shown on screen.",
                ors1Legend: "Individually",
                ors1Sub: "Your personal wellbeing and how you feel these days",
                ors2Legend: "In close relationships",
                ors2Sub: "Family, partner, or close friends",
                ors3Legend: "In social life",
                ors3Sub: "Work, study, friendships, and daily activities",
                ors4Legend: "Overall",
                ors4Sub: "If you had to give one overall picture of how you are",
                orsSubmit: "Submit assessment",
                orsIncomplete: "I cannot finish right now",
                metaSession: "Session:",
                metaSessionValue: "3",
                metaVersion: "Content version:",
                metaVersionValue: "fa-IR-1.0.2",
                confirmAnswer: "I confirm this answer",
                confirmRequired: "Please confirm every answer before submitting.",
                noPendingAssessment: "No open session assessment was found. Open this from a session reminder when one is ready.",
                sessionIneligible: "This session was cancelled, so there is nothing to assess. Book the next session when you are ready.",
                contentUnavailable: "This assessment is not available for your current care path.",
                srsNotDue: "Session feedback opens after the session is held.",
                srsWindowExpired: "The 24-hour window for session feedback has closed.",
                orsWindowClosed: "Path assessment for this session closed after the session was held.",
                outOfSequence: "Complete the current assessment first, then continue in order.",
                backToPortal: "Back to my portal",
                submitError: "Could not save the assessment. Check your connection and try again.",
                submitErrorContinue: "Could not sync assessment; continuing with local save.",
                orsAlreadyDone: "Pre-session assessment is already recorded for this session.",
                orsAlreadySkipped: "This path assessment was already skipped for this session.",
                assessAlreadyDone: "This session assessment is already complete.",
                notifyEmpty: "No new notifications right now.",
                notifyPendingWhen: "Waiting",
                doneFormOrs: "ORS · pre-session",
                doneFormSrs: "SRS · post-session",
                rulerAriaUnset: "Unset until confirmed",
                anchorLow: "Low",
                anchorHigh: "High",
                anchorMin: "Minimum",
                anchorMax: "Maximum",
                doneTitle: "Your answers were saved",
                doneBody: "Your answers were saved. No score or diagnosis is shown on this page.",
                doneMetaAria: "Submission summary",
                doneSession: "Session number",
                doneSessionValue: "3",
                doneForm: "Form name",
                doneFormValue: "Saved",
                doneStatus: "Save status",
                doneStatusValue: "Successful",
                doneStatusUnconfirmed: "This page does not confirm a saved assessment.",
                doneSessionUnknown: "—",
                doneCta: "Back to dashboard",
                missTitle: "This assessment is not saved yet",
                missBody: "This assessment is not saved yet. Please complete it to assess your treatment path.",
                missSkippedTitle: "This skip is saved",
                missSkippedBody: "The path assessment for this session was skipped. It is no longer an open item.",
                missUnknownBody: "The saved assessment status is unavailable. Open the assessment from your session again.",
                missMetaAria: "Missing assessment details",
                missSession: "Related session",
                missSessionValue: "Session 3",
                missForm: "Questionnaire",
                missFormValue: "Path assessment",
                missReasonAria: "Reason for missing assessment",
                missOpt_later: "I will finish later",
                missOpt_tech: "I had a technical problem",
                missOpt_skip: "I prefer to skip for now",
                missComplete: "Complete the form for this session",
                missBack: "Back",
                thTitle: "Therapist dialogue and action",
                thBody: "The path pattern needs a short conversation and a recorded action. This page is for the assigned therapist.",
                thMetaAria: "Therapist follow-up details",
                thClient: "Assigned client",
                thClientValue: "Sara M.",
                thTrigger: "Trigger",
                thTriggerValue: "Path review needed",
                thOrs: "Path assessment",
                thOrsValue: "Saved · session 3",
                thDeadline: "Deadline",
                thDeadlineValue: "48 hours",
                thGuide: "Talk guide: without mentioning a number or clinical label, ask what felt harder or better this week and agree one shared action.",
                thActionAria: "Therapist action",
                thOpt_talk: "Conversation completed",
                thOpt_plan: "Shared plan set",
                thOpt_escal: "Escalate to quality follow-up",
                thSubmit: "Save therapist action",
                thEscalate: "Refer to quality manager",
                th03Title: "Therapist path continuation (preview)",
                th03Body: "Therapist path continuation preview (TH-03) — the full page arrives in Therapist Portal.",
                th03Back: "Back",
                qm01Title: "Quality list (preview)",
                qm01Body: "Quality list preview (QM-01) — the full page arrives in Quality Operations.",
                qm01Cta: "Back",
                qmTitle: "Quality manager follow-up",
                qmBody: "Quality case for decision review. Session feedback and the private letter are not transferred here.",
                qmMetaAria: "Quality case details",
                qm_case: "Quality case",
                qm_caseValue: "QM-2403",
                qm_reason: "Access reason",
                qm_reasonValue: "Step-one follow-up",
                qm_ors: "Path assessment",
                qm_orsValue: "Allowed summary",
                qm_history: "Step-one history",
                qm_historyValue: "Recorded by therapist",
                qm_owner: "Owner",
                qm_ownerValue: "Quality manager",
                qm_deadline: "Deadline",
                qm_deadlineValue: "72 hours",
                qmDecisionAria: "Quality decision",
                qmOpt_monitor: "Continue monitoring",
                qmOpt_coach: "Support the therapist",
                qmOpt_rematch: "Suggest a path change to the client",
                qmSubmit: "Save quality decision",
                notifyTitle: "Notifications",
                notifyBody: "Operational notifications without clinical detail. Opening each item re-checks destination access.",
                notifyListAria: "Notification list",
                notifySrsTitle: "Session feedback is ready to submit",
                notifySrsWhen: "Today · 10:12",
                notifyOrsTitle: "Path assessment waiting to be completed",
                notifyOrsWhen: "Yesterday · 18:40",
                notifyDoneTitle: "Previous response saved successfully",
                notifyDoneWhen: "2 days ago",
                notifyUnread: "Unread",
                notifyPendingState: "Waiting to submit",
                notifyMissing: "Not submitted",
                notifySession: "Session {n}",
                notifyRead: "Read",
                notifyBack: "Back",
            },
            portal: {
                brand: "RooBroo",
                avatarShort: "Ni",
                avatarFallback: "—",
                personName: "Niloufar Ahmadi",
                personNameFallback: "My account",
                personRole: "Client account",
                therapistName: "Dr. Nastaran Sadeghi",
                therapistAvatar: "NS",
                therapistRole: "Your therapist",
                eyebrow: "My portal",
                navAria: "Portal menu",
                navSectionsAria: "Account sections",
                mobileNavAria: "Mobile navigation",
                sideFoot: "Consent and readiness",
                profileAria: "Profile",
                navHome: "My home",
                navServices: "Services and account",
                navServicesShort: "More",
                navHomeShort: "Home",
                navSessions: "My sessions",
                navSessionsShort: "Sessions",
                navProgress: "My progress",
                navProgressShort: "Progress",
                navPayments: "Payments & credit",
                navPaymentsShort: "Payments",
                navSupport: "My requests",
                navSupportShort: "Support",
                navChat: "Therapist messages",
                navChatShort: "Messages",
                details: "Details",
                reschedule: "Change time",
                back: "Back",
                currency: "Toman",
                homeTitle: "Hello Niloufar",
                homeTitleLive: "Hello {name}",
                homeSubtitle: "See your sessions, treatment path, payments, and requests here.",
                homeHeroDay: "28",
                homeHeroMonth: "Esfand",
                homeHeroEyebrow: "Next session",
                homeHeroTitle: "Tuesday · 17:30",
                homeHeroBody: "With Dr. Nastaran Sadeghi · Online · Ready to start",
                homeHeroCta: "View next session",
                homeHeroAlt: "Change or cancel",
                homeNoSessionEyebrow: "No upcoming session",
                homeNoSessionTitle: "Nothing booked yet",
                homeStartScreeningTitle: "Find a therapist for you",
                homeStartScreeningBody: "Start screening so we can understand your needs and help you find a suitable therapist.",
                homeStartScreeningCta: "Start screening",
                homeNoSessionBody: "When a session is reserved, it shows up here with time and readiness.",
                homeNoSessionCta: "See my sessions",
                homeNoSessionTitleCredit: "You still have session credit",
                homeNoSessionBodyCredit: "The cancelled session came back as credit for {n} session(s). Reserve a new time whenever you are ready.",
                homeNoSessionCtaCredit: "Book with credit",
                homeSummaryAria: "Action summary",
                homeCardOrsTitle: "Path assessment",
                homeCardOrsBody: "You’re up to date — nothing to submit right now. After your next session, feedback opens here.",
                homeCardOrsBodyIdle: "You’re up to date — nothing to submit right now. After your next session, feedback opens here.",
                homeCardOrsBodyPending: "You have an open path assessment waiting.",
                homeCardSrsBodyPending: "Session feedback is open for a held session — complete it within 24 hours.",
                homeCardOrsCta: "Complete assessment",
                homeCardOrsCtaIdle: "View my progress",
                homeCardPayBodyIdle: "Session credit and receipts live here.",
                homeCardPayBodyCredit: "You have credit for {n} session(s) to book.",
                homeCardPayBodyFactor: "Latest receipt · {status} · {amount}",
                homeCardSupportBodyIdle: "Open a request when you need help between sessions.",
                homeCardSupportBodyLive: "{title} · {status}",
                homeCardSupportCtaIdle: "Open support",
                homeJ1BodyIdle: "Match with a therapist to start your path.",
                homeJ2BodyIdle: "Book a session to continue here.",
                homeJ2BodyDone: "Your first session is complete.",
                homeJ3BodyIdle: "Feedback after sessions builds this view.",
                homeJ3BodyPending: "An open assessment is waiting.",
                homeJ3BodyLive: "Path check-ins recorded ({n}). Continue on progress.",
                therapistNameEmpty: "No therapist assigned yet",
                homeTherapistBodyEmpty: "After matching, your therapist appears here for short coordination between sessions.",
                loadError: "Could not load this page. Check your connection and try again.",
                loadSoftError: "Some account data could not be refreshed. Showing what is available.",
                needsLogin: "To load live sessions and therapist data, sign in again.",
                needsLoginCta: "Sign in",
                loading: "Loading…",
                noData: "Nothing to show yet.",
                sessionsNoUpcomingTitle: "No upcoming sessions",
                sessionsNoUpcomingBody: "Buy a package or wait for coordination to see the next time here.",
                sessionsNoUpcomingBodyCredit: "You still have credit for {n} session(s). Reserve a new time without buying again.",
                nextSessionTitle: "Schedule your next session",
                nextSessionKicker: "Your care plan",
                nextSessionIntro: "Choose a suitable time with your assigned therapist.",
                nextSessionCta: "Choose a time",
                nextSessionTherapist: "Your therapist: {name}",
                nextSessionTherapistLabel: "Your assigned therapist",
                nextSessionExperience: "{years} years of experience",
                nextSessionRating: "Rating {rating}",
                nextSessionSlotsTitle: "Available times",
                nextSessionSlotsHint: "Select a time to hold it for ten minutes.",
                nextSessionNoSlots: "There are no available times right now.",
                nextSessionHold: "Hold this time",
                nextSessionReserve: "Reserve this time",
                nextSessionChooseAndPay: "Choose this time",
                nextSessionHoldUntil: "This time is held for {time}",
                nextSessionUseCredit: "Use session credit",
                nextSessionPaymentRequired: "This time is held. Complete payment before the hold expires.",
                nextSessionGoToPayment: "Go to payment",
                nextSessionPaymentResult: "Session payment",
                nextSessionPaymentProcessing: "Checking payment and confirming your session…",
                nextSessionPaymentPending: "Payment confirmation is still pending. You can check its status again.",
                nextSessionPaymentFailed: "Payment was not completed. Your session has not been booked.",
                nextSessionPaymentExpired: "The reservation deadline passed. Please choose a time again.",
                nextSessionPaymentLate: "Your payment was received, but the reserved time is no longer held. Support will help you rebook or resolve the payment.",
                nextSessionPaymentSupport: "Contact support",
                nextSessionPickAgain: "Choose a time again",
                nextSessionCheckStatus: "Check payment status",
                nextSessionRelease: "Release this time",
                nextSessionConfirmed: "Your next session is scheduled.",
                nextSessionAlreadyScheduled: "Your next session is already scheduled.",
                nextSessionUnavailable: "Next-session scheduling is not available right now.",
                sessionMissingTitle: "Session not found",
                sessionMissingBody: "This session is not available. Pick another from your list.",
                progressChartDescLive: "Direction recorded across {n} session(s). No clinical score is shown.",
                progressMsgTitleOngoing: "Your path is underway.",
                progressMsgBodyLive: "Based on {n} recorded check-in(s). Short questions after sessions keep the path adjusted.",
                homeCardPayTitle: "Payments & credit",
                homeCardPayBody: "You have credit for {n} session(s) to book.",
                homeCardPayCta: "View payments",
                homeCardSupportTitle: "Support request",
                homeCardSupportBody: "{title} · {status}",
                homeCardSupportCta: "Track request",
                homePathTitle: "My path",
                homePathSub: "A simple view of where you are now",
                homePathCta: "View my progress",
                homeJ1Title: "Path started",
                homeJ1Body: "Sign-up and match completed",
                homeJ2Title: "First session",
                homeJ2Body: "Confirmed booking · ready to start",
                homeJ3Title: "Continue path",
                homeJ3Body: "Built with your feedback",
                homeTherapistBody: "Leave a short coordination message between sessions. Bring therapy topics to the session.",
                homeChatCta: "Message therapist",
                homeRequestCta: "Submit support request",
                sessionsTitle: "My sessions",
                sessionsBuyCta: "Buy a new session",
                sessionsUpcoming: "Upcoming sessions",
                sessionsUpcomingBody: "See time, readiness, and booking changes in one place.",
                session1Day: "28",
                session1Title: "Tuesday 28 Esfand · 17:30",
                session1Meta: "Dr. Nastaran Sadeghi · Online · Confirmed",
                session2Day: "4",
                session2Title: "Saturday 4 Farvardin · 18:00",
                session2Meta: "Dr. Nastaran Sadeghi · Online · Time reserved",
                sessionsPast: "Past sessions",
                sessionsEmptyTitle: "No sessions held yet",
                sessionsEmptyBody: "After a session, feedback and progress appear here.",
                progressTitle: "My progress",
                progressIntro: "Your path, in plain language",
                progressIntroBody: "This view shows the overall direction of your experience — not a clinical score or judgment.",
                progressVideoAria: "Play progress explanation video",
                progressVideoEyebrow: "Short video",
                progressVideoTitle: "What does this chart show?",
                progressVideoMeta: "About 90 seconds · no clinical score",
                progressChartTitle: "Overall treatment path",
                progressChartDesc: "Direction of experience recorded for the first two sessions. No clinical score is shown.",
                progressAxis: "Session number",
                progressMsgTitle: "You are near the start of the path.",
                progressMsgBody: "After each session a few short questions help you and your therapist adjust the path.",
                progressEmptyTitle: "No path to show yet",
                progressEmptyBody: "The chart appears after a held session and a short check-in. A cancelled session does not add a measurement.",
                progressEmptyCta: "Reserve a session",
                progressOrsCta: "Complete next assessment",
                progressLetterCta: "Letter to myself",
                payTitle: "Payments & credit",
                payIntro: "Payments and credit",
                payIntroBody: "The amount for each session is clear before booking.",
                payCreditLabel: "Current credit",
                payCreditValue: "2 sessions",
                payCreditCta: "Book with credit",
                payLastLabel: "Last payment",
                payLastValue: "Successful",
                payLastMeta: "480,000 Toman · Online",
                payLastNone: "No payments yet",
                payLastNoneMeta: "Buy a package to see receipts here.",
                payEmptyTitle: "No receipts yet",
                payEmptyBody: "After a purchase, invoices and receipts appear in this list.",
                payBuyPackage: "Buy a new package",
                payReceiptTitle: "First session with Dr. Nastaran Sadeghi",
                payReceiptMeta: "25 Esfand · Online payment · Successful",
                payReceiptCta: "View receipt",
                payRefundTitle: "Refund tracking",
                payRefundMeta: "If needed, check the request status",
                payRefundCta: "Refund status",
                packTitle: "Buy session package",
                packIntro: "Package for your therapist",
                packIntroBody: "Package price is based on each therapist’s session rate. Credit is only usable with that therapist.",
                packTherapistEyebrow: "Current therapist",
                packRate: "Rate per session",
                packRateValue: "480,000 Toman",
                packSizeAria: "Choose package size",
                packSizeLegend: "Package size",
                packFeatured: "Recommended",
                packSessions: "sessions",
                pack3Title: "3-session package",
                pack3Copy: "Short start · no discount",
                pack3Price: "1,440,000",
                pack5Title: "5-session package",
                pack5Copy: "5% off · most common choice",
                pack5Price: "2,280,000",
                pack5Was: "instead of 2,400,000",
                pack10Title: "10-session package",
                pack10Copy: "10% off · longer path",
                pack10Price: "4,320,000",
                pack10Was: "instead of 4,800,000",
                packNote: "Package credit is only usable for sessions with Dr. Nastaran Sadeghi and cannot be transferred.",
                packNoteLive: "Package credit is only usable for sessions with {name} and cannot be transferred.",
                packNoteGeneric: "Package credit is tied to your assigned therapist and cannot be transferred.",
                packRateMissing: "Session rate will show after your therapist is assigned.",
                packRateUnavailable: "Your session rate is currently unavailable. Please try again later.",
                packCountTitle: "Package of {n} sessions",
                packPriceChanged: "The package price changed. Refresh this page before continuing.",
                packEmptyTitle: "No packages available",
                packEmptyBody: "Packages appear after a therapist is assigned, or try again later.",
                packFallbackTitle: "Session package",
                packFallbackCopy: "Credit for sessions with your therapist.",
                packPayError: "Could not add this package to the cart. Try again.",
                packPayCta: "Continue to payment",
                supportTitle: "My requests",
                supportHeroEyebrow: "We are with you",
                supportHeroTitle: "What do you need help with?",
                supportHeroBody: "Raise booking, payment, or technical issues with support. Bring therapy topics to your therapist.",
                supportNewCta: "Submit new request",
                supportCat1: "Booking and time change",
                supportCat2: "Payment and refund",
                supportCat3: "Session access problem",
                supportOpen: "Your requests",
                supportStatus_open: "Open",
                supportStatus_resolved: "Resolved",
                supportStatus_blocked: "Blocked",
                supportStatus_closed: "Closed",
                supportReplyClosed: "This request is closed. Submit a new one if you still need help.",
                requestBodyRequired: "Write a short message before submitting.",
                requestReplyPlaceholder: "Write your message for support…",
                supportItemTitle: "Time-change coordination",
                supportItemMeta: "Last update: today · 10:12",
                supportOpenStatus: "Open",
                chatEyebrow: "Async messaging",
                chatSla: "Messages are not live chat. A response time is not guaranteed.",
                chatBanner: "This conversation is for coordination between sessions. Bring deep therapy topics to the session. Instant reply is not guaranteed.",
                chatThreadAria: "Message thread",
                chatDay1: "Saturday 24 Esfand",
                chatDay2: "Today",
                chatT1Time: "18:40",
                chatT1Body: "Hi Niloufar. We booked the first session for Tuesday. If you need coordination before then, write here.",
                chatC1Time: "19:05",
                chatC1Body: "Thanks. Just confirming the join link activates the same day.",
                chatT2Time: "10:12",
                chatT2Body: "Yes — the link is available about 15 minutes before start in session details. Support can help if anything goes wrong.",
                chatC2Time: "11:02",
                chatC2Body: "Great, thanks for explaining.",
                chatYou: "You",
                chatRead: "Read",
                chatSent: "Sent",
                chatNow: "Now",
                chatComposerAria: "Send message",
                chatInputLabel: "Message text",
                chatPlaceholder: "Write your message… your therapist replies later.",
                chatHint: "It will be sent and your therapist replies when available.",
                chatSend: "Send message",
                chatNoContact: "No chat contact is available yet.",
                chatEmpty: "No messages in this thread yet.",
                chatLoadFail: "Could not load messages.",
                chatSendFail: "Could not send the message.",
                messengerUnavailable: "Messenger is unavailable right now. Try again later or use the main chat from the panel.",
                supportMessengerNote: "This form is for practical support and is separate from therapist chat. Avoid clinical details. A response time is not guaranteed here.",
                supportEmpty: "No support requests yet.",
                letterTitle: "A letter you wrote to yourself",
                letterSubtitle: "Shown only to you.",
                letterBody: "I wrote to my future self why I started this path: I want to speak with more calm to myself and others, and move step by step.",
                letterMeta: "Written: 20 Esfand · only for you · not editable here",
                letterBack: "Back to my progress",
                sessionDetailTitle: "Session details",
                sessionDetail_therapist: "Therapist",
                sessionDetail_therapistValue: "Dr. Nastaran Sadeghi",
                sessionDetail_number: "Session number",
                sessionDetail_numberValue: "1",
                sessionDetail_time: "Local time",
                sessionDetail_timeValue: "Tuesday 28 Esfand · 17:30 · Tehran",
                sessionDetail_status: "Status",
                sessionDetail_statusValue: "Finalized",
                sessionReadyCta: "Get ready for session",
                sessionCancel: "Cancel session",
                requestTitle: "Support request",
                requestTopic: "Operational topic",
                requestTopic_booking: "Booking and time change",
                requestTopic_payment: "Payment and refund",
                requestTopic_access: "Session access problem",
                requestTopic_other: "Other",
                requestTopic_delivery: "Operational delivery problem",
                requestTopic_dissatisfaction: "Concern about a session or therapist (quality team)",
                requestBody: "Your message",
                requestPlaceholder: "Write a short message for support…",
                requestSubmit: "Submit request",
                requestCancel: "Cancel",
                requestSendFail: "Could not submit the support request. Nothing was recorded as delivered.",
                requestSavedLocal: "Request was not submitted.",
                requestStatusSent: "Open · recorded by support service",
                requestStatusLocal: "Not submitted",
                requestDetailTitle: "Track request",
                requestDetail_topic: "Topic",
                requestDetail_topicValue: "Time-change coordination",
                requestDetail_status: "Status",
                requestDetail_statusValue: "Open · awaiting reply",
                requestDetail_last: "Last message",
                requestDetail_lastValue: "Today · 10:12",
                requestDetail_reply: "Support reply",
                requestDetail_replyValue: "No reply recorded yet",
                requestDetailBack: "Back to requests",
                receiptTitle: "Payment receipt",
                invoiceDetailsTitle: "Invoice details",
                invoiceNotReceipt: "This invoice is not a confirmed payment receipt. Check its status before paying again.",
                receipt_amount: "Amount",
                receipt_amountValue: "480,000 Toman",
                receipt_date: "Date",
                receipt_dateValue: "25 Esfand 1404 · 14:22",
                receipt_method: "Method",
                receipt_methodValue: "Online payment",
                receipt_code: "Receipt number",
                receipt_codeValue: "RB-258441",
                receipt_session: "Session",
                receipt_sessionValue: "First session · Dr. Nastaran Sadeghi",
                receipt_status: "Status",
                receiptMethodOnline: "Online payment",
                receiptMethodOffline: "Offline payment",
                receiptMethodWallet: "Wallet settlement",
                receiptMethodUnknown: "—",
                receiptMissingTitle: "Receipt not found",
                receiptMissingBody: "This invoice is not available. Pick another from payments.",
                receiptPayCta: "Continue payment",
                receiptBack: "Back to payments",
                refundTitle: "Refund tracking",
                refund_amount: "Amount",
                refund_amountValue: "480,000 Toman",
                refund_dest: "Destination",
                refund_destValue: "Same payment card",
                refund_requestStatus: "Request status",
                refund_requestStatusValue: "Reviewed",
                refund_execStatus: "Execution status",
                refund_execStatusValue: "In payout queue",
                refund_updated: "Last update",
                refund_updatedValue: "Yesterday · 16:40",
                refundBack: "Back to payments",
                refundUnavailable: "Online refund tracking is unavailable. Contact support about your payment.",
                refundSupportCta: "Payment or refund help",
                refundSupportIntro: "Send a payment request to support. This does not approve a refund or set a payout date. You can follow the request and replies after submitting it.",
                refundSupportBody: "Please review my payment and let me know whether a refund is available and what happens next.",
                refundSupportReference: "Receipt reference: {reference}",
            },

            consentSignerTitle: "Participants and consent",
            consentSignerBody: "For this path, participant signatures are required. Join access stays closed until the required statuses are complete.",
            consentSignerColName: "Name",
            consentSignerColRole: "Role",
            consentSignerColParty: "Party",
            consentSignerColSigner: "Signer",
            consentSignerColStatus: "Status",
            consentSignerRow: {
                client: {
                    name: "Sara M.",
                    role: "Client",
                    party: "Self",
                    signer: "Required",
                    status: "Pending",
                },
                guardian: {
                    name: "Legal guardian",
                    role: "Guardian",
                    party: "Teen",
                    signer: "Required",
                    status: "Incomplete",
                },
            },
            consentSignerCta: "Confirm participant details",
            consentGuardTitle: "Guardian authority confirmation",
            consentGuardBody: "For a teen/child path, guardian authority must be recorded and reviewed. This page is not a legal license; it only collects information per policy.",
            consentGuardSummaryAria: "Guardian authority summary",
            consentGuardId: "Guardian identity",
            consentGuardIdValue: "In progress",
            consentGuardParty: "Party",
            consentGuardPartyValue: "Teen · under 18",
            consentGuardWitness: "Authority evidence",
            consentGuardWitnessValue: "No documents attached",
            consentGuardReview: "Review status",
            consentGuardReviewValue: "Needs submission",
            consentGuardCta: "Submit authority details",
            consentGuardLater: "Later for now",
            consentTitle: "Continue with awareness before the session",
            consentIntro: "This confirmation is a real gate; until it is recorded, joining the session is not possible.",
            consentDocTitle: "Therapy consent — Roobroo",
            consentDocLead: "Before you start, we want you to know what you are accepting.",
            consentDraftLabel: "Prototype draft:",
            consentDraftBody: "This text must be approved by legal and clinical review before release.",
            consentSection1Title: "1 · Who provides your therapy",
            consentSection1Body: "Your therapist holds a practice license. If a therapist change is needed, it happens with your notice and agreement.",
            consentSection2Title: "2 · What is recorded about you",
            consentSection2Body: "Intake answers, short questionnaire results each session, the therapist’s clinical notes, and financial and communication records.",
            consentSection3Title: "3 · Who sees what",
            consentSection3Body: "Your therapist sees your clinical file. Support sees only operational and financial information, not the clinical file. The quality manager sees measurement data to monitor the care path. The therapist’s clinical notes are for that therapist only.",
            consentSection4Title: "4 · Confidentiality and its limits",
            consentSection4Body: "What happens in session is confidential, except where the law or your safety or someone else’s safety does not allow silence. Exact disclosure boundaries for self-harm, harm to others, and matters involving children must be finalized in the legal version.",
            consentSection5Title: "5 · Recording",
            consentSection5Body: "Sessions are not recorded unless you explicitly request and agree.",
            consentSection6Title: "6 · Right to stop",
            consentSection6Body: "You can stop therapy whenever you want and do not need to give a reason. Session cancel and refund terms are written separately.",
            consentSection7Title: "7 · Emergencies",
            consentSection7Body: "Roobroo is not an emergency service and does not provide round-the-clock response. If you are in an urgent situation, use the numbers below.",
            consentAccept: "I have read the consent text and accept it.",
            consentHint: "Your confirmation is kept with the text version and the time it was recorded.",
            consentSignatureLabel: "Full name",
            consentSubmit: "Confirm consent and continue",
            consentSavedNote: "Consent saved. First-session prep questions come next — not connected yet.",
            baselineTitle: "How was this week?",
            baselinePageIntro: "It takes less than a minute.",
            baselineSessionNumber: "Session number: {n}",
            baselineIntro: "Over the past week until today, how satisfied have you been in the situations below? For each item you can choose anywhere on the scale. The left end is the least satisfaction and the right end is the most.",
            baselineNote: "If you are filling this for someone else who attends therapy, please reflect that person’s view.",
            baselineLow: "Not satisfied at all",
            baselineHigh: "Completely satisfied",
            baselineItemAria: "{label}; from {low} to {high}",
            baselineItem: {
                ors1: "Satisfaction with yourself",
                ors2: "Satisfaction with family and close relationships",
                ors3: "Satisfaction in social situations (work, school, friends)",
                ors4: "Overall satisfaction",
            },
            baselineSubmit: "Save and continue",
            letterTitle: "A keepsake from the starting point",
            letterIntro: "Optional, and only for you.",
            letterSeal: "For your future self",
            letterLabel: "If you like, write a few lines to yourself",
            letterBody: "You can write why you started this path, or what you hope to remember at the end. At the end of the therapy path, when you read this letter again, you will be proud of yourself and what you achieved.",
            letterPlaceholder: "I started this path because…",
            letterPrivacy: "This letter is not used in matching or therapist assessment and is kept only for you.",
            letterSubmit: "Save and go to dashboard",
            letterSkip: "I will not write for now",
            matchSlot: {
                sat1800: "Monday, 18:30",
                sun1630: "Tuesday, 16:30",
                tue1900: "Thursday, 20:00",
            },
            therapist: {
                t1: {
                    name: "Dr. Sara Nikpey",
                    title: "Psychotherapist",
                    tier: "Level 2",
                    reason: "We chose Sara for you because her experience with anxiety and sleep matches what you shared, and her nearest opening also fits the evenings you prefer.",
                    evidence: "Direct qualification on the adult path, valid evidence for anxiety and burnout, a matching intensity ceiling",
                    bio: "Over eight years of experience with anxiety, sleep, and work pressure. Individual sessions focused on day-to-day skills and steady follow-up.",
                },
                t2: {
                    name: "Dr. Nazanin Rad",
                    title: "Clinical psychologist",
                    tier: "Level 1",
                    reason: "We put Nazanin forward because her experience with work and burnout fits your concern, and her nearest opening aligns with your time preference.",
                    evidence: "Direct qualification on the adult path, coverage of several topics, requested time available",
                    bio: "Helps clarify overlapping concerns and build a clear first step, with attention to mood and work stress.",
                },
                t3: {
                    name: "Dr. Elham Amini",
                    title: "Psychotherapist",
                    tier: "Level 3",
                    reason: "We suggested Elham because her experience lines up with the topics you chose, and her nearest opening fits the time window you prefer.",
                    evidence: "High complexity ceiling, direct topic qualification, capacity to start soon",
                    bio: "Experienced with longer-standing issues that touch several areas of life, with careful pacing and trauma-informed care.",
                },
            },
            topicGroup: {
                mood: "Mood",
                anxiety: "Anxiety and worry",
                trauma: "Grief and trauma",
                relationship: "Relationships and intimacy",
                family: "Family",
                work: "Work and study",
                sleep: "Sleep",
                substance: "Addictive habits",
                identity: "Self and identity",
                body: "Body and health",
            },
            topic: {
                mood_depression: "Depression and low mood",
                mood_swing: "Mood swings",
                mood_anhedonia: "Low motivation",
                anxiety_worry: "Ongoing worry",
                anxiety_panic: "Panic attacks",
                anxiety_phobia: "Specific fears",
                trauma_grief: "Grief and loss",
                trauma_event: "Traumatic experience",
                trauma_flashback: "Nightmares and reminders",
                relationship_conflict: "Conflict with a partner",
                relationship_trust: "Trust",
                relationship_loneliness: "Loneliness in a relationship",
                family_conflict: "Family conflict",
                family_boundaries: "Boundaries",
                family_caregiving: "Caring for others",
                work_burnout: "Burnout",
                work_stress: "Work stress",
                work_study: "Academic performance",
                sleep_insomnia: "Insomnia",
                sleep_hypersomnia: "Sleeping too much",
                sleep_nightmare: "Nightmares",
                substance_alcohol: "Alcohol",
                substance_drugs: "Substances",
                substance_compulsive: "Harmful repeating behaviors",
                identity_confusion: "Identity confusion",
                identity_esteem: "Self-esteem",
                identity_meaning: "Meaning",
                body_pain: "Chronic pain",
                body_image: "Body image",
                body_illness: "Illness and adjustment",
                unsure: "Not sure",
            },
            participants: {
                SELF: "Just me",
                COUPLE: "Couple",
                CHILD: "Child",
                TEEN: "Teen",
                PARENT: "Parent consultation",
                TOGETHER: "Teen",
                FAMILY: "Family",
            },
        },
        authUi: {
            brand: "Roobroo",
            back: "Back",
            tabsLabel: "Sign in or create an account",
            tabLogin: "Sign in",
            tabRegister: "Create account",
            loginTitle: "Sign in",
            loginLead: "Enter the mobile number you registered with so we can send you a sign-in code.",
            loginIntro: "That’s the only number we need. If you don’t have an account yet, start with Create account.",
            loginTrust: "Signing in only sends a confirmation code. It does not record therapy consent.",
            needAccount: "Don’t have an account?",
            haveAccount: "Already have an account?",
            registerTitle: "Create account",
            registerLead: "You don’t need a clear issue to start. We create the account first so this path stays with you.",
            registerTrust: "We only store what is needed to create the account and send the code. Getting a code is not therapy consent.",
            familyNote: "Roobroo is part of the Simiaroom family.",
            nameLabel: "First and last name",
            nameHint: "The name you’ll be addressed by on this path.",
            nameError: "Enter a name so the account can be created with it.",
            phoneLabel: "Mobile number",
            phoneHint: "The code is sent only to this number.",
            phoneError: "Enter the full number; it usually starts with 09 and has 11 digits.",
            phonePlaceholder: "09123456789",
            submitCode: "Get confirmation code",
            formIncomplete: "Complete a few items so we can continue.",
            termsUse: "Terms of use",
            termsAnd: "and",
            termsPrivacy: "Privacy notice",
            termsAfter: "I have read these documents, accept the terms of use and understand the data processing described.",
            termsError: "Accepting the terms of use and privacy policy is required to create an account.",
            termsDialogTitle: "Terms of use and privacy policy",
            termsDialogBody: "Read the terms of use and privacy notice. Informed therapy consent is collected separately before session entry.",
            termsDialogOk: "Got it",
            verifyTitle: "Confirm mobile number",
            verifyLead: "Enter the code that was sent. There’s no rush; the number stays the same.",
            phoneSent: "The code was sent to this number",
            changeNumber: "Change number",
            otpLabel: "6-digit code",
            otpHint: "The 6-digit code is valid for two minutes. After two minutes, request a new code.",
            otpDigit: "Digit {n} of 6",
            otpInvalid: "This code is not correct. Try again, or request a new one if it has expired.",
            otpSaveRetry: "Your information could not be saved. Try again with the same code before it expires.",
            otpIncomplete: "Enter the 6-digit code",
            resendIn: "Resend in {seconds} seconds",
            resendReady: "You can send a new code; the number stays the same.",
            resend: "Send code again",
            verifyContinue: "Confirm and continue",
            verifyTrust: "Used only to confirm this number.",
            passwordTitle: "Password",
            passwordLead: "Choose a password and confirm it to finish signing in.",
            passwordSubmit: "Save and continue",
            testimonialLabel: "Anonymous client comments",
            testimonialTitle: "What some clients have said",
            testimonialTitleBrand: "on the Roobroo platform",
            afterSessionPrefix: "After",
            afterSessionSuffix: "sessions",
            afterOne: "one",
            afterTwo: "two",
            afterThree: "three",
            afterFour: "four",
            afterSix: "six",
            anonymous: "Anonymous",
            quoteDot: "Comment {n}",
            quote1: "It was the first time I spoke without rush or judgment; a sense of safety was there from the start.",
            quote2: "My number stayed only for sign-in, and the rest moved quietly and privately.",
            quote3: "Instead of a crowd of steps, I moved one by one, and each stage was clear.",
            quote4: "I felt someone was actually listening, not just having me fill in a form.",
            quote5: "From sign-in to confirming the number it was simple; I wasn’t worried about my identity leaking.",
        },

        newPreSession: {
            yourEvaluationSession: "Your evaluation section will be",
            step1: "Registration",
            step2: "Select Time",
            step3: "Personal Information",
            sessionInfo: "Session Information",
            sessionDuration: "Duration of the Session:",
            sessionOrganizer: "Organizer:",
            min: "minutes",
            sessionLocation: "Location:",
            selectTime: "Select Time",
            selectTimezone: "Select Timezone",
            preferredTime: "What time would you prefer for the pre-session?",
            closestTime: "Closest time for session:",
            calendarAndOtherTimes: "View Calendar and Choose Other Times",
            timeSlots: "Available Time Slots",
            noAvailableTimes: "Unfortunately, all evaluation session times are full at the moment.",
            timesUpdated: "This time has recently been updated",
            contactViaWhatsApp: "Additionally, if you wish, you can contact our expert via WhatsApp to reserve the first available time slot for you.",
            reserveOnWhatsApp: "Reserve on WhatsApp",
            confirmAndContinue: "Confirm and Continue",
            chooseDateTime: "Please choose a date and time",
            selectTimeError: "Please select a time",
            recentUpdateError: "This time was recently updated, please refresh to try again.",
            noEmptySlots: "No available time slots in this period.",
            morning: "Morning",
            afternoon: "Afternoon",
            evening: "Evening",
            withChat: "With a friendly chat, take a step for yourself",
            sessionHelpText: "In this 15-minute session, we'll help you choose the most suitable therapist.",
            sessionOrganizerText: "Senior Evaluation and Referral Specialist",
            sessionLocationText: "Online on Microsoft Teams",
            times: "Times",
            notifyNewSlot: "We will notify you as soon as a new slot becomes available.",
            contactViaWhatsAppText: "Additionally, if you wish, you can contact our expert via WhatsApp to reserve the first available time slot for you.",
            selectTimezoneError: "Please select your time zone",
            hourText: " ",
            selectTimeErrorText: "Please select a time",
            somethingWentWrong: "Something went wrong, please try again.",


            name: "Name",
            family: "Family",
            personalInformation: "Personal information",
            howToAddressYou: "How would you like us to address you?",
            otherInfoHelp: "Other information that helps us get to know you better:",
            countryLabel: "Country",
            howHeardAboutUs: "How did you hear about us?",
            userFeedback: "93% of users felt better after the evaluation session.",
            chooseDateAndTime: "Please choose a date and time",
            submitAndContinue: "Submit and Continue",
            fillRequiredFields: "Please fill in the required fields.",
            slotUnavailable: "This slot is no longer available; please choose another time.",
            serverError: "Error communicating with the server.",

            successMessage: "Well done! You've taken an important step towards change!",
            supportMessage: "We are with you to help you experience a good feeling.",
            sessionScheduled: "Your evaluation session has been successfully scheduled.",
            evaluationSpecialist: "Evaluation Specialist:",
            evaluationSession: "Evaluation Session",
            downloadLinksMessage: "To download Teams, you can use the links below based on your mobile device:",
            messageFirst: "I understand that I need to message the specialist first.",
            messageForSession: "Please install the Teams app right now, and message the evaluation specialist via the link we sent you through WhatsApp or email. At the session time, you can call them and start the conversation...",
            addTo: "Add to",
            platform: "Platform",
            heldIn: "held in ",
            downloadModalTitle: "Download link for Teams platform",
            sessionInfoView: "View session information",
            timezoneLabel: "Timezone:",
            changeTimezone: "Change",
            timezoneModalTitle: "Change timezone",
            "stepOneTitle": "Choose a Time",
            "stepTwoTitle": "Personal Info",
            "stepOneSubtitle": "Next step: Personal Info",
            "stepTwoSubtitle": "Next step: A Better State of Mind",
            "promoStepOne": "Take a step for yourself with a friendly chat",
            "promoStepTwo": "93% of users felt better after their evaluation session",


            downloadLink: "Download link",
            in: "in",


        },
        sessionCard: {
            "info_title": "Session Participation Tips",
            "info_intro": "Here are a few tips to help you have a better evaluation session:",
            "info_1": "The session time is set based on your timezone — we're looking forward to meeting you.",
            "info_2": "Take a moment before the session to reflect on your goals for therapy.",
            "info_3": "Write down any questions you may have so you don’t forget them.",
            "info_4": "Sit somewhere calm and comfortable to help you focus during the session.",
            "info_5": "And don’t worry — everything you share with us stays completely confidential.",
            "countdown": "until the journey begins...",
            "assessment_session": "Evaluation Session",
            "session_link": "Join Session Link",
            "add_to_calendar": "Add to Google Calendar",
            "info_link_text": "Things to know before your session",
            "evaluator_label": "Evaluator:",
            "therapist_label": "Therapist:",
            "show_resume": "View Resume",
            "skype_link": "Therapist Skype Link",
            "request_session": "Request Session",
            "used": "Completed",
            "download_invoice": "Download Invoice",
            "can_enter": "Session is ready to start",
            "date_fixed": "Session is scheduled",
            "reminder_call": "Please call on Skype at the scheduled time.",
            "presession": "Pre-session",
            "extra_time": "For additional minutes",
            "missing_time": "For missed minutes",
            session: "",
            therapist_name: "Therapist :",
            download_starting: "downloading will be started soon",
            scheduled_success: "session has been set.",
            upcoming_sessions: "upcoming sessions"

        },
        inBasketError: 'This package is already in your basket',
        termsTitle: "Terms and Conditions of Simiaroom",
        offlinePayment: {
            title: "Choose your payment method",
            close: "Close",
            action: "Record payment",
            desc: "Deposit the cost of your consulting package to the above account and after payment, enter the payment information in the following fields, and wait for the confirmation of the financial department and the call from Simiaroom.",
            paymentDate: "Date of payment",
            currency: "Currency",
            PaymentAmount: "Amount",
            return: "Return to payment methods"
        },
        next: 'Next',
        yes: 'Yes',
        no: 'No',
        today: "Today",
        emptyStateCart: "Your shopping cart is empty!",
        menu: {
            factor: "Factor",
            dashboard: 'Dashboard',
            messenger: 'Messages',
            sessions: 'Sessions',
            session_request: 'Sessions Request',
            tasks: 'Tasks',
            packages: 'Packages',
            profile: 'Profile',
            file: 'File',
            setting: "Profile",
            wallet: "Wallet",
            close: "Close",
            finance: "Finance",
            tests: "Tests",
            feedback: "Feedback",
            exit: "Exit",
        },
        cycle: "Cycle",
        section_title: {
            gift_code: "Enter the gift code",
            wallet: "Wallet transactions",
            personalInfo: "Personal information",
            moreInfo: "Rest of Information",
            cycles: "Cycles"
        },
        cycles: {
            'Individual': 'Individual',
            'Couple': 'Couple',
            'Family': ' Family',
            'Child and Adolescent': 'Child and Adolescent',
            'Sex Therapy': 'Sex Therapy',
            'Psychiatry': 'Psychiatry',
            'Children and adolescents, growth, parenting': 'Children and adolescents, growth, parenting',
            'Couple therapy': 'Couple therapy',
            'Sex therapy': 'Sex therapy'
        },
        clientStepper: {
            dataTime: {
                hint: "Please enter your Preferred times  slot (on time zone) for the coming session. Adding More time slots  helps us to schedule your session faster.",
                typeCalender: [
                    'Gregorian',
                    'Solar'
                ],
                periods: [
                    'Morning',
                    'Noon',
                    'Afternoon',
                    'Night'
                ],
                changeTimeZone: "Change Time Zone",
                changeTimeZoneHint: "Enter preferred times for session after changing the time zone",
                tables: {
                    header: [
                        'Date',
                        'Times',
                    ],
                }
            }
        },
        knway: {
            youtube: "Youtube",
            google: "Google search",
            twitter: "twitter",
            instagram: "instagram",
            podcast: "podcast",
            bannerads: "Banner ads",
            friends: "Introduce friends and acquaintances",
            site: "site",
            other: "other",
        },
        giftCode: {
            title: "Add gift code",
            placeholder: "code"
        },
        errors: {
            fillIsRequired: 'This field is required.',
            passwordLengthError: "Password must be more than 8 characters",
            emailErrValid: "Enter valid Email address",
            phoneNumberError: "Invalid Phone Number."
        },
        gender: {
            female: 'Female',
            FEMALE: 'Female',
            male: 'Male',
            MALE: 'Male',
            OTHER: 'Others',
            other: 'Others',
        },

        relationships: {
            single: 'Single',
            married: 'Married',
            inrel: 'Partner ',
            other: 'Other ',
            divorced: "Divorce",
            partner: ' Others'
        },
        loginComponent: {
            preparing: "preparing ...",
            whatsAppLogin: "Login with WhatsApp",
            googleLogin: "Login with Google",
            googleSignIn: "Sign up with Google",
            telegramUnavailable: "Telegram account linking is currently unavailable. You can still sign in.",
            changingEmail: "Change email",
            enterVerificationCode: "Enter verification code",
            or: "or",
            verificationCodeSent: "The verification code was sent to you via",
            whatsapp: "WhatsApp",
            sent: "sent",
            resendingCode: "Resend code",
            testLogin: "Login/ Sign-up with your account to receive your test commentary",
            login: "Login",
            title: "Welcome to Simiaroom",
            subTitle: "In order to Purchase, Please Login to your Account",
            form: [
                'Email Address',
                'Password '
            ],
            registerLabel: "Don't you have an account?",
            noAccount: "I don't have an Account!",
            haveAccount: "I have an Account!",
            registerAction: "Sign-Up/ Registration",
            resetPassword: "Forgot Password?",
            forgetPassword: {
                title: "Forgot Password",
                subTitle: "Password re",
                action: "Get Link",
                loginLabel: "Already have an account?",
                loginAction: "Login",
            },
            checkEmail: "Check your Email Inbox",
            postOtpPasswordHint: "Choose a password and confirm it to finish signing in.",
            postOtpPasswordSubmit: "Save and continue",
        },
        medicalView: {
            history: "Have you ever suffer from Medical Condition/Illness",
            servicesHistory: "",
            suicide: {
                yes: "Yes",
                no: "No",
                yesDesc: "I Have!",
                noDesc: "I Have not! "
            },
            yes: "Yes",
            no: "No",
            psychology: "Psychology",
            psychiatry: "Psychiatry",
            mentalHospital: "Mental Institution/Hospital",
            HistorySuicide: "Have you ever Thought about Suicide?",

        },
        dashboard: {
            modal: {
                title: "Your information has been successfully registered.",
                desc: "You enter your user panel and through the user panel you can find out about the status of your sessions and communicate with your dedicated support.",
                action: "Confirm"
            },
            chat: {
                action: "Chat",
                title: "Chat with support",
                placeholder: "Message"
            },
            wallet_status: {
                title: "Credit balance of your sessions:",
                subtitle: " Purchase the package here ",
                action: " Buy"
            },
            session_status: {
                title: "The status of your next session",
                subtitle: " Your session wallet is empty. Please purchase the package ",
                action: "Buy"
            },
            sessions: {
                title: ' Upcoming Sessions ',
                subtitle: ' Reserved Sessions ',
                action: " Details "
            },
            walletSession: {
                title: 'Package Remaining',
                subtitle: 'Sessions Remaining',
                action: 'Purchase '
            },
            wallet: {
                title: 'Transactions',
                subTitle: 'Add Gift Code'
            },
            inviteFriends: {
                title: ' Share the Happiness !',
                subtitle: "10 Dollar Gift ",
                action: 'Invite a friend'
            },
            tests: {
                title: 'Free Psychological Tests',
                viewAll: 'Tests',
            }
        },
        basket: {
            title: "Selected Packages",
            addGiftCode: "Gift Code ",
            factor: "Receipt",
            discountCode: "Coupon and Discount code",
            off: "Offers and Coupons",
            wallet: "wallet",
            finalPrice: "Final Price",
            onlinePay: "Online Payment",
            offlinePay: "Offline Payment ",
            datatable: {
                title: "Package type ",
                qty: "Numbers",
                price: "Unit Price",
                totalAmount: "Total Price"
            }
        },
        wallet: {
            charge: 'Gift Card',
            use: 'Payment',
            balance: 'account Balance',
            wallet: 'wallet',
            transaction: 'Transaction',
            title: "Cart ",
        },
        messenger: {
            fileToolTip: "File Attachment",
            imgToolTip: "Send Video",
            messagePlaceholder: "Text in here...",
            emptyState: "Empty"
        },
        jobs: {
            employed: 'Employed',
            unemployed: 'Unemployed',
            housewife: 'Housewife',
            student: 'Student',
            other: 'Others'
        },
        country: "Country",
        edit: "Edit",
        city: "City",
        tz: "Local Time Zone ",
        noTz: "No Time Zone",
        child: "Offspring",
        marital: "Marital status",
        reserve_session: {
            segment_control: {
                title: "Subject",
                time: "Time",
                reserve: "Reserve",
            }
        },
        segments: {
            reserve: 'Reserve ',
            packages: "Packages",
            profile: "General Information ",
            medicalInfo: "Medical Information ",
            factors: "Receipt",
            wallet: "Wallet"
        },
        session_wallet: {
            empty: {
                title: "There are no credits or packages for booking sessions !",
                action: "charge"
            },
            general_session: "Sessions",
            therapy_session: "Therapy Session",
            action: "Reserve"
        },
        select_cycle: {
            titleSection: "Therapy Subject",
            title: "Subject",
            package: "Package",
            duration: "45 Minutes",
            description: "ِDescription",
            selectCycle: "other packages",
            action: "Confirm and Continue",
            notify: {
                selectCycle: "Select your package please",
                addit: "package added"
            },
            modal: {
                modalTitle: "Therapy Topic/Subject",
                description: "Select your therapy Topic/Subject",
                select_title: "Select your therapy Topic/Subject",
                add_cycle: "Add Topic/Subject",
                add_description: "Add Description",
            },
            cycle_heading: {
                Individual: {
                    title: "Individual",
                    description: "For Myself "
                },
                Couple: {
                    title: "Couple and Family",
                    description: "for my spouse and I"
                },
                family: {
                    title: "Family",
                    description: "For My family"
                },
                kid: {
                    title: "Child and Adolescent",
                    description: "For My child"
                },
                sex: {
                    title: "Sex Therapy",
                    description: "For my partner and I"
                },
                Psychiatry: {
                    title: "Psychiatry",
                    description: "For medical diagnosis and prescription"
                }
            }
        },
        download: "Download",
        description: "Description",
        select_time: {
            title: "time preferences",
            description: "Select your time preferences",
            continue: "Confirm and Continue",
        },
        accept_request: {
            title: "Request Submitted",
            newUser: "your appointment will be fixed after therapist approval" +
                "out support team will be in touch for fixing an assessment and pre therapy session. This 15-minute session will help you to choose the best match therapist for your therapeutic roadmap.",
            oldUser: " your appointment will be fixed after therapist approval "
        },
        factors: {
            datatable: {
                date: "Date",
                type: "type",
                state: "Status",
                amount: "Price"
            },
            factorState: {
                paid: "Successful Payment",
                submit: "Pre-Purchase/Payment",
                canPay: "Payment due ",
                partially_paid: "Advance Payment"
            },
            paymentState: {
                awaiting: "awaiting",
                paid: 'Paid/ Successful Payment ',
                wallet_pending: "",
                failed: "failed",
                debit_checkout: "Failure to pay",
                rejected: "rejected",
                pending: "pending"
            },
            paymentType: {
                wallet_debt_settlement: " Debt ",
                online: "online",
                wallet_payment: "wallet",
                offline: "offline",
            }
        },
        product: "Product",
        details: "Detail",
        factor: "Receipts",
        close: "Close",
        packageFactor: "Package Receipts:",
        paymentDetail: "Payment Details:",
        //ssssss
        account: 'Dashboard',
        nextLabel: 'Next',
        Paid: 'Successful Payment',
        return: "Return",
        confirm: 'Confirm',
        confirmAndContinue: "Confirm and Continue",
        assignconsult: {
            successMessage: "Payment Successful!",
            failedMessage: "Oops! Something went Wrong. We couldn't Process your payment.",
            code: "Transaction Number",
            sorry: 'Sorry',
            oldUserTexts: [
                "Oops! Something went Wrong. We couldn't Process your payment.",
                "Don't worry, You can text the support team via WhatsApp or Telegram to complete the process.",
            ],
            newUserTexts: [
                "Oops! Something went Wrong. We couldn't Process your payment.",
            ]
        },
        sentEmailLink: 'Link has been sent/mailed to your Email Address',
        dontHaveAccount: "Don't have an account?",
        phoneLabel: 'Phone Number',
        matchMaking: {
            pleaseAnswer: 'Answer Few Questions, please.',
            lastMessage: [
                'Approximately',
                'of the people who gave answers similar to yours mentioned the following things in the pre-counseling session and initial therapy sessions (psychological consultation):',
                'Next, after completing the registration process, you can discuss all your conditions and needs with Simiarum\'s psychology specialist in the pre-consultation session.\n',
            ],
            chooseFieldQuestion: 'Select your Therapy Topic/Subject',
            chooseGenderQuestion: 'Specify your preferred therapist gender:',
            buttons: {
                submit: 'Submit',
                firstStep: 'First Step'
            },
            registerForm: {
                registerByGoogle: ' Register by Google Account',
                registerByEmail: 'Sign-Up/ Register by Email'
            },
            primaryResult: {
                1: [
                    '1. Disturbance in sleep and appetite ',
                    '2. Feeling tired due to continuous stress',
                    '3. Difficulty in managing emotions, especially anger',
                    '4.  suffering from cardiovascular Conditions',
                ],
                2: [
                    '1. Difficulty in managing emotions, especially anger',
                    '2. Increase in anxiety symptoms',
                    '3. Challenges in interpersonal communication and partnership',
                ],
                3: [
                    '1. Concentration and Attention Disturbance ',
                    '2. procrastination',
                    '3. Learning and memory problems',
                    '4. Challenges in interpersonal communication'
                ],
                4: [
                    '1. Anxiety Disorders',
                    '2. Procrastination,',
                    '3. Concentration and Attention Disturbance ',
                ],
                5: [
                    '1. Anxiety Disorder',
                    '2.procrastination',
                    '3. Perfectionism',
                    '4.  Concentration and Attention Disturbance',
                ],
                6: [
                    '1.low mood and depression (Cold and Blue)',
                    '2. Difficulty in managing stressful situations',
                    '3. identity and attachment issues',
                ],
                7: [
                    '1. Challenges in Relationship and Partnership',
                    '2. increase in anxiety symptoms',
                ],
                8: [
                    '1. increase in anxiety symptoms',
                    '2. rumination and obsessions',
                    '3. challenges with family and friends',
                ],
                9: [
                    '1. low mood and depression (Sad and Blue)',
                    '2. challenges of intercultural and intergenerational differences',
                    '3. sexual problems',
                ],
                10: [
                    '1. Anxiety Disorders',
                    '2. rumination and obsessions',
                    '3. Challenges in Relationship and Partnership',
                ],
                11: [
                    '1.Difficulties in Emotion Management',
                    '2.Substance Abuse',
                    '3. challenges in interpersonal relationships',
                ],
                12: [
                    '1. challenges in interpersonal relationships',
                    '2. Challenges in the occupation/education',
                    '3.  procrastination',
                    '4. suicidal Ideation',
                ],
                13: [
                    '1. Depressive Disorder',
                    '2. Rumination and worries about body and Self-Image',
                    '3. Anxiety Disorders',
                ],
                14: [
                    '1. Anxiety disorders, including panic attacks',
                    '2. low mood and depression (Sad and Blue)',
                    '3. challenges in interpersonal relationships',
                    '4. Challenges in the occupation/education',
                ]
            },
            profile: 'Profile/Record',
            selectTherapist: 'Therapist Selection',
            register: 'Registration',
            description: 'Description',
            hintTitle: 'Thanks for your answers!',
            hintDescription: [
                'Additional information will be received and reviewed during the Pre-Session. There are also some general questions due to understanding your current condition.'
            ],
        },
        signUpLabel: "Sign-Up/ Registration",
        accountRecoveryCaption: 'Enter your email to recover your account',
        accountRecovery: 'account recovery',
        emailErrRequired: 'Email Required',
        passwordLabel: 'Password',
        passwordConfirmationLabel: 'Re-type password',
        emailLabel: 'Email',
        onboarding: {
            country: {
                title: "Please select your country of residence",
                subTitle: "We need this information to schedule sessions based on your timezone."
            },
            startStep: {
                title: "مشاوره روانشناسی  چگونه کار می کند؟",
                reasons: [
                    '1 - یک نام کاربری و رمز عبور خصوصی برای دسترسی به سیستم ایجاد کنید.',
                    '2 - اطلاعات پرداخت را وارد و مبلغ پکیج مورد نظر را پرداخت نمایید.',
                    '3 - یک جلسه پیش مشاوره برای شناخت بیشتر و تعیین درمانگر شما برگزار می‌شود.',
                    '4 - بر اساس نتیجه جلسه پیش مشاوره، ما شما را با یک درمانگر متخصص هماهنگ می‌کنیم.'
                ]
            },
            stepTwo: {
                title: "What is Chief Complaint? (Choose the main Reason)",
                why: {
                    1: "احساس افسردگی کرده ام",
                    2: "احساس اضطراب یا مستاصل شدن (پریشان حالی و کلافه بودن) می کنم",
                    3: "خلق و خوی من با عملکرد شغلی / درسی ام تداخل دارد",
                    4: "با ایجاد یا حفظ روابط چالش دارم",
                    5: "نمی توانم هدف و معنایی در زندگی ام پیدا کنم",
                    6: "غصه میخورم",
                    7: "ضربه (چالش) روحی را تجربه کرده ام",
                    8: "باید درباره یک چالش یا موقعیت خاص صحبت کنم",
                    9: "می خواهم اعتماد به نفسم را تقویت کرده یا بدست بیاورم",
                    10: "می خواهم خودم را بهبود بخشم اما نمی دانم از کجا شروع کنم",
                    11: "استفاده از خدمات مشاوره به من توصیه شده است (دوست، خانواده، دکتر)",
                    12: "سایر",
                }
            },
            stepThree: {
                title: "انتظارات شما از درمانگرتان چیست؟ درمانگری که…",
                exception: {
                    1: ' شنونده خوبی باشد',
                    2: ' تجربه سکونت در خارج از کشور را داشته باشد',
                    3: ' گذشته من را کاوش می کند',
                    4: ' به من مهارت های جدیدی می آموزد',
                    5: ' باورهای من را به چالش می کشد',
                    6: ' به من تکلیف می دهد',
                    7: ' مرا برای تعیین اهداف راهنمایی می کند',
                    8: ' فعالانه با من در ارتباط بوده و مسائل من را چک می کند',
                    9: ' سایر',
                    10: ' من نمی دانم',
                }
            },
            hint: {
                title: "مشاوره روانشناسی و طول دروه درمان من چند جلسه خواهد بود؟",
                hints: [
                    'حداقل زمانی که شما نیاز دارید تا از اثر مشاوره در زندگی خود آگاه شوید، 5 جلسه است.',
                    'طبق آمار جهانی،',
                    'حداقل میانگین دوره درمان',
                    'روانشناسی 13 تا 16 جلسه بوده،',
                    'اما تعداد این جلسات براساس نوع روش درمان و میزان شدت مسائل ',
                    'شما می‌تواند بیشتر یا کمتر باشد.',
                    '                      بخاطر داشته باشید مسائلی که مدت زیادی شما را دچار چالش کرده است نیازمند زمان جهت بهبود می‌باشد.'
                ]
            },

        },

        register: "Register",
        addCycleModal: {
            add: "Add",
            title: "please write down your chief complaint and describe your problem",
            countChar: "Minimum of 20 Characters",
            categories: [
                'Individual',
                'Couple',
                'Family',
                'Child and Adolescent',
                'Sex Therapy ',
                'Psychiatry',
            ],
            categorySession: "Therapy Subject/Topic ",
            problemDescription: "chief Complaint (Issue Description)",
            problemDescriptionPlaceHolder: "Please explain your main Reason and Chief Complaint:",
            selectCycle: {
                title: "ُpackage selection",
                selecting: "Please select your preferred package",
                confirm: "Confirmed",
                action: "add new package",
                issueSession: "therapy subject:",
                totalSession: "Total session",
                request: "session request",
            },
        },
        loginLabel: 'Login',
        emailErrValid: 'Please enter a valid Email address',
        passwordErrRequired: 'Password required',


        Notify: {
            without_cycle: "To register a meeting request, first complete your information",
            session_request: "The session request was successfully registered",
            medicalEdit: "Added successfully",
            requiredChat: "Please enter the text of your message",
            loginSuccess: "Successful Login",
            loginFailed: "Wrong Username/Password",
            doctorTime: [
                'Just Select one Time/Date',
            ],
            successfully: "Successfully Registered",
            successfullyV2: "Registration Completed",
            successfullyWithRedirect: "Successfully registered, you will be redirected to your dashboard and Profile",
            successfullyRequest: 'Your request submitted',
            successDateTime: "Your information has been successfully registered, we will contact you within the next 24 hours",
            changePassword: "Password Successfully Changed",
            packageComponent: "Please select at least one currency for each package",
            changeStatus: 'Please select a Therapy Time and Date',
            DateTime: {
                selectDateFirst: "Please Select the Date",
                selectDay: "Please select at least one Time and Date",
                success: "Your selected times have been registered successfully",
            },
            clientSessionRequest: 'Please select your therapy time and date',
            signupComponent: {
                phoneNumberError: "Phone Number Invalid.",
                success: "Regestration Compeleted Successfully",
                emailError: "Email already used/Existed",
                passwordError: "Password Incorrect",
                passwordLengthError: "The minimum password is 8 characters",
                rePasswordRequired: "This field is required. ",
                rePasswordCheck: 'password and Re-enterd password are not the same',
                phoneRequired: "Phone Number Required",
                countrySelected: "Select Regional Code",
                passwordRequired: "Password reenter required",
                notSamePassword: 'password and Re-enterd password are not the same',
            },
            login: {
                password: {
                    passwordRequired: "Password is required",
                    minLength: "The minimum password is 8 characters"
                }
            },
            acceptTerms: "Agreement to the terms and conditions is required.",
            calender: {
                successDelete: "Successfully deleted"
            },
            error: 'An error has occurred',
            connectionError: "Please check your internet connection",
            addNewCycle: {
                oneSession: "the minimum is one session.",
                notEnough: "Not Enough Sessions.",
            },
            clientDashboard: {
                selectTime: "Please select a Therapy Time and Date",
                selectOne: "Please select one of the options",
                success: "Your time have been successfully recorded",
                selectTimespan: "Please select at least one time duration"
            },
            ExperssComponent: {
                selectOne: "Please Select a therapy time and date",
                successSelect: "Your suggested times have been successfully registered"
            },
            CartComponent: {
                enterCode: "Enter Coupon/discount Code",
                needLogin: "Please sign-up/Login to your account"
            },
            cycle: {
                needPackage: "No Package existed, please purchase before Reservation"
            },
            sessionCountRules: {
                countRequired: "Session Numbers",
                minCount: "The number of sessions must be at least 1",
                buyError: "The number of requested sessions must be less than your purchased sessions"
            },
            successCopy: ' Copied'
        },
        signupComponent: {
            title: "Create account",
            subTitle: "Please fill out boxes below",
            form: [
                "Phone Number (with Regional Code)"
            ],
            terms: "accept the Terms and Conditions",
            accept: '.',
            inviter: "Invitation Code",
            action: "Sign Up/ Register",
            loginLabel: "Already have an account?",
            loginAction: "Login",
            phone: "Phone Number",
            registerAction: "Register",
            acceptTerms: 'I accept the Terms and Condition',
            loginActionOnBoarding: "Login",
        },
        onBoarding: {
            titleLogin: "Sign-Up",
            continue: 'Continue',
            next: "Next",
            prev: "Previous",
            selectedPackage: 'Package Selection ',
            stepOne: {
                title: 'Toward a better Me',
                subTitle: "For a better matching, please answer some quick questions.",
                start: "Start",
                skip: "Not now!"
            },
            stepTwo: {
                title: "Select your therapy topic/Subject",
                subtitle: "you can change all the profile information whenever you want!.",
                cycles: [
                    'Individual Therapy',
                    'Relationship and Couple',
                    'Child and Adolescent',
                    'Family Therapy',
                    'Psychiatry',
                    'Sex Therapy'
                ],
            },
            stepThree: {
                title: "Choose your gender preference",
                gender: [
                    'Male (He/His)',
                    'Female (She/Her)',
                    'Other'
                ]
            },
            stepFour: {
                title: 'Your Age:',
                agePeriods: [
                    '18 or Less',
                    '46 or more'
                ]
            },
            stepFive: {
                title: "Choose your Relation/Marital status:",
                relationships: [
                    'Single',
                    'Married',
                    'In Relationship ',
                    'Others'
                ]
            },
            history: "Have you ever tried therapy before?",
            loginWithDifferentWay: "Login with:",
            loginItems: [
                'Googe Account',
                'Facebook Account'
            ],
            terms: "Terms and Conditions ",
            wizardLogin: [
                'Sign-Up/ Registration',
                'Login'
            ],
            wizardStepIndicator: {
                info: "Information",
                register: "Registration ",
                pay: "Payment",
                selectConsulter: "Therapist and Date Selection"
            },
            whoSelectDoctor: {
                title: "How do you prefer select your therapist?",
                items: [
                    'I will choose all by myself',
                    "I want to have SimiaRoom Specialists' Suggestions!",
                ]
            },
            selectDoctor: {
                searchPlaceHolder: 'Search: Name',
                labelCategory: 'Expertise',
                all: 'All'
            },
            selectTime: {
                title: "preferred time and Date",
                subTitle: "Please select your preferred Time and Date for your therapy session"
            },
            testimonial: {
                sweden: {
                    title: 'Sima from Sweden',
                    desc: "I am very happy that I found you and I was able to get help from you and my therapist in a situation where I could not trust anyone. I am much better now and I really thank you."
                },
                usa: {
                    title: 'Ebrahim From US',
                    desc: "Therapy changed my Life Attitude! Being Relieved from suffering was the most enjoyable gift from Therapy insight. Thank you and your Therapists."
                },
                germany: {
                    title: 'Delnaz from Germany ',
                    desc: "I had a bad-therapy experience and I know how much it can hurt you so I was afraid to start trusting again. Until my friend introduced you. I am so grateful for my current Happiness."
                },
                usaTwo: {
                    title: 'Shadi from US',
                    desc: "Thank you Simiarom and the counselors. I really haven't seen this system anywhere that even change their own therapists in order to give you the best quality!"
                },

                priceBox: {
                    select: 'package selection',
                    title: 'Select your Package ',
                    subtitle: 'choose your preferred package',
                    labelPopular: 'Most Popular',
                    labelBestPrice: 'Best Price',
                    hint: 'Unconditional refund in case of dissatisfaction',
                    emptyBasket: 'Empty Cart'
                }
            },
            signupComponent: {
                title: "Create account",
                subTitle: "Please fill out boxes below",
                form: [
                    "Phone Number (with Regional Code)"
                ],
                terms: "accept the Terms and Conditions",
                accept: '.',
                inviter: "Invitation Code",
                action: "Sign Up/ Register",
                loginLabel: "Already have an account?",
                loginAction: "Login",
                phone: "Phone Number",
                registerAction: "Sign Up/ Register",
                acceptTerms: 'I accept the Terms and Condition',
                loginActionOnBoarding: "Login",
            },
            logout: 'Log out',
            showAccount: "Account",
            $vuetify: {
                noDataText: 'No entry'
            },


            welcome: 'Welcome to your Personalized Dashboard',
            singleFactor: {
                title: "Selected Packages",
                addGiftCode: "Add Gift Code",
                factor: "Recipt",
                discountCode: "Coupon and Discount Code",
                off: "Discount/ Gift Code",
                wallet: "wallet",
                remaining: "Remaining",
                finalPrice: "Final Price",
                onlinePay: "Online Price",
                offlinePay: "Offline Price ",
                payments: "Payments",
                datatable: {
                    title: "Package Type",
                    qty: "Number",
                    price: "Unit Price",
                    totalAmount: "Total Price",
                    off: "Discount"
                }
            },
            socialLogin: {
                enterNumber: "Enter your phone number, please"
            },
            CartComponent: {
                PreSessionModal: {
                    title: 'Pre-Counseling Session',
                    continue: "Confim and Continue",
                    text: {
                        leadUser: [
                            "out support team will be in touch for fixing an assessment and pre therapy session. This 15-minute session will help you to choose the best match therapist for your therapeutic roadmap.",
                        ],
                        oldUser: [
                            " your appointment will be fixed after therapist approval ",
                            'our support team will send your payment link',
                            'Contact us',
                        ]
                    },
                    action: 'Okay! '
                },
                payByWallet: "Use my wallet",
                clearingWallet: 'clear wallet',
                invoiceItems: {
                    total: " Total Price",
                    offer: "Discount",
                    finalPrice: "Final Price"
                },
                title: "selected Packages",
                price: "Price",
                headersTable: [
                    'Item',
                    'Unit Price',
                    'Numbers',
                    'Total Price ',
                    'Delete'
                ],
            }
        },
        notifications: {
            needBuy: {
                title: "No Package available",
                description: "Purchase a Package before Session Reservation",
                action: "Package Purchase"
            },
            reserveSession: {
                title: "Reserve Session",
                description: "Reserve a new Session",
                action: "Reserve Session"
            }
        },
        payableFactor: {
            title: "You have a Payment Receipt for",
            mobTitle: "You have a pending payment!",
            have: "",
            details: 'Details'
        },
        notify: {
            completeProfile: "Complete your profile, please",
            giftCodeSubmit: "Gift Code Submitted",
            couponCodeSubmit: "Coupon/Discount Code Submitted",
            deleteCouponCode: "Coupon/Discount Code deleted",
            notFoundCoupon: "Coupon/Discount Code not found",
            enterCouponCode: "Enter Coupon",
            failed: "Action Failed. ",
            passwordChange: "Password Changed",
        },
        sidebar: {
            calender: {
                title: "Session Booked/Reserved",
            },
            chat: "Chat with Support/Specialist",
            localClock: "Local Time Zone",
        },
        layout: {
            footer: {
                charge: " charge",
                reserve: "Reserve",
                wallet: "Wallet"
            }
        },
        currency: {
            longRial: 'Toman',
            rial: 'T',
            dollar: '$',
            dollarCanada: 'Dollar (Canada) ',
            dollarAustralia: 'Dollar (Australia)',
            Euro: '€',
            try: 'try',
            pound: 'pound',
        },
        routes: {
            dashboard: 'Overview',
            sessions: 'Sessions',
            wallet: 'Transactions',
            index: 'Home Page',
            packages: 'Packages/ Reserve',
            messenger: "Messenger",
            profile: 'My Record/Profile',
            tasks: 'Test & Task',
            userInfo: 'Setting',
            basket: 'Cart',
            factor: 'Receipt',
            personal_profile: "Personal info",
            roots_in_soil: 'Roots in soil',
            tests: 'My tests',
            test_history: 'Test history',
            chatGpt: 'AI tool',
            invitees: 'Invitations',
            presession_detail: 'Pre-consultation details',
        },
        months: {
            farvardin: 'Farvardin',
            ordibehesht: 'Ordibehesht',
            khordad: "Khordad",
            tir: 'Tir',
            mordard: "Mordad",
            sharivar: "Shahrivar",
            mehr: "Mehr",
            aban: "Aban",
            azar: "Azar",
            day: "Dey",
            bahman: "Bahman",
            esfand: "Esfand",
        },
        days: {
            Saturday: "Saturday",
            Sunday: "Sunday",
            Monday: "Monday",
            Tuesday: "Tuesday",
            Wednesday: "Wednesday",
            Thursday: "Thursday",
            Friday: "Friday",
        },
        mobileMenu: {
            dashboard: "Dashboard",
            tasks: "Tasks",
            sessions: "Sessions",
            packages: "Packages",
            profile: "Records/ Profiles",
            setting: "Setting",
            wallet: "Ttansactions",
            account_manager: "Support",
            chat: "Messenger",
            terms: "Terms and Conditions/confidentiality",
            about: "About Us ",
            logout: " Sign Out "
        },
        sessions: {
            empty: "No Session Reserved",
            action: "Reserve",
            loading: "Loading... Thanks for your Patience",
            title: "Your sessions",
            session_request: "Request a new session",
            modal: {
                title: " Your counseling session has been successfully registered",
                subtitle: " Our experts will contact you within 48 hours ",
                action: "Confirm"
            }
        },
        packages: {
            perSession: "Per Session",
            buy: "Purchase ",
        },
        tasks: {
            empty: {
                title: "No Task/Test assigned!",
                text: "Tasks and Tests will be assigned..."
            }
        },
        resetPassword: {
            title: "Reset/Forget Password",
        },
        profileView: {
            name_hint: 'You can use a pseudonym.',
            email: "email",
            degree: "Degree",
            contact: "Contact Info ",
            username: "User",
            job: "Job",
            sex: "Gender",
            moreInfo: "More Information",
            non: "گفته نشده",
            child: "Child",
            noKid: "No child",
            age: "Age",
            localTime: "Timezone",
            moreInfoModal: {
                countryLabel: "Select your Location",
                localTime: "Select your Time Zone",
                childCount: "How Many kids do you have?",
                noChild: "No child"
            },
            jobModal: {
                title: " ٌWhat is your career status?",
            },
            birthDate: "Birth Date",
            name: "Name",
            family: "Last Name ",
            jobStatus: "Job/Career Status ",
            tell: "Phone Number",
            childOrder: "What is your birth order?",
            maritalStatus: "Marital Status ",
            childCount: " How Many kids do you have?",
        },
        userOnboarding: {
            specifications: "Details",
            childOrder: " What is your birth order?",
            maritalStatus: "What is your Marital Status",
            childCount: " How Many kids do you have?",
            jobStatus: " ٌWhat is your career status?",
            country: "ٌWhere do you live?",
            tz: "Time Zone",
            city: "City",
            notify: {
                done: "Done"
            }
        },
        new_onboarding: {
            next: "Next page ",
            prev: "Previous page",
            new_indicator: {
                info: "Demography",
                title_session: "Therapy field",
                register: "Registration"
            },
            stepOne: {
                text: [
                    "We are very happy to be with you. Whenever you enter your information ",
                    'Your meetings will be arranged. Meetings will be held online with ',
                    'Google meet',
                    '',
                    'Before the main sessions, you have a pre-consultation.'

                ],
                action: "Complete information"
            },
            stepTwo: {
                title: "Please enter your name and details here.",
                name: "Name",
                family: "Last Name ",
                tell: "Phone Number",
                birthDay: "Birthdate*"
            },
            stepThree: {
                title: "Your location information is received to better match your local time with the therapist.",
                country: "Country",
                city: 'City',
                tz: 'Timezone',
                kenway: 'how know us'
            },
            stepFour: {
                title: " Please write a short description of your problem in this section. ",
                category: "Subject",
                desc: "Descrition"
            },
            idicatior: {
                info: " Information ",
                location: "Location",
                cycle: "Reason"
            },
        },
        education: {
            NoDegree: 'No degree',
            Diploma: 'Diploma',
            AssociateDegree: 'Associate Degree',
            Bachelor: 'Bachelor',
            MA: 'MA',
            PHD: 'P.H.D',
        },
    },
    fa: {
        intake: {
            screeningUi: {
                estimate: "سؤال‌ها بر اساس پاسخ‌ها و موضوع‌های انتخابی تو تنظیم می‌شوند؛ طول مسیر برای همه یکسان نیست. می‌توانی بعداً از پاسخ‌های ذخیره‌شده ادامه بدهی.",
                answeredCount: "پرسش‌های پاسخ‌داده‌شده: {count}",
                adaptiveLength: "سؤال‌های باقی‌مانده به پاسخ‌هایت بستگی دارند.",
                topicContext: "دربارهٔ: {topics}",
                clarificationContext: "کمک به روشن‌شدن موضوعی که به تجربهٔ تو نزدیک‌تر است.",
                emptyChoices: "این سؤال درست آماده نشده است. پاسخ‌های قبلی‌ات ذخیره شده‌اند. همین سؤال را دوباره دریافت کن یا به مرحلهٔ قبل برگرد.",
                r8GuideTitle: "راهنمای فهم این سؤال",
                r8GuideIntro: "این سؤال چند وضعیت متفاوت را کنار هم آورده است. دربارهٔ وضعیت فعلی پاسخ بده؛ قرار نیست خودت تشخیص پزشکی بدهی.",
                r8GuideUncertain: "اگر معنی عبارتی روشن نیست یا نمی‌دانی دربارهٔ تو صدق می‌کند، می‌توانی «مطمئن نیستم» را انتخاب کنی. این پاسخ مسیر بررسی را فعال می‌کند و به‌تنهایی به معنی بحران فوری نیست. شروع دوباره، محدودیت قبلی را خودکار برنمی‌دارد.",
                retryQuestion: "دریافت دوبارهٔ همین سؤال",
                retryingQuestion: "در حال دریافت دوبارهٔ سؤال…",
                retryQuestionFailed: "دریافت دوبارهٔ سؤال انجام نشد. پاسخ‌های قبلی‌ات ذخیره شده‌اند. دوباره تلاش کن یا به مرحلهٔ قبل برگرد.",
                backQuestion: "بازگشت به مرحلهٔ قبل",
                wellbeingContext: "دربارهٔ حال عمومی تو، در مجموع موضوع‌های انتخابی",
                historyContext: "دربارهٔ سابقهٔ کمک تخصصی و شرایط فعلی",
                safetyContext: "دربارهٔ ایمنی و گام مناسب بعدی",
                preferencesContext: "دربارهٔ زمان‌های ممکن و ترجیحات درمانگر",
                serviceContext: "دربارهٔ نوع خدمت و شرکت‌کنندگان",
                optionalPreference: "اختیاری است؛ می‌توانی بدون انتخاب ترجیح به مرحلهٔ بعد بروی.",
                flexibilityHelp: "انتخاب همهٔ روزها به معنی آزادبودن همهٔ ساعت‌ها نیست. این سؤال دربارهٔ امکان جلسه خارج از بازه‌های انتخابی توست.",
                pastCareHelp: "اینجا دربارهٔ کمک تخصصی‌ای که گرفته‌ای می‌پرسیم. ارتباط فعلی با درمانگر یا پزشک را جداگانه بررسی می‌کنیم.",
                currentCareHelp: "این سؤال دربارهٔ ارتباط فعلی توست، جدا از تجربهٔ درمان قبلی؛ تا مراقبتی که اکنون دریافت می‌کنی در نظر گرفته شود.",
                recallTwoWeeks: "در دو هفتهٔ گذشته",
                screening: "حال این روزها",
                ageLabel: "چند سال داری",
                safetyIntro: "لطفاً این سؤال‌ها را بر اساس وضعیت فعلی پاسخ بده. این پاسخ‌ها کمک می‌کنند قدم بعدی مناسب شرایطت مشخص شود. اگر این مسیر برای شرایط تو مناسب نباشد، قدم امن بعدی را نشانت می‌دهیم. پاسخ این بخش محرمانه ثبت می‌شود.",
                restart: "شروع از ابتدا",
                restartConfirm: "از ابتدا شروع می‌کنی؟ پیشرفت این فرم و انتخاب درمانگر پاک می‌شود. رزروهای قبلی در حساب تو باقی می‌مانند.",
                unavailable: "این فرم موقتاً در دسترس نیست. لطفاً کمی بعد دوباره تلاش کن.",
            },
            matchUi: {
                count: "تعداد پیشنهادهای در دسترس: {count}",
                refreshMessages: "دریافت پیام‌های تازه",
                restartNeeded: "برای نمایش پیشنهادهای معتبر، لازم است این فرم دوباره تکمیل شود.",
                refreshNeeded: "این پیشنهادها نیاز به تازه‌سازی دارند. لطفاً دوباره تلاش کن.",
                empty: "در حال حاضر پیشنهادی برای انتخاب در دسترس نیست. برای قدم بعدی می‌توانی از پشتیبانی کمک بگیری.",
                declineAll: "هیچ‌کدام مناسب نیست",
                declineHint: "می‌توانی برای انتخاب درمانگر از پشتیبانی کمک بگیری؛ نیازی به رزرو یا پرداخت نیست.",
                supportIntro: "برای دریافت کمک در انتخاب درمانگر، این فرم را ارسال کن. درخواست پس از ارسال موفق ثبت می‌شود.",
                supportBody: "هیچ‌کدام از درمانگران پیشنهادی مناسب نیست. لطفاً برای قدم بعدی راهنمایی کنید. کد پیشنهاد: {reference}",
            },
            bookingUi: {
                timezone: "زمان‌ها به وقت {zone} نمایش داده می‌شوند.",
                loading: "در حال دریافت زمان‌های در دسترس…",
                loadError: "دریافت زمان‌ها انجام نشد. لطفاً دوباره تلاش کن.",
                retry: "تلاش دوباره",
                empty: "در حال حاضر زمانی در دسترس نیست. می‌توانی درمانگر دیگری انتخاب کنی.",
                preparingHold: "در حال بررسی و نگه‌داشتن زمان انتخابی…",
                paymentUnavailable: "پرداخت موقتاً در دسترس نیست. لطفاً کمی بعد دوباره تلاش کن.",
            },
            title: "برای شروع، کمی از شرایطت بدانیم",
            intro: "این پاسخ کمک می‌کند وارد مسیر درست شوی.",
            progressLabel: "پیشرفت آنبوردینگ",
            progressMeta: "گام {step} از {total}",
            serviceTitle: "شرکت‌کنندگان و مسیر خدمت",
            stepLabels: {
                participants: "شرکت‌کنندگان",
                topics: "موضوع‌ها",
                priority: "اولویت",
                severity: "شدت اثر",
                complexity: "سابقه و دامنه",
                safety: "غربال ایمنی",
                crisis: "پاسخ ایمنی",
                availability: "زمان و ترجیح",
                review: "مرور",
                match: "پیشنهاد",
                profile: "پروفایل درمانگر",
                slots: "زمان جلسه",
                checkout: "رزرو",
                consent: "رضایت‌نامه",
                baseline: "نقطه شروع",
                letter: "نامه",
            },
            u1Title: "کدام بخش زندگی بیشتر درگیر است؟",
            u1Legend: "این سختی بیشتر روی کدام بخش‌های زندگی اثر گذاشته؟",
            u1: {
                mood: "حال و خلق",
                body: "بدن و خواب",
                rel: "رابطه‌ها",
                work: "کار/تحصیل",
                other: "دیگر",
            },
            u2Title: "کدام تجربه‌ها به چیزی که این روزها می‌گذرد نزدیک‌تر است؟",
            u2Legend: "کدام تجربه‌ها به چیزی که این روزها می‌گذرد نزدیک‌تر است؟",
            u2: {
                a: "نگرانی و تنش",
                b: "افت حال",
                c: "تعارض رابطه‌ای",
                d: "چیز دیگر",
            },
            u3Title: "از بین این گزینه‌ها، کدام به تجربهٔ تو نزدیک‌تر است؟",
            u3Legend: "از بین این گزینه‌ها، کدام به تجربهٔ تو نزدیک‌تر است؟",
            u3: {
                yes: "نزدیک است",
                partial: "تا حدی",
                no: "نه",
            },
            u3Submit: "تأیید موضوع",
            intAgeTitle: "سن شرکت‌کننده",
            intAgeLegend: "سن شرکت‌کننده را بنویس",
            unsupportedTitle: "این ترکیب نیاز به مسیر دیگری دارد",
            unsupportedBody: "می‌توانی ترکیب شرکت‌کنندگان را عوض کنی یا از مسیر دیگری ادامه بدهی.",
            unsupportedAction: "اصلاح شرکت‌کنندگان",
            safeCoupleTitle: "امنیت در رابطه",
            safeCouple: {
                coupleFear: "آیا در این رابطه از واکنش طرف مقابل می‌ترسی، یا برای صحبت آزادانه دربارهٔ رابطه احساس امنیت نمی‌کنی؟",
                coupleHarm: "در شش ماه گذشته تهدید، کنترل شدید، اجبار جنسی یا آسیب جسمی در رابطه رخ داده است؟",
            },
            safeFamilyTitle: "امنیت در خانواده",
            safeFamily: {
                familyFear: "آیا هیچ‌کدام از اعضای خانواده در خانه از فرد دیگری می‌ترسد یا نمی‌تواند آزادانه و امن صحبت کند؟",
                familyConcern: "آیا نگرانی فعالی دربارهٔ خشونت، آزار، آسیب به خود/دیگران یا امنیت کودک وجود دارد؟",
            },
            safeYouthTitle: "امنیت فرزند",
            safeYouth: {
                youthTalk: "آیا کودک/نوجوان اخیراً دربارهٔ مردن، نبودن یا آسیب‌زدن به خودش حرف زده یا رفتاری نشان داده است؟",
                youthConcern: "آیا نگرانی فعالی دربارهٔ خشونت، آزار، بی‌توجهی جدی، فرار، مصرف مواد یا خطر برای دیگران وجود دارد؟",
            },
            safeAcuteTitle: "وضعیت‌های فعلی",
            p3Title: "این موضوع‌ها معمولاً هم‌زمان پررنگ می‌شوند؟",
            p3Legend: "این موضوع‌ها معمولاً هم‌زمان پررنگ می‌شوند یا جدا از هم؟",
            sevPerNeedTitle: "اثر این موضوع بر زندگی",
            sevPerNeedSubmit: "ثبت و ادامه",
            sevPerNeed: {
                frequency: "برای «{topic}»: در دو هفتهٔ گذشته، این موضوع چند وقت یک‌بار حضور داشته؟",
                function: "برای «{topic}»: انجام کارهای روزمره، مراقبت از خود، کار/تحصیل یا ارتباط را چقدر سخت کرده؟",
                trend: "برای «{topic}»: در چند هفتهٔ اخیر، بیشتر چه روندی داشته؟",
            },
            branch: {
                shellTitle: "ماژول موضوعی",
                shellLead: "ادامه به پرسش‌های موضوعی مسیر تو.",
                submit: "ثبت و ادامه",
                scale: {
                    NONE: "تقریباً هیچ",
                    LOW: "کم",
                    MED: "متوسط",
                    HIGH: "زیاد",
                },
                yesNo: {
                    yes: "بله",
                    no: "خیر",
                },
                modules: {
                    anger: {
                        title: "خشم و واکنش‌ها",
                        q1: "واکنش‌ها چند وقت یک‌بارند؟",
                        q2: "پیامد جدی ساخته‌اند؟",
                    },
                    anx: {
                        title: "اضطراب و ترس",
                        q1: "این اضطراب معمولاً چند وقت یک‌بار سراغت می‌آید؟",
                        q2: "به‌خاطرش از موقعیت‌ها، آدم‌ها یا کارهایی دوری می‌کنی؟",
                    },
                    bipolar: {
                        title: "تغییر خواب و انرژی",
                        q1: "در این دوره‌ها کاهش خواب، افزایش انرژی یا تصمیم‌های ناگهانی چقدر روی زندگی‌ات اثر گذاشته؟",
                        q2: "آیا این تغییر نسبت به حالت معمول تو ناگهانی بوده و دیگران هم متوجه آن شده‌اند؟",
                    },
                    couple: {
                        title: "رابطهٔ زوج",
                        q1: "در چهار هفتهٔ اخیر بحث یا قطع ارتباط چند وقت یک‌بار رخ داده؟",
                        q2: "بعد از تعارض معمولاً چقدر طول می‌کشد دوباره ارتباط قابل‌قبول شود؟",
                    },
                    dep: {
                        title: "حال و خلق",
                        q1: "این حالت در روزهای اخیر چقدر در زندگی روزمره‌ات حضور داشته؟",
                        q2: "چقدر کارهای معمول، مراقبت از خود یا ارتباط را سخت کرده؟",
                    },
                    eating: {
                        title: "غذا و بدن",
                        q1: "این موضوع چقدر ذهنت را درگیر می‌کند؟",
                        q2: "روی سلامت جسمی، انرژی یا توان روزمره اثر گذاشته؟",
                    },
                    family: {
                        title: "روابط خانواده",
                        q1: "تنش یا قطع ارتباط چند وقت یک‌بار رخ می‌دهد؟",
                        q2: "بعد از تعارض خانواده چقدر می‌تواند به ارتباط عادی برگردد؟",
                        q3: "کارهای اساسی خانواده چقدر مختل شده؟",
                    },
                    health: {
                        title: "سلامت و درد",
                        q1: "این موضوع چقدر روی خلق، خواب، تمرکز یا رابطه اثر گذاشته؟",
                        q2: "آیا پیگیری پزشکی فعال لازم است یا انجام نشده؟",
                    },
                    identity: {
                        title: "خودشناسی و هویت",
                        q1: "این موضوع چقدر روی انتخاب‌ها و رابطه‌ها اثر گذاشته؟",
                        q2: "خودانتقادی یا شرم چقدر تکرار می‌شود؟",
                    },
                    neuro: {
                        title: "توجه و رشد",
                        q1: "این مشکل در چند محیط دیده می‌شود؟",
                        q2: "چقدر باعث بی‌نظمی، عقب‌افتادن یا افت عملکرد شده؟",
                    },
                    ocd: {
                        title: "فکرها و رفتارهای تکراری",
                        q1: "فکرها یا رفتارها روزی چقدر وقت می‌گیرند؟",
                        q2: "اگر نتوانی آن‌ها را دنبال کنی، اضطرابت چقدر بالا می‌رود؟",
                    },
                    parent: {
                        title: "مشورت دربارهٔ فرزند",
                        q1: "مسئله چند وقت یک‌بار رخ می‌دهد؟",
                        q2: "در چند محیط دیده می‌شود؟",
                        q3: "چقدر زندگی روزمره خانواده یا رابطه شما با فرزند را مختل کرده؟",
                        q4: "آیا لازم است خود کودک/نوجوان هم مستقیماً ارزیابی یا درمان شود؟",
                    },
                    perinatal: {
                        title: "بارداری و پس از آن",
                        q1: "این موضوع به کدام دوره نزدیک‌تر است؟",
                        q2: "چقدر روی خلق، خواب، احساس امنیت یا توان انجام کارهای روزمره اثر گذاشته؟",
                    },
                    psychosis: {
                        title: "تجربه‌های غیرمعمول",
                        q1: "اخیراً چیزهایی دیده یا شنیده‌ای که دیگران متوجه نمی‌شوند، یا باورهایی داشته‌ای که برای دیگران بسیار غیرمعمول است؟",
                    },
                    relationship: {
                        title: "رابطه",
                        q1: "این موضوع چقدر روی حال عمومی، تمرکز یا عملکرد اثر گذاشته؟",
                        q2: "آیا به بحران فعلی تبدیل شده؟",
                    },
                    sexual: {
                        title: "سلامت جنسی و صمیمیت",
                        q1: "این موضوع چقدر روی رابطه، صمیمیت یا حال روانی اثر گذاشته؟",
                        q2: "آیا درد، اجبار، نگرانی پزشکی یا اجتناب شدید وجود دارد؟",
                    },
                    sleep: {
                        title: "خواب",
                        q1: "چند شب در هفته این مشکل خواب اتفاق می‌افتد؟",
                        q2: "روز بعد چقدر روی انرژی، تمرکز یا عملکرد اثر دارد؟",
                    },
                    substance: {
                        title: "عادت‌های اعتیادی",
                        q1: "کنترل این رفتار چقدر سخت شده؟",
                        q2: "روی کدام بخش‌ها اثر گذاشته؟",
                    },
                    trauma: {
                        title: "سوگ و تروما",
                        q1: "این تجربه چقدر روی خواب، تمرکز، رابطه یا احساس امنیتت اثر گذاشته؟",
                        q2: "آیا هنوز در موقعیت یا رابطه‌ای هستی که احساس ناامنی می‌سازد؟",
                    },
                    work: {
                        title: "کار و فرسودگی",
                        q1: "عملکردت چقدر تغییر کرده؟",
                        q2: "فرسودگی بیشتر شبیه کدام است؟",
                    },
                    youth: {
                        title: "حال و رفتار فرزند",
                        q1: "رفتار یا حال نگران‌کننده چند وقت یک‌بار دیده می‌شود؟",
                        q2: "در کجا دیده می‌شود؟",
                        q3: "چقدر روی مدرسه، دوستی، خواب، غذا یا کارهای روزمره اثر گذاشته؟",
                        q4: "نسبت به قبل افت یا تغییر محسوسی دیده‌اید؟",
                    },
                },
            },
            disp: {
                accountSupportAction: "کمک برای حساب کاربری",
                accountSupportNote: "پشتیبانی برای مسائل حساب کاربری و استفاده از سایت است؛ این فرم کمک فوری، ارجاع بالینی یا درخواست تماس نیست. برای استفاده از راه‌های کمک بالا منتظر پاسخ پشتیبانی نمان. پاسخ‌های غربال و جزئیات بالینی را اینجا ننویس.",
                accountSupportBody: "برای حساب کاربری و استفاده از سایت به راهنمایی اجرایی نیاز دارم. لطفاً راه ادامه را راهنمایی کنید.",
                sharedIntro: "مسیر پیشنهاد معمول درمانگر در این وضعیت ادامه پیدا نمی‌کند. از منابع تأییدشده استفاده کن.",
                opsQueued: "درخواست پشتیبانی برای مسیر همین حساب ثبت شد.",
                more: {
                    title: "برای ادامه، یک پاسخ را روشن کنیم",
                    intro: "یکی از پاسخ‌ها برای پیشنهاد امن درمانگر معمول به‌اندازهٔ کافی روشن نیست. از همان‌جا که مانده‌ای ادامه بده؛ این یک شروع از صفر نیست.",
                    action: "تکمیل پاسخ‌ها",
                    clarifySafety: {
                        title: "برای پیشنهاد امن، یک پاسخ را روشن کنیم",
                        intro: "دربارهٔ یک یا چند وضعیت فعلی «مطمئن نیستم» را انتخاب کردی. این پاسخ نیاز به روشن‌سازی و بررسی دارد و به معنی تأیید بحران فوری نیست. برای اصلاح برداشت اشتباه می‌توانی سؤال را بازبینی کنی. محدودیت قبلی رزرو همچنان به بررسی تیم مراقبت نیاز دارد؛ تغییر پاسخ یا شروع دوباره آن را خودکار رفع نمی‌کند.",
                        action: "بازبینی سؤال ایمنی",
                        helpAction: "اگر واقعاً مطمئن نیستی، دیدن راه‌های کمک",
                    },
                    terminalFinalized: {
                        title: "این غربال از اینجا ادامه پیدا نمی‌کند",
                        intro: "این پرونده قبلاً بسته شده و قابل ویرایش نیست. غربال جدید را شروع کن، یا اگر برای همین حساب کمک می‌خواهی با پشتیبانی تماس بگیر.",
                        action: "شروع غربال جدید",
                        supportHint: "اگر برای مسیر همین حساب به کمک دستی نیاز داری، با پشتیبانی تماس بگیر.",
                    },
                },
                unsupported: {
                    title: "این ترکیب نیاز به مسیر دیگری دارد",
                    intro: "این ترکیب شرکت‌کننده و سن برای مچ معمول پشتیبانی نمی‌شود. ترکیب یا سن‌ها را عوض کن، یا از مسیر دیگری ادامه بده.",
                    action: "اصلاح شرکت‌کنندگان",
                },
                private: {
                    title: "در فضای امن پاسخ بده",
                    action: "بازگشت به سؤال‌های ایمنی",
                },
                specialist: {
                    title: "راهنمای دریافت کمک مناسب",
                    action: "دیدن راه‌های کمک",
                    reviewTitle: "پاسخ‌های غربالگری تو",
                    reviewAction: "بازبینی پاسخ‌ها",
                    editAction: "اصلاح آخرین پاسخ",
                    restartAction: "شروع غربال جدید",
                    reviewUnavailable: "این پاسخ‌ها از اینجا قابل ویرایش نیستند. می‌توانی غربال جدیدی شروع کنی یا از راه‌های کمک پایین استفاده کنی.",
                },
                ops: {
                    title: "هماهنگی لازم است",
                    action: "باز کردن فرم پشتیبانی",
                },
            },
            clinical: {
                disclaimer: "پاسخ‌ها تشخیص نیستند؛ فقط برای ادامهٔ مسیر غربال استفاده می‌شوند.",
                submit: "ثبت پاسخ‌ها",
                likert: {
                    "0": "اصلاً",
                    "1": "چند روز",
                    "2": "بیش از نیمی از روزها",
                    "3": "تقریباً هر روز",
                },
                yesNo: {
                    yes: "بله",
                    no: "خیر",
                },
                cssrsTiming: {
                    M3: "در ۳ ماه گذشته",
                    Y1: "در ۱ سال گذشته",
                    OLDER: "قدیمی‌تر",
                    NA: "مورد ندارد",
                },
                phq2: {
                    title: "حال شما در دو هفتهٔ گذشته",
                    i0: "علاقه یا لذت کم نسبت به کارها",
                    i1: "احساس افسردگی، ناامیدی یا دل‌گرفتگی",
                },
                phq9: {
                    title: "ادامهٔ بررسی حال شما",
                    i0: "علاقه یا لذت کم نسبت به کارها",
                    i1: "احساس افسردگی، ناامیدی یا دل‌گرفتگی",
                    i2: "مشکل در به خواب رفتن، در خواب ماندن، یا زیاد خوابیدن",
                    i3: "احساس خستگی یا کم‌انرژی بودن",
                    i4: "کم‌اشتهایی یا پرخوری",
                    i5: "احساس بد نسبت به خود — یا این‌که شکست خورده‌ای یا باعث ناامیدی خود یا خانواده‌ات شده‌ای",
                    i6: "مشکل در تمرکز روی چیزهایی مثل خواندن یا تماشای برنامه",
                    i7: "حرکت یا صحبت آن‌قدر آهسته که دیگران متوجه شوند — یا برعکس بی‌قراری و ناآرامی بیشتر از حد معمول",
                    i8: "فکرهایی دربارهٔ مردن، یا آسیب زدن به خود",
                },
                gad2: {
                    title: "نگرانی در دو هفتهٔ گذشته",
                    i0: "احساس عصبی، مضطرب یا روی اعصاب بودن",
                    i1: "نمی‌توانم نگرانی را متوقف یا کنترل کنم",
                },
                gad7: {
                    title: "ادامهٔ بررسی نگرانی",
                    i0: "احساس عصبی، مضطرب یا روی اعصاب بودن",
                    i1: "نمی‌توانم نگرانی را متوقف یا کنترل کنم",
                    i2: "نگرانی زیاد دربارهٔ چیزهای مختلف",
                    i3: "مشکل در آرام شدن",
                    i4: "بی‌قراری به‌قدری که سخت بتوان آرام نشست",
                    i5: "زودرنجی یا تحریک‌پذیری",
                    i6: "احساس ترس انگار اتفاق بدی رخ می‌دهد",
                },
                cssrs: {
                    title: "چند سؤال دربارهٔ ایمنی",
                    i0: "در ماه گذشته، آیا آرزو کرده‌اید که مرده بودید، یا کاش می‌خوابیدید و دیگر بیدار نمی‌شدید؟",
                    i1: "در ماه گذشته، آیا واقعاً به این فکر کرده‌اید که به زندگی خودتان پایان بدهید؟",
                    i2: "آیا به این فکر کرده‌اید که چطور ممکن است این کار را انجام بدهید؟",
                    i3: "آیا این فکرها را داشته‌اید و تا حدی قصد عمل کردن به آن‌ها را هم داشته‌اید؟",
                    i4: "آیا جزئیات را مشخص کرده یا برنامه‌ریزی را شروع کرده‌اید و قصد اجرای آن را داشته‌اید؟",
                    i5: "آیا تا به حال کاری برای پایان دادن به زندگی‌تان انجام داده‌اید، شروع کرده‌اید، یا برایش آماده شده‌اید؟",
                    i6: "آخرین بار چه زمانی بوده است؟",
                },
            },
            participantLegend: "چه کسی یا چه کسانی قرار است در جلسه‌ها شرکت کنند؟",
            participantHelp: "گزینه‌ای را انتخاب کن که به شرایط تو نزدیک‌تر است.",
            participantRequired: "این مورد را کامل کن.",
            participantHelpAria: "توضیح بیشتر دربارهٔ {name}",
            participantHints: {
                SELF: "جلسهٔ فردی برای خودم",
                COUPLE: "من و شریک زندگی‌ام",
                CHILD: "کودک زیر ۱۲ سال با همراهی بزرگسال",
                TEEN: "نوجوان ۱۲ تا ۱۷ سال",
                PARENT: "دربارهٔ فرزند",
                FAMILY: "بیش از دو نفر از اعضای خانواده",
            },
            participantHelpText: {
                SELF: "این گزینه برای وقتی است که فقط خودت در جلسه‌ها شرکت می‌کنی. مسیر فردی با تمرکز روی نیازها و هدف‌های تو تنظیم می‌شود.",
                COUPLE: "وقتی تو و شریک زندگی‌ات با هم در جلسه‌ها حاضر می‌شوید. هماهنگی زمان و رضایت هر دو نفر در ادامهٔ مسیر اهمیت دارد.",
                CHILD: "برای کودک زیر ۱۲ سال. معمولاً یک بزرگسال همراه در کنار کودک است و سن ثبت‌شده مربوط به خود کودک است.",
                TEEN: "برای نوجوان ۱۲ تا ۱۷ سال. پرسش‌ها و مسیر بعدی با این گروه سنی هماهنگ می‌شود.",
                PARENT: "وقتی والد بدون حضور فرزند، دربارهٔ موضوع مرتبط با فرزند مشاوره می‌گیرد. سن مربوط به فرزند ثبت می‌شود، نه والد.",
                FAMILY: "وقتی بیش از دو نفر از اعضای خانواده در جلسه‌ها شرکت می‌کنند. در ادامه ترکیب گروه سنی پرسیده می‌شود.",
            },
            continue: "ادامه",
            ageSelf: "سن مرتبط را بنویس",
            ageRelated: "سن مرتبط را بنویس",
            ageChild: "سن فرزند را بنویس",
            ageCoupleLegend: "سن هر دو نفر را بنویس",
            ageSelfLabel: "سن خودت",
            agePartnerLabel: "سن شریک زندگی",
            agePlaceholder: "مثلاً ۲۸",
            agePartnerPlaceholder: "مثلاً ۳۰",
            ageRequired: "سن را درست بنویس.",
            ageRange: "سن باید بین ۱ تا ۱۲۰ باشد.",
            ageTeenRange: "سن نوجوان باید بین ۱۲ تا ۱۷ باشد.",
            ageChildRange: "سن کودک باید ۱۱ یا کمتر باشد.",
            formIncomplete: "چند مورد را کامل کن تا بتوانیم ادامه بدهیم.",
            trustPath: "فقط برای ادامهٔ مسیر همین حساب استفاده می‌شود.",
            topicsTitle: "موضوع‌های نیاز به کمک",
            topicsLegend: "کدام موضوع‌ها برای کمک گرفتن مهم‌اند؟",
            topicsHelp: "دسته‌ها را باز کن و از یک تا {max} موضوعی را که برایشان کمک می‌خواهی انتخاب کن. اگر مطمئن نیستی، به‌جای موضوع‌ها گزینهٔ «مطمئن نیستم» را بزن.",
            topicsCount: "{count} از ۶ انتخاب",
            topicsUnsurePath: "مسیر «مطمئن نیستم»",
            topicsUnsureHint: "اگر برچسب‌ها جور نیستند، چند سؤال راهنما می‌آید.",
            topicsMin: "حداقل یک موضوع یا «مطمئن نیستم» را انتخاب کن.",
            topicsMax: "حداکثر شش موضوع انتخاب کن.",
            saving: "در حال ذخیره…",
            priorityTitle: "اولویت اصلی",
            priorityIntro: "اگر چند موضوع انتخاب شده، مشخص می‌کند کدام تغییر بیشترین اثر را دارد یا اینکه موضوع‌ها به هم وابسته‌اند.",
            priorityLegend: "اگر فقط یکی از این موضوع‌ها در چند هفتهٔ آینده بهتر شود، بهترشدن کدام‌یک بیشترین فرق را در زندگی‌ات ایجاد می‌کند؟",
            linkedLegend: "این موضوع‌ها معمولاً هم‌زمان پررنگ می‌شوند یا جدا از هم؟",
            linked: {
                together: "معمولاً هم‌زمان",
                separate: "جدا از هم",
                unsure: "نمی‌دانم",
            },
            fieldRequired: "این مورد را کامل کن.",
            priorityConnected: "نمی‌دانم؛ با هم مرتبط‌اند",
            priorityRequired: "یک نقطه شروع انتخاب کن.",
            severityTitle: "شدت اثر روی زندگی",
            severityIntro: "ثبت شدت اثر مسئله اصلی روی زندگی روزمره در چهار سطح از کم تا خیلی زیاد.",
            intensityLegend: "برای «{topic}»: اگر صفر یعنی تقریباً اثری ندارد و ده یعنی تحملش بسیار سخت است، شدت آن را همین الان چند می‌دانی؟",
            intensityLegendGeneric: "اگر صفر یعنی تقریباً اثری ندارد و ده یعنی تحملش بسیار سخت است، شدت آن را همین الان چند می‌دانی؟",
            intensityScaleAria: "شدت از ۰ تا ۱۰",
            frequencyLegend: "در دو هفتهٔ گذشته، این موضوع چند وقت یک‌بار حضور داشته؟",
            frequency: {
                NEVER: "اصلاً",
                FEW_DAYS: "چند روز",
                MORE_THAN_HALF: "بیش از نیمی از روزها",
                ALMOST_DAILY: "تقریباً هر روز",
            },
            functionLegend: "انجام کارهای روزمره، مراقبت از خود، کار/تحصیل یا ارتباط را چقدر سخت کرده؟",
            functionDifficulty: {
                NONE: "تقریباً هیچ",
                LOW: "کم",
                MEDIUM: "متوسط",
                HIGH: "زیاد",
            },
            recentTrendLegend: "در چند هفتهٔ اخیر، بیشتر چه روندی داشته؟",
            recentTrend: {
                BETTER: "بهتر شده",
                SAME: "تقریباً ثابت",
                WORSE: "بدتر شده",
                VOLATILE: "نوسان زیاد",
            },
            severityFocusConnected: "موضوع‌هایی که انتخاب کردی",
            severityFocus: "دربارهٔ {focus}",
            severityLegend: "در دو هفته گذشته، چقدر روی زندگی روزمره‌ات اثر گذاشته؟",
            severityRequired: "یک گزینه را انتخاب کن.",
            severity: {
                LOW: "کم؛ بیشتر کارها را انجام می‌دهم",
                MEDIUM: "متوسط؛ بعضی بخش‌ها سخت‌تر شده",
                HIGH: "زیاد؛ چند بخش زندگی‌ام مختل شده",
                VERY_HIGH: "خیلی زیاد؛ انجام کارهای معمول برایم سخت است",
            },
            complexityTitle: "مدت، دامنه اثر و روند",
            complexityIntro: "ثبت مدت مسئله، تعداد حوزه‌های درگیر زندگی و اینکه وضعیت بهتر، ثابت یا بدتر شده است.",
            historyDurationLegend: "چند وقت است که این موضوع با تو/شماست؟",
            historyDuration: {
                LT_1M: "کمتر از ۱ ماه",
                M1_3: "۱ تا ۳ ماه",
                M3_12: "۳ تا ۱۲ ماه",
                GT_1Y: "بیش از ۱ سال",
                CHILDHOOD: "از کودکی/نوجوانی",
            },
            priorHelpLegend: "برای این موضوع قبلاً کمک تخصصی گرفته‌ای؟",
            priorHelp: {
                none: "هنوز نه",
                therapy: "روان‌درمانی/مشاوره",
                psych: "روان‌پزشکی",
                other: "کمک دیگر",
            },
            priorOutcomeLegend: "اگر قبلاً درمان یا مشاوره داشته‌ای، نتیجه‌اش به تجربهٔ خودت کدام بوده؟",
            priorOutcome: {
                helped: "کمک کرده",
                mixed: "مختلط",
                not: "کمک نکرده",
                na: "مورد ندارد",
            },
            currentCareLegend: "الان برای این موضوع با روان‌درمانگر، روان‌پزشک یا پزشک دیگری در ارتباطی؟",
            currentCare: {
                none: "خیر",
                therapist: "روان‌درمانگر",
                psychiatrist: "روان‌پزشک",
                doctor: "پزشک دیگر",
            },
            needsCoordinationLegend: "آیا برای شروع درمان جدید لازم است درمانگر با متخصص دیگری هماهنگ باشد؟",
            needsCoordination: {
                yes: "بله",
                no: "خیر",
                unsure: "هنوز مشخص نیست",
            },
            involvedDomainsLegend: "آیا الان چند بخش زندگی یا چند نفر بیشتر از حد معمول این نوع جلسه، فعال درگیر این مسئله‌اند؟",
            involvedDomains: {
                work: "کار/تحصیل",
                home: "خانه",
                health: "سلامت",
                others: "افراد دیگر",
                none: "خیر / معمول است",
            },
            goalAlignmentLegend: "افرادی که قرار است در جلسه باشند، دربارهٔ هدف اصلی درمان چقدر هم‌نظرند؟",
            goalAlignment: {
                aligned: "هم‌نظر",
                partial: "تا حدی",
                not: "اختلاف زیاد",
                self_only: "فقط خودم",
            },
            caregiverAvailableLegend: "الان بزرگسال قابل‌اعتمادی هست که بتواند برنامهٔ درمان کودک/نوجوان را پیگیری کند؟",
            caregiverAvailable: {
                yes: "بله",
                no: "خیر",
                na: "مورد ندارد",
            },
            durationLegend: "این وضعیت از چه زمانی همراهت بوده؟",
            impactLegend: "روی چند بخش زندگی‌ات اثر گذاشته؟",
            trendLegend: "در یک ماه اخیر چه روندی داشته؟",
            complexityRequired: "برای هر سؤال یک گزینه انتخاب کن.",
            duration: {
                WEEKS: "چند هفته",
                MONTHS_1_6: "یک تا شش ماه",
                MONTHS_6_12: "شش ماه تا یک سال",
                YEARS: "بیشتر از یک سال",
            },
            impact: {
                ONE: "فقط یک بخش",
                SEVERAL: "چند بخش؛ مثل کار، خواب یا رابطه‌ها",
                MOST: "تقریباً همه بخش‌های مهم",
            },
            trend: {
                BETTER: "بهتر شده",
                SAME: "تقریباً ثابت مانده",
                WORSE: "سخت‌تر شده",
            },
            safetyTitle: "غربال ایمنی",
            safetyIntro: "پاسخ‌ها محرمانه‌اند و وارد پیشنهاد درمانگر یا تحلیل عمومی نمی‌شوند.",
            safetySubmit: "ثبت پاسخ‌های ایمنی",
            safetyRedirectCrisis: "در حال هدایت به راهنمای ایمنی…",
            safetyContinueOk: "در حال ادامه…",
            yesNo: {
                yes: "بله",
                no: "خیر",
            },
            safetyScreen: {
                wishDeadLegend: "در ماه گذشته، آیا آرزو کرده‌اید که مرده بودید، یا کاش می‌خوابیدید و دیگر بیدار نمی‌شدید؟",
                ideationLegend: "در ماه گذشته، آیا واقعاً به این فکر کرده‌اید که به زندگی خودتان پایان بدهید؟",
                methodLegend: "آیا به این فکر کرده‌اید که چطور ممکن است این کار را انجام بدهید؟",
                intentLegend: "آیا این فکرها را داشته‌اید و تا حدی قصد عمل‌کردن به آن‌ها را هم داشته‌اید؟",
                planLegend: "آیا جزئیات را مشخص کرده یا برنامه‌ریزی را شروع کرده‌اید و قصد اجرای آن را داشته‌اید؟",
                behaviorLegend: "آیا تا به حال کاری برای پایان‌دادن به زندگی‌تان انجام داده‌اید، شروع کرده‌اید، یا برایش آماده شده‌اید؟",
                lastTimeLegend: "آخرین بار چه زمانی بوده است؟",
                acuteStatesLegend: "آیا یکی از این وضعیت‌ها الان وجود دارد؟",
                domesticThreatLegend: "آیا در رابطه یا خانه، کسی تو را می‌ترساند، کنترل می‌کند، تهدید می‌کند، مجبورت می‌کند یا به تو آسیب می‌زند؟",
            },
            safetyLastTime: {
                '3m': "در ۳ ماه گذشته",
                year: "در ۱ سال گذشته",
                older: "قدیمی‌تر",
                na: "مورد ندارد",
            },
            safetyAcute: {
                none: "خیر / هیچ‌کدام",
                mania: "بی‌خوابی شدید و انرژی غیرعادی",
                psychosis: "تجربهٔ غیرمعمول شدید",
                intox: "مسمومیت/مصرف فعال خطرناک",
                medical: "ناپایداری پزشکی/غذایی",
            },
            safetyDomestic: {
                no: "خیر",
                yes: "بله",
                unsure: "مطمئن نیستم",
            },
            safetyLegend: "آیا الان هرکدام از این وضعیت‌ها وجود دارد؟",
            safetyHelp: "می‌توانی چند گزینه را انتخاب کنی. پاسخ تو محرمانه است و برای مراقبت ایمن استفاده می‌شود.",
            safetyMin: "حداقل یک گزینه را انتخاب کن.",
            safety: {
                SELF_HARM: "فکر آسیب‌زدن به خود یا پایان‌دادن به زندگی",
                HARM_OTHER: "فکر آسیب‌زدن به فرد دیگری",
                VIOLENCE: "خشونت، تهدید یا احساس ناامنی در خانه",
                PSYCHOSIS: "دیدن یا شنیدن چیزهایی که دیگران تجربه نمی‌کنند",
                MANIA: "چند شب بی‌خوابی همراه با انرژی یا رفتارهای غیرمعمول",
                MEDICAL: "وضعیت جسمی یا مصرفی که ممکن است نیاز به کمک فوری داشته باشد",
                NONE: "هیچ‌کدام از این وضعیت‌ها وجود ندارد",
            },
            crisisTitle: "پاسخ ایمنی",
            crisisSafetyTitle: "الان ایمنی تو از هر چیزی مهم‌تر است",
            crisisIntro: "مسیر پیشنهاد معمول درمانگر در این وضعیت ادامه پیدا نمی‌کند. از منابع تأییدشده استفاده کن.",
            crisisHeading: "بهتر است همین حالا تنها نمانی.",
            crisisBody: "پاسخت نشان می‌دهد ممکن است به کمک فوری‌تری نیاز داشته باشی. رو‌برو جای خدمات اورژانسی نیست و در این مرحله درمانگر پیشنهاد نمی‌دهد.",
            crisisCall: "تماس با ۱۲۳",
            crisisEdit: "اصلاح پاسخ",
            crisisSeeHelp: "دیدن راه‌های کمک فوری",
            crisisContactCall: "تماس",
            crisisContact: {
                social: "اورژانس اجتماعی ۱۲۳",
                addiction: "خط ملی اعتیاد / بحران ۱۴۸۰",
                emergency: "اورژانس ۱۱۵",
            },
            emergencyAria: "شماره‌های کمک فوری",
            emergencyLead: "این مسیر برای شرایط فوری یا اورژانسی نیست.",
            emergencyIfIran: "اگر در ایران هستی و خطر فوری وجود دارد:",
            emergency: {
                social: "اورژانس اجتماعی",
                health: "معاونت بهداشت",
                rahava: "خط بحران ره‌آوا",
                police: "پلیس",
            },
            availabilityTitle: "روز، زمان و ترجیحات",
            daysLegend: "چه روزهایی معمولاً برای جلسه مناسب‌ترند؟",
            timesLegend: "چه زمان‌هایی برایت مناسب‌ترند؟",
            slotsLegend: "در طول هفته چه روزها و بازه‌هایی معمولاً می‌توانی جلسه داشته باشی؟",
            flexibilityLegend: "اگر در زمان ایده‌آل گزینهٔ مناسب نبود، چقدر می‌توانی زمان جلسه را جابه‌جا کنی؟",
            flexibility: {
                low: "کم",
                mid: "متوسط",
                high: "زیاد",
            },
            genderLegend: "آیا جنسیت درمانگر برای شروع و ادامهٔ درمان ضروری است؟",
            genderPref: {
                any: "فرقی ندارد",
                woman: "ضروری: زن",
                man: "ضروری: مرد",
            },
            livedExperienceLegend: "آیا لازم است درمانگر تجربه یا شناخت مشخصی از یکی از زمینه‌های زندگی‌ات داشته باشد؟",
            livedExperience: {
                none: "خیر",
                lgbt: "هویت و گرایش",
                faith: "زمینهٔ فرهنگی/دینی",
                chronic: "بیماری مزمن",
                migration: "مهاجرت",
            },
            livedExperienceNeedLegend: "این شناخت برایت چقدر ضروری است؟",
            livedExperienceNeed: {
                soft: "ترجیح نرم",
                hard: "ضروری",
                na: "مورد ندارد",
            },
            dayShort: {
                SAT: "شنبه",
                SUN: "یکشنبه",
                MON: "دوشنبه",
                TUE: "سه‌شنبه",
                WED: "چهارشنبه",
                THU: "پنجشنبه",
                FRI: "جمعه",
            },
            slot: {
                MORNING: "صبح",
                NOON: "ظهر",
                AFTERNOON: "عصر",
                EVENING: "شب",
            },
            availabilityRequired: "حداقل یک روز، یک زمان، و ترجیح درمانگر را انتخاب کن.",
            reviewAnswers: "مرور پاسخ‌ها",
            day: {
                SAT: "شنبه‌ها",
                SUN: "یکشنبه‌ها",
                MON: "دوشنبه‌ها",
                TUE: "سه‌شنبه‌ها",
                WED: "چهارشنبه‌ها",
                THU: "پنجشنبه‌ها",
                FRI: "جمعه‌ها",
            },
            timeOfDay: {
                MORNING: "صبح",
                NOON: "ظهر",
                AFTERNOON: "عصر",
                EVENING: "شب",
            },
            gender: {
                FEMALE: "درمانگر خانم",
                MALE: "درمانگر آقا",
                NO_PREFERENCE: "تفاوتی ندارد",
            },
            path: {
                SELF: "مسیر فردی",
                COUPLE: "مسیر زوج",
                CHILD: "مسیر کودک",
                TEEN: "مسیر نوجوان",
                PARENT: "مسیر مشاورهٔ والد",
                TOGETHER: "مسیر نوجوان",
                FAMILY: "مسیر خانواده",
            },
            reviewTitle: "مرور پاسخ‌ها",
            reviewIntro: "پاسخ‌هایت را مرور کن؛ هر بخش را می‌توانی تغییر بدهی.",
            reviewPath: "مسیر",
            reviewTopics: "موضوع‌ها",
            reviewStart: "نقطه شروع",
            reviewWhen: "زمان ترجیحی",
            reviewGender: "جنسیت درمانگر",
            reviewEdit: "ویرایش",
            reviewConnected: "موضوع‌ها به هم مرتبط‌اند",
            reviewGroupPath: "شرکت‌کنندگان و مسیر",
            reviewGroupTopics: "موضوع‌ها و اولویت",
            reviewGroupSeverity: "شدت و پیچیدگی",
            reviewGroupSafety: "ایمنی",
            reviewGroupSchedule: "زمان و ترجیحات",
            reviewAgeSelf: "سن {age}",
            reviewAgeChild: "سن {age}",
            reviewAgePartner: "سن شریک {age}",
            reviewPriorityValue: "اولویت: {topic}",
            reviewIntensityValue: "شدت {value}",
            reviewTrendValue: "روند {value}",
            reviewPriorHelpValue: "سابقه کمک: {value}",
            reviewGenderValue: "جنسیت: {value}",
            reviewSafetyClear: "غربال تکمیل شد · بدون نمایش پاسخ خام",
            reviewSafetyFlagged: "غربال تکمیل شد · مسیر ایمنی باز شد",
            reviewEmpty: "—",
            reviewPrivacyNote: "پاسخ خام ایمنی و نمرهٔ بالینی در این مرور نشان داده نمی‌شود.",
            listJoin: " · ",
            privacyTitle: "چه چیزی به درمانگر نشان داده می‌شود؟",
            privacyBody: "فقط اطلاعات لازم برای شروع درمان و مراقبت ایمن. پاسخ‌های حساس ایمنی داخل کارت پیشنهاد درمانگر نمایش داده نمی‌شوند.",
            findMatches: "دیدن پیشنهادها",
            matchingTitle: "داریم پیشنهادی های تورو آماده میکنیم",
            matchingIntro: "در قدم بعد، درمانگرها و هزینه هر جلسه رو ببین و انتخاب کن",
            matchingRetry: "تلاش مجدد",
            matchingStepPath: "مسیر درست",
            matchingStepSkill: "صلاحیت و ظرفیت",
            matchingStepTimes: "زمان‌های موجود",
            recommendationNotActive: "این پیشنهاد دیگر فعال نیست. برای انتخاب درمانگر دیگر دوباره مچ کنید.",
            slotOrQuoteStale: "این زمان یا قیمت دیگر در دسترس نیست. لطفاً یک زمان زنده دیگر انتخاب کنید.",
            rematchAction: "مچ دوباره",
            matchesTitle: "پیشنهادهای درمانگر",
            matchesIntro: "می‌توانی دلیل هر پیشنهاد، هزینه و زمان‌های واقعی را قبل از انتخاب ببینی.",
            matchesUnderstood: "درمانگرهای پیشنهادی از نظر صلاحیت، ظرفیت و زمان با پاسخ‌های تو همخوان‌اند.",
            recommendBadge: "پیشنهاد اول رو‌برو",
            whyMatch: "این پیشنهاد بر چه اساسی است؟",
            nearestSlot: "نزدیک‌ترین زمان",
            sessionPrice: "هزینه هر جلسه",
            toman: "تومان",
            selectTherapist: "انتخاب زمان و رزرو",
            selectTimeAndBook: "انتخاب زمان و رزرو",
            matchReflect: "بر اساس پاسخ‌هایت، این موضوع‌ها را برای پیشنهاد در نظر گرفتیم:",
            whyThisSuggestion: "چرا این پیشنهاد؟",
            verifiedTherapist: "تراپیست تایید شده",
            sessionsHeldBadge: "+{count} جلسه موفق برگزار شده",
            sessionsHeldBadgeWords: {
                t1: "+هشتاد و پنج جلسه موفق برگزار شده",
                t2: "+صد و ده جلسه موفق برگزار شده",
                t3: "+صد و چهل جلسه موفق برگزار شده",
            },
            sessionOnlineHour: "جلسه آنلاین یک ساعته",
            priceNote: "تفاوت قیمت رتبهٔ کیفیت درمان نیست؛ به سطح قرارداد و مدت جلسه مربوط می‌شود.",
            sessionsProofLabel: "جلسه موفق برگزار شده",
            sessionsProofAria: "سابقه جلسات",
            approachLabel: "رویکرد: {approach}",
            bookNearestWith: "رزرو نزدیک‌ترین زمان با {name}",
            viewAllSlots: "مشاهده همه زمان‌های آزاد",
            sessionFactsAria: "جزئیات جلسه",
            capabilityAria: "حوزه‌های صلاحیت و رویکرد",
            matchExpiredTitle: "پیشنهادها نیاز به تازه‌سازی دارند",
            matchExpiredBody: "اعتبار پیشنهادها به پایان رسیده است. انتخاب‌های آنبوردینگ محفوظ می‌مانند و می‌توانی پیشنهادهای تازه بگیری.",
            matchInvalidTitle: "پیشنهاد قبلی دیگر قابل رزرو نیست",
            matchInvalidBody: "این پیشنهاد دیگر برای رزرو در دسترس نیست.",
            matchRefreshAction: "دریافت پیشنهادهای تازه",
            matchGapTitle: "فعلاً گزینهٔ مناسب پیدا نشد",
            matchGapBody: "الان درمانگر واجد شرایط با زمان آزاد پیدا نشد. تکرار همین جستجو ظرفیت تازه‌ای نمی‌سازد.",
            matchGapRequestAction: "ثبت درخواست پیگیری برای ثبت جلسه",
            matchGapBackAction: "بازگشت به غربالگری",
            matchGapRequestDone: "درخواست پیگیری ثبت شد.",
            matchRetryableTitle: "مشکل موقت در پیشنهاد",
            matchRetryableBody: "یک وابستگی موقتاً قطع شد. می‌توانی دوباره پیشنهاد را امتحان کنی.",
            matchRetryableAction: "تلاش دوباره",
            matchNoslotTitle: "زمان مناسبی پیدا نشد",
            matchNoslotBody: "می‌توانی زمان‌های دیگر را بررسی کنی یا ترجیحات را اصلاح کنی. این به معنای رد صلاحیت نیست.",
            matchNoslotOtherTimesAction: "بررسی زمان‌های دیگر",
            matchNoslotEditPrefsAction: "اصلاح ترجیحات زمانی",
            rematchOfferTitle: "می‌خواهی گزینهٔ دیگری بررسی کنیم؟",
            rematchCurrentTherapist: "درمانگر فعلی: {name}",
            rematchOfferReason: "در صورت تمایل می‌توانی گزینه‌های جایگزین را بررسی کنی. پیشنهاد به‌تنهایی درمان یا رزرو فعلی را عوض نمی‌کند.",
            rematchBulletReject: "حق رد پیشنهاد محفوظ است.",
            rematchBulletUnchanged: "تا تأیید نهایی، پرونده فعلی بدون تغییر می‌ماند.",
            rematchAcceptAction: "ثبت تغییر تراپیست",
            rematchDeclineAction: "الان نه",
            rematchDecideTitle: "تصمیم دربارهٔ تغییر درمانگر",
            rematchDecideQuestion: "کدام مسیر را می‌خواهی؟",
            rematchSeeOthers: "می‌خواهم گزینهٔ دیگری ببینم",
            rematchStayCurrent: "با درمان فعلی ادامه می‌دهم",
            rematchStayHint: "پیشنهاد تغییر را رد می‌کنم",
            rematchRunTitle: "در حال بررسی گزینه‌های جدید",
            rematchReviewTitle: "درخواست تغییر درمانگر",
            rematchManualReviewPending: "درخواست تغییر درمانگرت برای بررسی ثبت شده است. درمان فعلی فعال می‌ماند. پیش از رزرو جایگزین، هماهنگی رزرو و وضعیت اعتبار باید بررسی شود. زمان مشخصی برای پاسخ وعده داده نشده است.",
            rematchReviewRefresh: "به‌روزرسانی وضعیت درخواست",
            rematchSupportAction: "کمک از پشتیبانی",
            rematchBookingInProgress: "رزرو جایگزین از قبل در حال انجام است. از صفحهٔ جلسه‌ها ادامه بده یا پیش از شروع رزرو دیگر از پشتیبانی کمک بگیر.",
            rematchStatusUnavailable: "دریافت وضعیت تغییر درمانگر ممکن نشد. دوباره بررسی کن یا از پشتیبانی کمک بگیر. درمان فعلی تغییر نکرده است.",
            rematchNoneActive: "پیشنهاد فعالی برای تغییر درمانگر وجود ندارد. می‌توانی درمان فعلی را ادامه بدهی یا از پشتیبانی کمک بگیری.",
            rematchDecisionFailed: "تأیید تصمیم تو ممکن نشد. دوباره تلاش کن؛ درمان فعلی تغییر نکرده است.",
            rematchSafetyBlocked: "غربال اخیر اجازهٔ تطبیق معمول را نداده است. راهنمای مسیر غربال را دنبال کن یا دربارهٔ حسابت از پشتیبانی کمک بگیر. درمان فعلی محفوظ است.",
            rematchRunStatus: "در حال بررسی گزینه‌های جایگزین…",
            rematchCheckKeep: "درمان فعلی تا رزرو جدید محفوظ است",
            rematchCheckEligible: "فقط گزینه‌های واجد شرایط و قابل رزرو",
            rematchCheckRetry: "در صورت خطا می‌توانی دوباره تلاش کنی",
            rematchCheckStatusAction: "بررسی وضعیت پیشنهادهای جدید",
            rematchExcluded: "الان برای این حساب پیشنهاد هم‌خوانی در دسترس نیست.",
            rematchRunFail: "بروزرسانی پیشنهادها ممکن نشد. دوباره امتحان کن.",
            rematchRunDone: "گزینه‌های جدید آماده‌اند.",
            rematchSafetyReviewPending: "بررسی ایمنی لازم هنوز در حال انجام است. درمان فعلی محفوظ می‌ماند و تا پایان این بررسی، تطبیق مجدد اجرا نمی‌شود.",
            rematchValueReviewPending: "تغییر درمانگرت به بررسی پرداخت یا اعتبار نیاز دارد. درمان قبلی به‌صورت خودکار بسته نمی‌شود. پیش از پرداخت یا رزرو دیگر، از پشتیبانی کمک بگیر.",
            rematchFailTitle: "این بار گزینهٔ جایگزین پیدا نشد",
            rematchFailBulletNoSafe: "این بار گزینهٔ جایگزین امنی ثبت نشد.",
            rematchFailBulletUnchanged: "درمان فعلی بدون تغییر می‌ماند.",
            rematchFailBulletRetryLater: "در صورت نیاز می‌توانی بعداً دوباره تلاش کنی.",
            rematchBackToCareAction: "بازگشت به درمان فعلی",
            rematchRetryAction: "تلاش دوباره",
            capability: {
                anxiety: "اضطراب",
                sleep: "خواب",
                work: "کار و فرسودگی",
                mood: "حال و خلق",
                trauma: "تروما",
                adult: "بزرگسال",
            },
            approach: {
                cbt: "شناختی‌رفتاری",
                integrative: "یکپارچه‌نگر",
                psychodynamic: "روان‌پویشی",
            },
            viewProfile: "مشاهده پروفایل",
            pricingTitle: "چرا قیمت جلسه‌ها متفاوت است؟",
            pricingBody: "تفاوت قیمت رتبهٔ کیفیت درمان نیست؛ به سطح قرارداد و مدت جلسه مربوط می‌شود.",
            matchesFootnote: "ترتیب نمایش، میزان همخوانی با پاسخ‌های همین مسیر است؛ نه رتبه‌بندی خوب یا بد بودن درمانگرها.",
            whyTitle: "مبنای پیشنهاد {name}",
            evidenceTitle: "نمای فنی",
            whyNote: "این متن از قطعه‌های تأییدشده بر اساس پاسخ کلاینت و شواهد واقعی همان درمانگر ساخته می‌شود؛ ادعای بدون داده وارد متن نمی‌شود.",
            close: "بستن",
            therapistSelected: "انتخاب شد. برای ادامه پروفایل را باز کن، یا در گام بعد زمان‌ها می‌آید.",
            profileTitle: "پروفایل درمانگر پیشنهادی",
            profileAboutTitle: "درباره شیوه کار",
            profileAboutBody: "جلسه‌های اول به روشن‌شدن مسئله، هدف مشترک و ساختن یک مسیر قابل‌فهم اختصاص پیدا می‌کند. روند درمان با بازخورد تو تنظیم می‌شود.",
            profileWhyTitle: "چرا برای مسیر تو پیشنهاد شده؟",
            profileCredentialsTitle: "اطلاعات حرفه‌ای",
            profileCredentialLicense: "پروانه و هویت حرفه‌ای تأییدشده",
            profileCredentialTopic: "صلاحیت موضوعی معتبر و نسخه‌دار",
            profileCredentialCapacity: "ظرفیت پذیرش پرونده با سطح فعلی",
            profilePerSession: "هر جلسه",
            profileViewSlots: "دیدن زمان‌ها",
            profileSlotsSoon: "زمان‌ها در گام بعد باز می‌شود. رزرو هنوز متصل نیست.",
            profileTagAdult: "بزرگسال",
            profileTagAnxiety: "اضطراب و فشار روزمره",
            profileTagFa: "فارسی",
            slotsTitle: "انتخاب زمان جلسه",
            slotsIntro: "یک زمان انتخاب کن. پیش از پرداخت، نگه‌داشتن موقت آن را تأیید می‌کنیم.",
            slotsDateLegend: "روز جلسه را انتخاب کن",
            slotsTimeLegend: "زمان‌های موجود",
            slotsTimezoneNote: "همه زمان‌ها بر اساس ساعت محلی تو نمایش داده می‌شوند.",
            slotsTzLabel: "منطقه زمانی",
            slotsTzValue: "تهران (UTC+۳:۳۰)",
            slotsMonthNext: "ماه بعد",
            slotsMonthPrev: "ماه قبل",
            slotsMonthOther: "فقط ماه نمونه",
            slotsMonthNavDemo: "در این پروتوتایپ فقط ماه نمونه نوبت آزاد دارد.",
            slotsMonthShort: "مرداد",
            slotsCalAria: "تقویم جلسه",
            slotsCalLegendAria: "راهنمای تقویم",
            slotsCalLegendAvail: "روزهای دارای نوبت آزاد",
            slotsCalLegendEmpty: "بدون نوبت",
            slotsCalLegendSelected: "روز انتخاب‌شده",
            slotsDow: "ش ی د س چ پ ج",
            slotsDayAvailable: "روز {day}، نوبت آزاد",
            slotsDayEmpty: "روز {day}، بدون نوبت",
            slotsPickDay: "یک روز",
            slotsTimesForDate: "زمان‌های آزاد {date}",
            slotsFilterAria: "فیلتر بازه روز",
            slotsFilter: {
                morning: "صبح",
                day: "ظهر - عصر",
                night: "شب",
            },
            slotsListAria: "زمان‌های آزاد",
            slotsEmptyPeriod: "در این بازه نوبت آزادی نیست.",
            slotsPolicyNote: "شرایط لغو یا تغییر زمان فقط وقتی نمایش داده می‌شود که سیاست معتبر فعلی در دسترس باشد.",
            slotsPriceSuffix: "برای هر جلسه",
            slotsContinue: "ادامه و رزرو",
            slotsBackProfile: "بازگشت به پروفایل",
            slotsSavedNote: "زمان ذخیره شد. پرداخت و نهایی‌سازی در گام بعد است — هنوز متصل نیست.",
            holdTitle: "این زمان موقتاً برای تو نگه داشته شده",
            holdHint: "مهلت نگه‌داشتن حدود ۱۰ دقیقه است",
            holdSummaryAria: "خلاصه نگه‌داشت",
            holdSummaryTime: "زمان",
            holdSummaryTz: "منطقه زمانی",
            holdSummaryAmount: "مبلغ",
            holdSummaryPayMethod: "روش پرداخت",
            holdPayMethodValue: "درگاه امن آنلاین",
            holdContinuePay: "ادامه برای پرداخت",
            holdChangeTime: "تغییر زمان",
            conflictTitle: "این زمان دیگر آزاد نیست",
            conflictBody: "زمانی که انتخاب کرده بودی دیگر قابل رزرو نیست. مبلغ و ترجیحاتت محفوظ است؛ می‌توانی از اسلات‌های تازه یکی را انتخاب کنی.",
            conflictSummaryAria: "خلاصه تعارض",
            conflictChosenTime: "زمان انتخابی",
            conflictStatusLabel: "وضعیت",
            conflictStatus: "دیگر آزاد نیست",
            conflictPickOther: "انتخاب زمان دیگر",
            holdExpTitle: "مهلت نگه‌داشتن زمان تمام شد",
            holdExpBody: "مهلت ۱۰ دقیقه‌ای نگه‌داشتن این زمان تمام شده است. برای ادامه، دوباره یک اسلات آزاد انتخاب کن.",
            holdExpSummaryAria: "خلاصه انقضای نگه‌داشت",
            holdExpPrevTime: "زمان قبلی",
            holdExpPayStatusLabel: "وضعیت پرداخت",
            holdExpPayStatus: "هنوز پرداخت نشده",
            holdExpPickOther: "انتخاب زمان دیگر",
            rescheduleTitle: "تغییر زمان جلسه",
            rescheduleSummaryAria: "خلاصه تغییر زمان",
            rescheduleCurrent: "زمان فعلی",
            rescheduleDeadline: "مهلت تغییر",
            rescheduleDeadlineValue: "سیاست ارائه نشده",
            rescheduleExtraPay: "پرداخت اضافه",
            rescheduleExtraPayValue: "تعریف نشده",
            rescheduleAltsLegend: "زمان های جایگزین",
            rescheduleAltsAria: "زمان‌های جایگزین",
            rescheduleConfirm: "ثبت زمان جدید",
            rescheduleDismiss: "انصراف",
            rescheduleSavedNote: "زمان جدید ذخیره شد.",
            rescheduleNoSlots: "الان زمان خالی نیست. بعداً دوباره امتحان کن.",
            rescheduleSlotsFail: "بارگذاری زمان‌های جایگزین ممکن نشد.",
            rescheduleSaveFail: "ذخیره زمان جدید ممکن نشد.",
            cancelSessionTitle: "مرور لغو جلسه",
            cancelSessionSummaryAria: "خلاصه لغو",
            cancelSessionWhen: "جلسه",
            cancelSessionRemaining: "زمان مانده",
            cancelSessionRemainingValue: "\u2014",
            cancelSessionCredit: "اثر بر اعتبار",
            cancelSessionCreditValue: "نتیجهٔ مالی تعریف نشده",
            cancelSessionNote: "لغو رزرو پرداخت‌نشده هیچ اثر اعتبار یا بازپرداختی ندارد. لغو دارای اثر مالی تا دریافت سیاست معتبر بسته می‌ماند.",
            cancelSessionPartialNote: "لغو جلسهٔ رزروشده از اینجا هنوز ممکن نیست. با پشتیبانی تماس بگیر، یا اگر رزرو پرداخت‌نشده داری همان را لغو کن.",
            cancelSessionConfirm: "لغو رزرو پرداخت‌نشده",
            cancelSessionKeep: "حفظ جلسه",
            cancelSessionSavedNote: "رزرو لغو شد.",
            cancelSessionSaveFail: "لغو این رزرو ممکن نشد.",
            bookingSpaceTherapist: "فضای درمانگر",
            bookingSpaceSupport: "فضای پشتیبانی",
            bkDismiss: "انصراف",
            bkStubSaved: "در این پروتوتایپ ذخیره شد (هنوز متصل نیست).",
            bkWeekDay: {
                sat: "شنبه",
                sun: "یکشنبه",
                mon: "دوشنبه",
                tue: "سه‌شنبه",
                wed: "چهارشنبه",
            },
            bkT02Title: "ویرایش برنامهٔ هفتگی",
            bkT02WeekAria: "دسترسی هفتگی",
            bkT02Off: "مرخص",
            bkT02Ranges: "{n} بازه",
            bkT02NoRange: "بدون بازه",
            bkT02Policy: "هم‌پوشانی بازه‌ها و جلسهٔ رزرو‌شدهٔ متأثر قبل از ذخیره بررسی می‌شود.",
            bkT02Save: "مرور و ذخیرهٔ برنامه",
            bkT03Title: "ثبت مرخصی",
            bkT03From: "از تاریخ",
            bkT03To: "تا تاریخ",
            bkT03FromDefault: "۲۲ مرداد ۱۴۰۴",
            bkT03ToDefault: "۲۵ مرداد ۱۴۰۴",
            bkT03Reason: "دلیل عملیاتی",
            bkT03ReasonDefault: "مرخصی از پیش برنامه‌ریزی‌شده",
            bkT03Affected: "رزرو متأثر",
            bkT03AffectedValue: "۲ جلسه · نیاز به اطلاع مشتری",
            bkT03Blocked: "اسلات‌های مسدود",
            bkT03BlockedValue: "۸ نوبت در بازه",
            bkT03Save: "ثبت مرخصی",
            bkT04Title: "لغو جلسه از طرف درمانگر",
            bkT04Client: "مراجع",
            bkT04ClientValue: "م. رضایی",
            bkT04Session: "جلسه",
            bkT04SessionValue: "۱۸ مرداد · دوشنبه ۱۸:۳۰",
            bkT04Credit: "اعتبار",
            bkT04CreditValue: "بازگشت کامل به مشتری",
            bkT04Save: "لغو و بازگرداندن اعتبار",
            bkTOnceTitle: "افزودن ساعت موردی",
            bkTOnceDate: "تاریخ",
            bkTOnceStart: "شروع",
            bkTOnceEnd: "پایان",
            bkTOnceDateDefault: "۲۱ مرداد ۱۴۰۴",
            bkTOnceStartDefault: "۱۴:۰۰",
            bkTOnceEndDefault: "۱۶:۰۰",
            bkTOncePolicy: "این بازه خارج از برنامهٔ هفتگی است و فقط برای همان روز اعمال می‌شود. هم‌پوشانی با بازه‌های موجود بررسی می‌گردد.",
            bkTOnceSave: "ثبت ساعت موردی",
            bkS01Title: "رزرو برای مشتری",
            bkS01Client: "مشتری",
            bkS01ClientValue: "ن. احمدی · تأیید هویت شده",
            bkS01Therapist: "درمانگر",
            bkS01Slot: "اسلات",
            bkS01SlotValue: "۱۹ مرداد · سه‌شنبه ۱۰:۰۰",
            bkS01Amount: "اعتبار / مبلغ",
            bkS01AmountValue: "۴۸۰٬۰۰۰ تومان · قابل پرداخت",
            bkS01Consent: "رضایت و دلیل نمایندگی",
            bkS01ConsentDefault: "رزرو به درخواست مشتری از کانال پشتیبانی با رضایت صریح",
            bkS01Policy: "همان قواعد صلاحیت، اسلات، اعتبار و پرداخت مسیر سلف‌سرویس اعمال می‌شود.",
            bkS01Save: "مرور رزرو مشتری",
            bkS01BackQueue: "بازگشت به صف",
            bkS02Title: "صف رزرو و زمان‌بندی",
            bkS02QueueAria: "صف پشتیبانی",
            bkS02Open: "باز کردن درخواست رزرو",
            bkS02Item: {
                a: {
                    name: "ن. احمدی",
                    badge: "بدون اسلات",
                    detail: "علت: ظرفیت آزاد نیست · مالک: پشتیبانی · مهلت: امروز ۱۸:۰۰ · اقدام: رزرو نمایندگی",
                },
                b: {
                    name: "م. کریمی",
                    badge: "تعارض زمان",
                    detail: "علت: اسلات همزمان گرفته شد · آخرین رخداد: ۱۲ دقیقه پیش · اقدام: پیشنهاد جایگزین",
                },
                c: {
                    name: "سارا محمدی",
                    badge: "نزدیک SLA",
                    detail: "علت: درخواست تغییر برنامه · مالک: پشتیبانی · اقدام: تغییر به نمایندگی",
                },
            },
            bkS03Title: "تغییر برنامه به نمایندگی درمانگر",
            bkS03Therapist: "درمانگر",
            bkS03Authority: "شاهد اختیار",
            bkS03AuthorityValue: "تیکت #۴۱۸۲ · تأیید کتبی",
            bkS03Before: "قبل",
            bkS03BeforeValue: "دوشنبه ۱۸:۰۰–۲۱:۰۰",
            bkS03After: "بعد",
            bkS03AfterValue: "دوشنبه ۱۷:۰۰–۲۰:۰۰",
            bkS03Reason: "دلیل تغییر",
            bkS03ReasonDefault: "درخواست درمانگر برای جابه‌جایی بازه عصر",
            bkS03Save: "مرور و ثبت تغییر",
            checkoutTitle: "رزروت را نهایی کن",
            checkoutSuccessTitle: "پرداخت موفق",
            checkoutHoldTitle: "زمان جلسه برایت نگه داشته شده",
            checkoutHoldExpired: "مهلت نگه‌داشت تمام شد. برگرد و دوباره زمان را انتخاب کن.",
            checkoutPayTitle: "پرداخت امن",
            checkoutPayIntro: "پس از پرداخت، رضایت‌نامه و چند سؤال کوتاه آمادگی جلسه اول را تکمیل می‌کنی.",
            checkoutPayCard: "پرداخت اینترنتی",
            checkoutPayAction: "پرداخت",
            checkoutPayActionAmount: "پرداخت {amount} {currency}",
            checkoutOrderTitle: "خلاصه رزرو",
            checkoutOrderDate: "تاریخ",
            checkoutOrderTime: "ساعت",
            checkoutOrderSession: "جلسه",
            checkoutOrderAmount: "مبلغ",
            checkoutSessionValue: "آنلاین · {minutes} دقیقه",
            checkoutSessionOnline: "آنلاین",
            checkoutProcessingTitle: "در حال تأیید پرداخت…",
            checkoutProcessingBody: "این صفحه را نبند.",
            checkoutBookedTitle: "جلسه‌ات رزرو شد",
            checkoutBookedSummary: "{date}، ساعت {time} با {name}",
            checkoutBookingCode: "کد رزرو",
            checkoutPrepare: "آماده‌شدن برای جلسه اول",
            checkoutDashboard: "رفتن به داشبورد",
            checkoutPrepareSoon: "رضایت‌نامه و آمادگی جلسه بعد می‌آید — هنوز متصل نیست.",
            checkoutMockNote: "این پرداخت آزمایشی است و پولی کسر نمی‌شود.",
            pay01Title: "مرور رزرو و روش پرداخت",
            pay01HoldHint: "مهلت نگه‌داشتن زمان",
            pay01SummaryAria: "خلاصه رزرو",
            pay01MethodLegend: "روش پرداخت",
            pay01MethodGateway: "درگاه آنلاین",
            pay01MethodGatewayHint: "پرداخت امن با کارت بانکی",
            pay01MethodPackage: "کسر از بسته",
            pay01MethodPackageHint: "در صورت موجودی کافی",
            pay01Continue: "ادامه برای پرداخت",
            pay02Title: "پرداخت در حال انجام",
            pay02Body: "در حال تأیید پرداخت از درگاه…",
            pay02SummaryAria: "خلاصه وضعیت پرداخت",
            pay02Ref: "مرجع رزرو",
            pay02BookingStatus: "وضعیت رزرو",
            pay02BookingActive: "وضعیت رزرو از سرور",
            pay02Check: "بررسی وضعیت پرداخت",
            pay03Title: "پرداخت موفق",
            pay03SummaryAria: "تأیید پرداخت",
            pay03SessionTime: "زمان جلسه",
            pay03AmountConfirmed: "مبلغ تأییدشده",
            pay03Receipt: "شماره رسید",
            pay03Note: "تأیید فقط وقتی نمایش داده می‌شود که وضعیت رزرو سمت سرور CONFIRMED باشد.",
            pay03Prepare: "آماده‌شدن برای جلسهٔ اول",
            pay03Calendar: "افزودن به تقویم",
            pay03CalendarSoon: "افزودن به تقویم هنوز متصل نیست.",
            payFailTitle: "پرداخت انجام نشد",
            payFailBody: "پرداخت تأیید نشد و جلسه هنوز رزرو قطعی نشده است. می‌توانی دوباره تلاش کنی تا مهلت نگه‌داشتن زمان تمام نشده.",
            payFailSummaryAria: "خلاصه پرداخت ناموفق",
            payFailConfirmStatus: "وضعیت تأیید",
            payFailConfirmValue: "وضعیت از سرور",
            payFailRetry: "تلاش دوباره برای پرداخت",
            payMismatchTitle: "پرداخت نیاز به بررسی دارد",
            payMismatchBody: "مبلغ تأییدشده با مبلغ مورد انتظار هم‌خوانی ندارد. تا روشن شدن وضعیت، رزرو در حالت بررسی می‌ماند.",
            payMismatchSummaryAria: "خلاصه ناهم‌خوانی",
            payMismatchExpected: "مبلغ مورد انتظار",
            payMismatchConfirmed: "مبلغ تأییدشده",
            payMismatchReceipt: "رسید",
            payMismatchFollow: "پیگیری پرداخت",
            payLateTitle: "پرداخت پس از پایان مهلت رسیده است",
            payLateBody: "پرداخت تأیید شده، اما بعد از پایان مهلت نگه‌داشتن زمان رسیده است؛ بنابراین رزرو هنوز قطعی نیست و مسیر اعتبار/بازپرداخت باز می‌شود.",
            payLateSummaryAria: "خلاصه پرداخت دیرهنگام",
            payLatePayment: "پرداخت",
            payLatePaymentValue: "تأییدشده",
            payLateBooking: "رزرو",
            payLateBookingValue: "قطعی‌نشده",
            payLateAction: "اقدام",
            payLateActionValue: "نتیجهٔ مالی حل‌نشده",
            payLateNewSlot: "انتخاب زمان تازه",
            payFollowSoon: "پشتیبانی به‌زودی پیگیری می‌کند.",
            payFailRetryPending: "لینک پرداخت برنگشت. دوباره امتحان کن یا بخش پرداخت‌ها را باز کن.",
            payFailRetryError: "شروع دوبارهٔ پرداخت ممکن نشد.",
            boundary01Title: "رضایت آگاهانه درمان",
            consentWithdrawalFailed: "لغو رضایت ثبت نشد. لطفاً دوباره تلاش کنید.",
            boundary01Reassure: "رزرو و پرداخت شما قطعی است. برای باز شدن دسترسی جلسه، رضایت‌نامه را بخوانید و تأیید کنید.",
            boundary01MetaTitle: "عنوان",
            boundary01MetaVersion: "نسخه",
            boundary01DocName: "رضایت آگاهانه درمان",
            boundary01Body1: "من می‌دانم جلسهٔ آنلاین مشاوره/روان‌درمانی در روبه‌رو برگزار می‌شود و محتوای جلسه محرمانه است، مگر در موارد الزام قانونی یا ایمنی.",
            boundary01Body2: "می‌دانم شرکت در جلسه داوطلبانه است و می‌توانم طبق سیاست لغو، زمان را تغییر دهم یا لغو کنم.",
            boundary01Body3: "می‌دانم روبه‌رو جایگزین اورژانس نیست و در بحران باید از کمک فوری محلی استفاده کنم.",
            boundary01Body4: "با ثبت پذیرش، نسخهٔ جاری این سند برای حساب من ثبت می‌شود.",
            boundary01Accept: "توضیح این نسخه از رضایت‌نامه و اطلاعیهٔ حریم خصوصی را خوانده‌ام، فرصت پرسیدن سؤال دارم و با دریافت خدمت آنلاین در چارچوب توضیح‌داده‌شده موافقم.",
            boundary01Confirm: "تأیید رضایت‌نامه",
            boundary01Decline: "عدم تایید",
            boundary02Title: "پرسش‌های آمادگی",
            boundary02Optional: "این بخش اختیاری است و روی تطبیق، رضایت یا مجوز ورود به جلسه اثر ندارد.",
            boundary02Retry: "تلاش دوباره",
            boundary02ReturnHome: "بازگشت به پرتال من",
            boundary02ScaleHint: "۰ = خیلی کم · ۱۰ = خیلی زیاد",
            boundary02: {
                importance: "از صفر تا ده، الان تغییر این وضعیت چقدر برایت مهم است؟",
                confidence: "از صفر تا ده، چقدر به توان خودت برای کار روی این موضوع اطمینان داری؟",
                expectancy: "از صفر تا ده، فکر می‌کنی روان‌درمانی در شرایط الان تو چقدر می‌تواند کمک‌کننده باشد؟",
                support: "کسی را در زندگی‌ات داری که وقتی واقعاً لازم داری، بتوانی رویش حساب کنی؟",
            },
            boundary02Support: {
                yes: "بله، معمولاً هست",
                sometimes: "گاهی، نه همیشه",
                no: "فعلاً نه",
                skip: "ترجیح می‌دهم نگویم",
            },
            boundary02Submit: "ثبت پاسخ‌ها",
            boundary02Later: "بعدا تکمیل میکنم",
            boundary03Title: "نامه اختیاری به خود آینده",
            boundary03Privacy: "این نامه فقط برای خودت ذخیره می‌شود و تنها در پنل کاربری خودت قابلیت نمایش دارد. پرکردن این نامه اختیاری است",
            boundary03Label: "برای خودِ آینده‌ات بنویس؛ چرا این مسیر را شروع کردی؟",
            boundary03Placeholder: "اگر دوست داری، چند خط اینجا بنویس…",
            boundary03Save: "ذخیره برای خودم",
            boundary03Skip: "رد کردن",
            letterVoiceTitle: "یک نامهٔ صوتی برای خودت",
            letterVoiceUnavailable: "نامهٔ صوتی فعلاً در دسترس نیست. می‌توانی نامه بنویسی یا بدون نامه ادامه بدهی.",
            letterVoiceOptional: "ثبت نامه اختیاری است و بدون آن هم می‌توانی وارد جلسه شوی.",
            letterVoiceBack: "بازگشت به نامه",
            letterVoicePrivacy: "ضبط فقط روی دستگاه تو می‌ماند تا ذخیره کنی. در این پیش‌نمایش هیچ فایلی ارسال نمی‌شود.",
            letterVoiceStart: "شروع ضبط",
            letterVoiceIdle: "برای شروع ضبط بزن",
            letterVoiceRecording: "در حال ضبط… دوباره بزن تا متوقف شود",
            letterVoicePlay: "بازشنوی",
            letterVoiceDiscard: "حذف برداشت",
            letterVoiceWrite: "نوشتن متنی",
            letterVoiceCaptured: "یک برداشت محلی در این پیش‌نمایش نگه داشته شد.",
            letterVoicePlayHint: "بازشنوی فقط محلی است.",
            letterVoiceEmpty: "هنوز برداشتی نیست.",
            letterVoiceDiscarded: "برداشت حذف شد.",
            readyLockTitle: "ورود هنوز باز نشده",
            readyLockBody: "جلسه قطعی شده است، اما ورود تا رفع هر مانع سمت سرور بسته می‌ماند. لینک ورود نمایش داده نمی‌شود.",
            readyLockSummaryAria: "خلاصه مانع ورود",
            readyLockSession: "جلسهٔ قطعی",
            readyLockSessionValue: "سه‌شنبه ۲۸ اسفند · ۱۷:۳۰",
            readyLockBlocker: "مانع فعلی",
            readyLockBlockerValue: "رضایت‌نامه تأیید نشده",
            readyLockAction: "اقدام مرتبط",
            readyLockActionValue: "تکمیل رضایت آگاهانه",
            readyLockCta: "تکمیل رضایت‌نامه",
            readyLockHome: "بازگشت به داشبورد",
            readyOkTitle: "برای ورود آماده‌ای",
            readyOkBody: "همهٔ پیش‌نیازها آماده است. می‌توانی در پنجرهٔ مجاز، دستگاهت را بررسی کنی و وارد جلسه شوی.",
            readyOkSummaryAria: "خلاصه آمادگی جلسه",
            readyOkSession: "جلسه",
            readyOkSessionValue: "جلسهٔ اول با درمانگر",
            readyOkDate: "تاریخ",
            readyOkDateValue: "سه‌شنبه ۲۸ اسفند",
            readyOkTime: "ساعت محلی",
            readyOkTimeValue: "۱۷:۳۰ · تهران",
            readyOkCta: "بررسی دستگاه و ورود",
            readyOkHome: "داشبورد",
            clientHomeTitle: "ادامهٔ مسیر درمان",
            clientHomeBody: "آمادگی جلسه‌ات را بررسی کن یا از خانهٔ پرتال ادامه بده.",
            clientHomeBodyMatched: "درمانگر برایت مشخص شده. می‌توانی به آمادگی بروی یا خانهٔ پرتال را باز کنی.",
            clientHomeDoctor: "درمانگر: {name}",
            clientHomeCta: "ورود به آمادگی",
            clientHomePortalCta: "باز کردن پرتال من",
            sessionCheckTitle: "بررسی دستگاه (پیش‌نمایش)",
            sessionCheckBody: "پیش‌نمایش بررسی دستگاه قبل از جلسه آنلاین — صفحهٔ کامل در بخش Online Session می‌آید.",
            sessionCheckBack: "بازگشت",
            onlineSession: {
                checkTitle: "بررسی صدا و تصویر",
                checkBody: "قبل از ورود، صدا و تصویر را بررسی کن. اگر دسترسی ندادی، راهنمای بازیابی در همین صفحه می‌ماند.",
                checkMic: "میکروفون",
                checkMicOk: "آماده",
                checkMicBad: "مسدود یا در دسترس نیست",
                checkCam: "دوربین",
                checkCamOk: "آماده",
                checkCamBad: "مسدود یا در دسترس نیست",
                checkPending: "در حال بررسی…",
                checkNetBad: "آفلاین",
                noSession: "جلسه‌ای برای پیوستن پیدا نشد.",
                joinReady: "لینک ورود آماده است",
                joinPending: "منتظر باز شدن",
                waitEnterRoom: "ورود به جلسه",
                waitReadyStatus: "جلسه‌ات آمادهٔ ورود است.",
                waitNotReady: "هنوز باز نشده — منتظر بمان یا کمی بعد دوباره امتحان کن.",
                waitStartNow: "الان",
                waitStartDays: "{days} روز و {hours} ساعت و {minutes} دقیقه",
                openJoinLink: "باز کردن لینک جلسه",
                roomConnLinked: "لینک اتاق خارجی آماده است",
                roomOpenedStatus: "لینک جلسه در تب جدید باز شد.",
                noJoinLink: "هنوز لینک اسکایپ/آلوکم روی این جلسه نیست.",
                reconnectFail: "بروزرسانی لینک ورود ممکن نشد.",
                endFeedbackDone: "قبلاً ثبت شده",
                endFeedbackOrs: "ORS در انتظار",
                endFeedbackSrs: "SRS در انتظار",
                checkSpeaker: "بلندگو",
                checkSpeakerOk: "تست‌شده",
                checkNet: "اتصال",
                checkNetOk: "پایدار",
                checkPreviewBadge: "پیش‌نمایش تو",
                checkPreviewLabel: "تصویر آزمایشی",
                checkPreviewWaitingTitle: "پیش‌نمایش دوربین",
                checkPreviewWaitingBody: "برای دیدن تصویرت، دسترسی دوربین و میکروفون را مجاز کن.",
                checkEnterWait: "ورود به اتاق انتظار",
                checkRetry: "تلاش دوباره برای دوربین و میکروفون",
                checkHelp: "راهنمای خطا و دسترسی",
                checkHelpStatus: "دسترسی دوربین و میکروفون را برای این سایت مجاز کن، بعد «تلاش دوباره» را بزن. در هر صورت می‌توانی وارد اتاق انتظار شوی.",
                checkInsecure: "دوربین و میکروفون فقط روی صفحهٔ امن (https یا localhost) کار می‌کند.",
                checkUnsupported: "این مرورگر به دوربین یا میکروفون دسترسی ندارد.",
                waitTitle: "منتظر شروع جلسه هستی",
                waitBody: "منتظر شروع جلسه هستی. حضور زودهنگام به‌معنی شروع جلسه نیست.",
                waitMetaAria: "جزئیات اتاق انتظار",
                waitTherapist: "درمانگر",
                waitTherapistValue: "در حال پیوستن",
                waitTime: "زمان جلسه",
                waitTimeValue: "۱۷:۳۰ · تهران",
                waitStart: "وضعیت شروع",
                waitStartValue: "هنوز شروع نشده",
                waitCheckStart: "بررسی شروع جلسه",
                waitLeave: "خروج",
                waitSupport: "پشتیبانی",
                roomTitle: "اتاق جلسه آنلاین",
                roomTherapistAria: "ویدئوی درمانگر",
                roomTherapistBadge: "درمانگر",
                roomLive: "متصل",
                roomTherapistLabel: "تصویر درمانگر",
                roomSelfAria: "ویدئوی خودت",
                roomSelfBadge: "تو",
                roomSelfLabel: "پیش‌نمایش",
                roomControlsAria: "کنترل‌های جلسه",
                roomMicIcon: "صدا",
                roomCamIcon: "تصویر",
                roomEndIcon: "پایان",
                roomMicOn: "میکروفون روشن",
                roomMicOff: "میکروفون خاموش",
                roomCamOn: "دوربین روشن",
                roomCamOff: "دوربین خاموش",
                roomEndControl: "خروج از جلسه",
                roomMetaAria: "جزئیات اتصال",
                roomConn: "وضعیت اتصال",
                roomConnValue: "پایدار",
                roomSupport: "پشتیبانی",
                roomSupportValue: "در دسترس",
                roomLeave: "خروج از جلسه",
                roomReconnectHint: "اگر ارتباط قطع شد",
                roomHelp: "کمک فوری",
                reconnectTitle: "ارتباط قطع شده است",
                reconnectBody: "ارتباط قطع شده است. در حال تلاش برای اتصال دوباره هستیم؛ این اتفاق جلسه را پایان‌یافته حساب نمی‌کند.",
                reconnectMetaAria: "جزئیات اتصال مجدد",
                reconnectConn: "وضعیت اتصال",
                reconnectConnValue: "قطع موقت",
                reconnectMic: "میکروفون",
                reconnectMicValue: "حفظ‌شده",
                reconnectCam: "دوربین",
                reconnectCamValue: "حفظ‌شده",
                reconnectAv: "میکروفون / دوربین",
                reconnectAvValue: "حفظ‌شده",
                reconnectRetry: "تلاش برای اتصال دوباره",
                reconnectSupport: "پشتیبانی",
                reconnectLeave: "خروج امن",
                endTitle: "جلسه پایان یافت",
                endBody: "جلسه شما پایان یافت. لطفا بازخورد خود را ثبت کرده و برای رزرو جلسه بعدی اقدام کنید.",
                endMetaAria: "جزئیات پایان جلسه",
                endStatus: "وضعیت پایان",
                endStatusValue: "ثبت‌شده",
                endFeedback: "بازخورد کلاینت",
                endFeedbackValue: "آمادهٔ ثبت",
                endFeedbackCta: "ثبت بازخورد جلسه",
                endDashboard: "بازگشت به داشبورد",
                endBookNext: "رزرو جلسه بعد",
                outcomePreviewTitle: "بازخورد جلسه (پیش‌نمایش)",
                outcomePreviewBody: "پیش‌نمایش بازخورد جلسه (SRS) — صفحهٔ کامل در بخش Treatment Measurement می‌آید.",
                outcomeBack: "بازگشت",
                supportStatus: "پشتیبانی به‌زودی با تو تماس می‌گیرد.",
            },
            treatment: {
                srsTitle: "بازخورد جلسه",
                srsIntro: "بازخورد این جلسه فقط برای درمانگر مرتبطت دیده می‌شود. نقطهٔ وسط پاسخ نهایی نیست؛ هر مورد را جداگانه تأیید کن. عدد زنده نشان داده نمی‌شود.",
                srs1Legend: "رابطه",
                srs1Sub: "امروز چقدر احساس کردی شنیده و درک می‌شوی؟",
                srs2Legend: "موضوعات و اهداف",
                srs2Sub: "آیا روی چیزی کار کردید که برایت مهم بود؟",
                srs3Legend: "رویکرد جلسه",
                srs3Sub: "روش کار امروز چقدر با نیازت جور بود؟",
                srs4Legend: "در کل",
                srs4Sub: "در مجموع، این جلسه چطور بود؟",
                srsSubmit: "ثبت بازخورد",
                srsLater: "بعداً",
                orsTitle: "ارزیابی مسیر",
                orsIntro: "لطفاً وضعیتت را در چند روز گذشته روی هر خط مشخص کن. نقطهٔ وسط فقط نقطهٔ شروع است و تا وقتی تأیید نکنی، پاسخ ثبت‌شده نیست. عدد روی صفحه نشان داده نمی‌شود.",
                ors1Legend: "به‌طور فردی",
                ors1Sub: "حال شخصی و احساس خودت در این روزها",
                ors2Legend: "در روابط نزدیک",
                ors2Sub: "خانواده، شریک زندگی یا دوستان نزدیک",
                ors3Legend: "در زندگی اجتماعی",
                ors3Sub: "کار، تحصیل، دوستی‌ها و فعالیت‌های روزمره",
                ors4Legend: "در کل",
                ors4Sub: "اگر بخواهی یک تصویر کلی از وضعیتت بدهی",
                orsSubmit: "ثبت ارزیابی",
                orsIncomplete: "فعلاً نمی‌توانم کامل کنم",
                metaSession: "جلسه:",
                metaSessionValue: "۳",
                metaVersion: "نسخه محتوا:",
                metaVersionValue: "fa-IR-۱٫۰٫۲",
                confirmAnswer: "این پاسخ را تأیید می‌کنم",
                confirmRequired: "لطفاً همهٔ پاسخ‌ها را قبل از ثبت تأیید کن.",
                noPendingAssessment: "ارزیابی بازی برای جلسه پیدا نشد. وقتی یادآور جلسه آماده باشد از آنجا باز کن.",
                sessionIneligible: "این جلسه لغو شده، پس ارزیابی‌ای برای ثبت نیست. وقتی آماده بودی جلسهٔ بعد را رزرو کن.",
                contentUnavailable: "این ارزیابی برای مسیر فعلی‌ات در دسترس نیست.",
                srsNotDue: "بازخورد جلسه بعد از برگزاری جلسه باز می‌شود.",
                srsWindowExpired: "مهلت ۲۴ ساعته بازخورد جلسه تمام شده است.",
                orsWindowClosed: "ارزیابی مسیر این جلسه بعد از برگزاری بسته شده است.",
                outOfSequence: "اول ارزیابی فعلی را کامل کن، بعد به ترتیب ادامه بده.",
                backToPortal: "بازگشت به پرتال من",
                submitError: "ثبت ارزیابی ممکن نشد. اتصال را بررسی کن و دوباره تلاش کن.",
                submitErrorContinue: "همگام‌سازی ارزیابی ممکن نشد؛ با ذخیرهٔ محلی ادامه می‌دهیم.",
                orsAlreadyDone: "ارزیابی قبل از جلسه برای این جلسه قبلاً ثبت شده است.",
                orsAlreadySkipped: "ارزیابی مسیر برای این جلسه قبلاً رد شده است.",
                assessAlreadyDone: "ارزیابی این جلسه کامل شده است.",
                notifyEmpty: "الان اعلان تازه‌ای نداری.",
                notifyPendingWhen: "در انتظار",
                doneFormOrs: "ORS · قبل از جلسه",
                doneFormSrs: "SRS · بعد از جلسه",
                rulerAriaUnset: "انتخاب‌نشده تا تأیید",
                anchorLow: "کم",
                anchorHigh: "زیاد",
                anchorMin: "کمینه",
                anchorMax: "بیشینه",
                doneTitle: "پاسخ‌هایت ثبت شد",
                doneBody: "پاسخ‌هایت ثبت شد. امتیاز یا تشخیصی روی این صفحه نشان داده نمی‌شود.",
                doneMetaAria: "خلاصه ثبت",
                doneSession: "شماره جلسه",
                doneSessionValue: "۳",
                doneForm: "نام فرم",
                doneFormValue: "ثبت‌شده",
                doneStatus: "وضعیت ثبت",
                doneStatusValue: "موفق",
                doneStatusUnconfirmed: "این صفحه ذخیرهٔ ارزیابی را تأیید نمی‌کند.",
                doneSessionUnknown: "—",
                doneCta: "بازگشت به داشبورد",
                missTitle: "این ارزیابی هنوز ثبت نشده",
                missBody: "این ارزیابی هنوز ثبت نشده. لطفا برای ارزیابی مسیر درمان خود اقدام به تکمیل این ارزیابی کنید.",
                missSkippedTitle: "رد کردن این ارزیابی ثبت شد",
                missSkippedBody: "ارزیابی مسیر برای همین جلسه رد شد و دیگر در فهرست باز نیست.",
                missUnknownBody: "وضعیت ثبت پاسخ در دسترس نیست. ارزیابی را از صفحهٔ جلسه دوباره باز کن.",
                missMetaAria: "جزئیات ارزیابی ناقص",
                missSession: "جلسهٔ مربوط",
                missSessionValue: "جلسه ۳",
                missForm: "نام پرسشنامه",
                missFormValue: "ارزیابی مسیر",
                missReasonAria: "دلیل ثبت‌نشدن ارزیابی",
                missOpt_later: "بعداً کامل می‌کنم",
                missOpt_tech: "مشکل فنی داشتم",
                missOpt_skip: "ترجیح می‌دهم فعلاً رد کنم",
                missComplete: "تکمیل فرم مربوط به جلسه",
                missBack: "بازگشت",
                thTitle: "گفت‌وگو و اقدام درمانگر",
                thBody: "الگوی مسیر نیاز به گفت‌وگوی کوتاه و ثبت اقدام دارد. این صفحه برای درمانگر منتسب است.",
                thMetaAria: "جزئیات پیگیری درمانگر",
                thClient: "کلاینت منتسب",
                thClientValue: "سارا م.",
                thTrigger: "تریگر",
                thTriggerValue: "نیاز به بررسی مسیر",
                thOrs: "ارزیابی مسیر",
                thOrsValue: "ثبت‌شده · جلسه ۳",
                thDeadline: "مهلت",
                thDeadlineValue: "۴۸ ساعت",
                thGuide: "راهنمای گفتگو: بدون اشاره به عدد یا برچسب بالینی، از مراجع بپرس چه چیزی در این هفته سخت‌تر یا بهتر شده و یک اقدام مشترک مشخص کنید.",
                thActionAria: "اقدام درمانگر",
                thOpt_talk: "گفت‌وگو انجام شد",
                thOpt_plan: "برنامهٔ مشترک تنظیم شد",
                thOpt_escal: "ارجاع به پیگیری کیفیت",
                thSubmit: "ثبت اقدام درمانگر",
                thEscalate: "ارجاع به مدیر کیفیت",
                th03Title: "ادامه مسیر درمانگر (پیش‌نمایش)",
                th03Body: "پیش‌نمایش ادامهٔ مسیر درمانگر (TH-03) — صفحهٔ کامل در Therapist Portal می‌آید.",
                th03Back: "بازگشت",
                qm01Title: "فهرست کیفیت (پیش‌نمایش)",
                qm01Body: "پیش‌نمایش فهرست کیفیت (QM-01) — صفحهٔ کامل در Quality Operations می‌آید.",
                qm01Cta: "بازگشت",
                qmTitle: "پیگیری مدیر کیفیت",
                qmBody: "پروندهٔ کیفیت برای بررسی تصمیم. بازخورد جلسه و نامهٔ خصوصی اینجا منتقل نمی‌شود.",
                qmMetaAria: "جزئیات پرونده کیفیت",
                qm_case: "پرونده کیفیت",
                qm_caseValue: "QM-۲۴۰۳",
                qm_reason: "علت دسترسی",
                qm_reasonValue: "پیگیری گام اول",
                qm_ors: "ارزیابی مسیر",
                qm_orsValue: "خلاصهٔ مجاز",
                qm_history: "تاریخچه گام اول",
                qm_historyValue: "ثبت‌شده توسط درمانگر",
                qm_owner: "مالک",
                qm_ownerValue: "مدیر کیفیت",
                qm_deadline: "مهلت",
                qm_deadlineValue: "۷۲ ساعت",
                qmDecisionAria: "تصمیم کیفیت",
                qmOpt_monitor: "ادامهٔ پایش",
                qmOpt_coach: "پشتیبانی از درمانگر",
                qmOpt_rematch: "پیشنهاد تغییر مسیر به کلاینت",
                qmSubmit: "ثبت تصمیم کیفیت",
                notifyTitle: "اعلان‌ها",
                notifyBody: "اعلان‌های عملیاتی بدون جزئیات بالینی. باز کردن هر مورد، دسترسی مقصد را دوباره کنترل می‌کند.",
                notifyListAria: "فهرست اعلان‌ها",
                notifySrsTitle: "بازخورد جلسه آمادهٔ ثبت است",
                notifySrsWhen: "امروز · ۱۰:۱۲",
                notifyOrsTitle: "ارزیابی مسیر در انتظار تکمیل",
                notifyOrsWhen: "دیروز · ۱۸:۴۰",
                notifyDoneTitle: "پاسخ قبلی با موفقیت ذخیره شد",
                notifyDoneWhen: "۲ روز پیش",
                notifyUnread: "خوانده‌نشده",
                notifyPendingState: "در انتظار ثبت",
                notifyMissing: "ثبت نشده",
                notifySession: "جلسه {n}",
                notifyRead: "خوانده‌شده",
                notifyBack: "بازگشت",
            },
            portal: {
                brand: "رو‌برو",
                avatarShort: "نا",
                avatarFallback: "—",
                personName: "نیلوفر احمدی",
                personNameFallback: "حساب من",
                personRole: "حساب مشتری",
                therapistName: "دکتر نسترن صادقی",
                therapistAvatar: "نص",
                therapistRole: "درمانگر شما",
                eyebrow: "پرتال من",
                navAria: "منوی پرتال",
                navSectionsAria: "بخش‌های حساب",
                mobileNavAria: "ناوبری موبایل",
                sideFoot: "رضایت و آمادگی",
                profileAria: "پروفایل",
                navHome: "خانه من",
                navServices: "خدمات و حساب من",
                navServicesShort: "بیشتر",
                navHomeShort: "خانه",
                navSessions: "جلسه‌های من",
                navSessionsShort: "جلسه‌ها",
                navProgress: "روند من",
                navProgressShort: "روند",
                navPayments: "پرداخت و اعتبار",
                navPaymentsShort: "پرداخت",
                navSupport: "درخواست‌های من",
                navSupportShort: "پشتیبانی",
                navChat: "پیام‌های درمانگر",
                navChatShort: "پیام‌ها",
                details: "جزئیات",
                reschedule: "تغییر زمان",
                back: "بازگشت",
                currency: "تومان",
                homeTitle: "سلام نیلوفر",
                homeTitleLive: "سلام {name}",
                homeSubtitle: "جلسه‌ها، مسیر درمان، پرداخت‌ها و درخواست‌هایت را اینجا می‌بینی.",
                homeHeroDay: "۲۸",
                homeHeroMonth: "اسفند",
                homeHeroEyebrow: "جلسه بعدی",
                homeHeroTitle: "سه‌شنبه · ۱۷:۳۰",
                homeHeroBody: "با دکتر نسترن صادقی · آنلاین · آماده برگزاری",
                homeHeroCta: "مشاهده جلسه بعد",
                homeHeroAlt: "تغییر یا لغو",
                homeNoSessionEyebrow: "جلسه‌ای در پیش نیست",
                homeNoSessionTitle: "هنوز رزروی نداری",
                homeStartScreeningTitle: "درمانگر مناسب خودت را پیدا کن",
                homeStartScreeningBody: "با شروع غربالگری، نیازهایت را بهتر می‌شناسیم و برای پیدا کردن درمانگر مناسب کمکت می‌کنیم.",
                homeStartScreeningCta: "شروع غربالگری",
                homeNoSessionBody: "وقتی جلسه‌ای رزرو شود، زمان و آمادگی اینجا دیده می‌شود.",
                homeNoSessionCta: "مشاهده جلسه‌ها",
                homeNoSessionTitleCredit: "اعتبار جلسه‌ات هنوز هست",
                homeNoSessionBodyCredit: "جلسه لغو‌شده به اعتبار {n} جلسه برگشت. هر وقت آماده بودی دوباره رزرو کن.",
                homeNoSessionCtaCredit: "رزرو با اعتبار",
                homeSummaryAria: "خلاصه اقدام‌ها",
                homeCardOrsTitle: "ارزیابی مسیر",
                homeCardOrsBody: "الان چیزی برای ثبت نداری — همه‌چیز به‌روز است. بعد از برگزاری جلسه بعدی، بازخورد از اینجا باز می‌شود.",
                homeCardOrsBodyIdle: "الان چیزی برای ثبت نداری — همه‌چیز به‌روز است. بعد از برگزاری جلسه بعدی، بازخورد از اینجا باز می‌شود.",
                homeCardOrsBodyPending: "یک ارزیابی مسیر باز داری که منتظر تکمیل است.",
                homeCardSrsBodyPending: "بازخورد جلسه برای یک جلسه برگزارشده باز است — تا ۲۴ ساعت فرصت داری تکمیلش کنی.",
                homeCardOrsCta: "تکمیل ارزیابی",
                homeCardOrsCtaIdle: "مشاهده روند من",
                homeCardPayBodyIdle: "اعتبار جلسه و رسیدها اینجا می‌آید.",
                homeCardPayBodyCredit: "اعتبار {n} جلسه برای رزرو داری.",
                homeCardPayBodyFactor: "آخرین رسید · {status} · {amount}",
                homeCardSupportBodyIdle: "اگر بین جلسه‌ها کمک لازم داری، درخواست ثبت کن.",
                homeCardSupportBodyLive: "{title} · {status}",
                homeCardSupportCtaIdle: "باز کردن پشتیبانی",
                homeJ1BodyIdle: "با مچ درمانگر، مسیر از اینجا شروع می‌شود.",
                homeJ2BodyIdle: "با رزرو جلسه، ادامه مسیر اینجا دیده می‌شود.",
                homeJ2BodyDone: "جلسه اولت برگزار شده است.",
                homeJ3BodyIdle: "بازخورد بعد از جلسه‌ها این نما را می‌سازد.",
                homeJ3BodyPending: "یک ارزیابی باز منتظر تکمیل است.",
                homeJ3BodyLive: "ثبت مسیر انجام شده ({n}). در روند ادامه بده.",
                therapistNameEmpty: "هنوز درمانگری مشخص نشده",
                homeTherapistBodyEmpty: "بعد از مچ، درمانگر اینجا برای هماهنگی کوتاه بین جلسه‌ها دیده می‌شود.",
                loadError: "بارگذاری این صفحه ممکن نشد. اتصال را بررسی کن و دوباره تلاش کن.",
                loadSoftError: "به‌روزرسانی بخشی از اطلاعات حساب ممکن نشد. آنچه در دسترس است نمایش داده می‌شود.",
                needsLogin: "برای دیدن جلسه‌ها و درمانگر زنده، دوباره وارد شو.",
                needsLoginCta: "ورود",
                loading: "در حال بارگذاری…",
                noData: "هنوز چیزی برای نمایش نیست.",
                sessionsNoUpcomingTitle: "جلسه‌ای در پیش نیست",
                sessionsNoUpcomingBody: "با خرید بسته یا پس از هماهنگی، زمان بعدی اینجا می‌آید.",
                sessionsNoUpcomingBodyCredit: "اعتبار {n} جلسه داری. بدون خرید دوباره، زمان جدید رزرو کن.",
                nextSessionTitle: "زمان جلسه بعدی",
                nextSessionKicker: "برنامه درمان شما",
                nextSessionIntro: "یک زمان مناسب را با درمانگر اختصاص‌یافته‌تان انتخاب کنید.",
                nextSessionCta: "انتخاب زمان",
                nextSessionTherapist: "درمانگر شما: {name}",
                nextSessionTherapistLabel: "درمانگر اختصاص‌یافته شما",
                nextSessionExperience: "{years} سال تجربه",
                nextSessionRating: "امتیاز {rating}",
                nextSessionSlotsTitle: "زمان‌های در دسترس",
                nextSessionSlotsHint: "یک زمان را انتخاب کنید تا ده دقیقه برایتان نگه داشته شود.",
                nextSessionNoSlots: "در حال حاضر زمان آزادی وجود ندارد.",
                nextSessionHold: "نگه‌داشتن این زمان",
                nextSessionReserve: "رزرو این زمان",
                nextSessionChooseAndPay: "انتخاب این زمان",
                nextSessionHoldUntil: "این زمان تا {time} نگه داشته می‌شود",
                nextSessionUseCredit: "استفاده از اعتبار جلسه",
                nextSessionPaymentRequired: "این زمان نگه داشته شده است. پرداخت را پیش از پایان مهلت کامل کنید.",
                nextSessionGoToPayment: "رفتن به پرداخت",
                nextSessionPaymentResult: "پرداخت جلسه",
                nextSessionPaymentProcessing: "در حال بررسی پرداخت و تأیید جلسه‌ات…",
                nextSessionPaymentPending: "تأیید پرداخت هنوز در حال انجام است. می‌توانی وضعیت را دوباره بررسی کنی.",
                nextSessionPaymentFailed: "پرداخت کامل نشد. جلسه‌ای برایت رزرو نشده است.",
                nextSessionPaymentExpired: "مهلت رزرو به پایان رسید. لطفاً دوباره یک زمان انتخاب کن.",
                nextSessionPaymentLate: "پرداختت دریافت شد، اما زمان انتخاب‌شده دیگر نگه داشته نشده است. پشتیبانی برای رزرو دوباره یا رسیدگی به پرداخت کمکت می‌کند.",
                nextSessionPaymentSupport: "درخواست پشتیبانی",
                nextSessionPickAgain: "انتخاب دوباره زمان",
                nextSessionCheckStatus: "بررسی وضعیت پرداخت",
                nextSessionRelease: "آزاد کردن این زمان",
                nextSessionConfirmed: "جلسه بعدی شما زمان‌بندی شد.",
                nextSessionAlreadyScheduled: "جلسه بعدی شما از قبل زمان‌بندی شده است.",
                nextSessionUnavailable: "زمان‌بندی جلسه بعدی فعلاً در دسترس نیست.",
                sessionMissingTitle: "جلسه پیدا نشد",
                sessionMissingBody: "این جلسه در دسترس نیست. از فهرست جلسه‌ها یکی را انتخاب کن.",
                progressChartDescLive: "جهت تجربه در {n} جلسه ثبت شده است. نمره بالینی نمایش داده نمی‌شود.",
                progressMsgTitleOngoing: "مسیرت در حال پیشروی است.",
                progressMsgBodyLive: "بر اساس {n} ثبت کوتاه. سؤال‌های بعد از جلسه مسیر را با درمانگرت تنظیم می‌کند.",
                homeCardPayTitle: "پرداخت و اعتبار",
                homeCardPayBody: "اعتبار {n} جلسه برای رزرو داری.",
                homeCardPayCta: "مشاهده پرداخت‌ها",
                homeCardSupportTitle: "درخواست پشتیبانی",
                homeCardSupportBody: "{title} · {status}",
                homeCardSupportCta: "پیگیری درخواست",
                homePathTitle: "مسیر من",
                homePathSub: "یک نمای ساده از جایی که الان هستی",
                homePathCta: "مشاهده روند من",
                homeJ1Title: "شروع مسیر",
                homeJ1Body: "ثبت‌نام و مچ انجام شد",
                homeJ2Title: "جلسه اول",
                homeJ2Body: "رزرو قطعی · آماده برگزاری",
                homeJ3Title: "ادامه مسیر",
                homeJ3Body: "با بازخورد تو ساخته می‌شود",
                homeTherapistBody: "برای هماهنگی کوتاه بین جلسه‌ها پیام بگذار. موضوع درمان را در جلسه مطرح کن.",
                homeChatCta: "پیام با درمانگر",
                homeRequestCta: "ثبت درخواست پشتیبانی",
                sessionsTitle: "جلسه‌های من",
                sessionsBuyCta: "خرید جلسه جدید",
                sessionsUpcoming: "جلسه‌های پیش رو",
                sessionsUpcomingBody: "زمان، وضعیت آمادگی و تغییر رزرو را یک‌جا می‌بینی.",
                session1Day: "۲۸",
                session1Title: "سه‌شنبه ۲۸ اسفند · ۱۷:۳۰",
                session1Meta: "دکتر نسترن صادقی · آنلاین · تأییدشده",
                session2Day: "۴",
                session2Title: "شنبه ۴ فروردین · ۱۸:۰۰",
                session2Meta: "دکتر نسترن صادقی · آنلاین · زمان رزروشده",
                sessionsPast: "جلسه‌های گذشته",
                sessionsEmptyTitle: "هنوز جلسه‌ای برگزار نشده",
                sessionsEmptyBody: "پس از جلسه، بازخورد و روند اینجا اضافه می‌شود.",
                progressTitle: "روند من",
                progressIntro: "مسیر تو، به زبان ساده",
                progressIntroBody: "این نما جهت کلی تجربه‌ات را نشان می‌دهد؛ نه نمره یا قضاوت بالینی.",
                progressVideoAria: "پخش ویدیوی توضیح روند و نتیجه",
                progressVideoEyebrow: "ویدیوی کوتاه",
                progressVideoTitle: "این نمودار چه چیزی را نشان می‌دهد؟",
                progressVideoMeta: "حدود ۹۰ ثانیه · بدون نمره بالینی",
                progressChartTitle: "روند کلی مسیر درمان",
                progressChartDesc: "جهت تجربه در دو جلسه اول ثبت شده است. نمره بالینی نمایش داده نمی‌شود.",
                progressAxis: "شماره جلسه",
                progressMsgTitle: "الان نزدیک شروع مسیر هستی.",
                progressMsgBody: "بعد از هر جلسه چند سؤال کوتاه کمک می‌کند مسیر را با درمانگرت تنظیم کنید.",
                progressEmptyTitle: "هنوز نموداری برای نشان دادن نیست",
                progressEmptyBody: "بعد از برگزاری جلسه و چند سؤال کوتاه، مسیر اینجا شکل می‌گیرد. جلسهٔ لغو‌شده ارزیابی ندارد.",
                progressEmptyCta: "رزرو جلسه",
                progressOrsCta: "تکمیل ارزیابی بعدی",
                progressLetterCta: "نامه به خودم",
                payTitle: "پرداخت و اعتبار",
                payIntro: "پرداخت‌ها و اعتبار",
                payIntroBody: "مبلغ هر جلسه قبل از رزرو روشن است.",
                payCreditLabel: "اعتبار فعلی",
                payCreditValue: "۲ جلسه",
                payCreditCta: "رزرو با اعتبار",
                payLastLabel: "آخرین پرداخت",
                payLastValue: "موفق",
                payLastMeta: "۴۸۰٬۰۰۰ تومان · اینترنتی",
                payLastNone: "هنوز پرداختی نیست",
                payLastNoneMeta: "با خرید بسته، رسید اینجا می‌آید.",
                payEmptyTitle: "هنوز رسیدی نیست",
                payEmptyBody: "بعد از خرید، فاکتورها و رسیدها در این فهرست می‌آیند.",
                payBuyPackage: "خرید بسته جدید",
                payReceiptTitle: "جلسه اول با دکتر نسترن صادقی",
                payReceiptMeta: "۲۵ اسفند · پرداخت اینترنتی · موفق",
                payReceiptCta: "مشاهده رسید",
                payRefundTitle: "پیگیری بازپرداخت",
                payRefundMeta: "در صورت نیاز، وضعیت درخواست را ببین",
                payRefundCta: "وضعیت بازپرداخت",
                packTitle: "خرید بسته جلسه",
                packIntro: "بسته برای درمانگر تو",
                packIntroBody: "قیمت بسته بر اساس نرخ جلسهٔ هر درمانگر محاسبه می‌شود. اعتبار فقط برای همان درمانگر قابل استفاده است.",
                packTherapistEyebrow: "درمانگر فعلی",
                packRate: "نرخ هر جلسه",
                packRateValue: "۴۸۰٬۰۰۰ تومان",
                packSizeAria: "انتخاب اندازه بسته",
                packSizeLegend: "اندازه بسته",
                packFeatured: "پیشنهادی",
                packSessions: "جلسه",
                pack3Title: "بسته ۳ جلسه‌ای",
                pack3Copy: "شروع کوتاه‌مدت · بدون تخفیف",
                pack3Price: "۱٬۴۴۰٬۰۰۰",
                pack5Title: "بسته ۵ جلسه‌ای",
                pack5Copy: "۵٪ تخفیف · رایج‌ترین انتخاب",
                pack5Price: "۲٬۲۸۰٬۰۰۰",
                pack5Was: "به‌جای ۲٬۴۰۰٬۰۰۰",
                pack10Title: "بسته ۱۰ جلسه‌ای",
                pack10Copy: "۱۰٪ تخفیف · مسیر بلندمدت",
                pack10Price: "۴٬۳۲۰٬۰۰۰",
                pack10Was: "به‌جای ۴٬۸۰۰٬۰۰۰",
                packNote: "اعتبار بسته فقط برای جلسات با دکتر نسترن صادقی قابل استفاده است و قابل انتقال به درمانگر دیگر نیست.",
                packNoteLive: "اعتبار بسته فقط برای جلسات با {name} قابل استفاده است و قابل انتقال نیست.",
                packNoteGeneric: "اعتبار بسته به درمانگر فعلی‌ات وابسته است و قابل انتقال نیست.",
                packRateMissing: "نرخ جلسه بعد از تعیین درمانگر اینجا دیده می‌شود.",
                packRateUnavailable: "نرخ جلسه در حال حاضر در دسترس نیست. لطفاً بعداً دوباره تلاش کن.",
                packCountTitle: "بسته {n} جلسه‌ای",
                packPriceChanged: "قیمت بسته تغییر کرده است. پیش از ادامه، این صفحه را به‌روزرسانی کن.",
                packEmptyTitle: "بسته‌ای در دسترس نیست",
                packEmptyBody: "بعد از تعیین درمانگر بسته‌ها می‌آیند، یا بعداً دوباره تلاش کن.",
                packFallbackTitle: "بسته جلسه",
                packFallbackCopy: "اعتبار جلسه با درمانگر تو.",
                packPayError: "افزودن این بسته به سبد ممکن نشد. دوباره تلاش کن.",
                packPayCta: "ادامه به پرداخت",
                supportTitle: "درخواست‌های من",
                supportHeroEyebrow: "کنارت هستیم",
                supportHeroTitle: "برای چه چیزی کمک می‌خواهی؟",
                supportHeroBody: "هماهنگی جلسه، پرداخت یا مسئله فنی را با پشتیبانی مطرح کن. موضوع درمان را با درمانگرت در میان بگذار.",
                supportNewCta: "ثبت درخواست جدید",
                supportCat1: "رزرو و تغییر زمان",
                supportCat2: "پرداخت و بازگشت وجه",
                supportCat3: "مشکل ورود به جلسه",
                supportOpen: "درخواست‌های تو",
                supportStatus_open: "باز",
                supportStatus_resolved: "حل‌شده",
                supportStatus_blocked: "مسدود",
                supportStatus_closed: "بسته",
                supportReplyClosed: "این درخواست بسته شده. اگر هنوز کمک لازم داری، درخواست جدید ثبت کن.",
                requestBodyRequired: "قبل از ثبت، یک پیام کوتاه بنویس.",
                requestReplyPlaceholder: "پیامت را برای پشتیبانی بنویس…",
                supportItemTitle: "هماهنگی تغییر ساعت",
                supportItemMeta: "آخرین به‌روزرسانی: امروز · ۱۰:۱۲",
                supportOpenStatus: "باز",
                chatEyebrow: "پیام غیرهمزمان",
                chatSla: "این گفت‌وگو زنده نیست و زمان پاسخ‌گویی تضمین نشده است.",
                chatBanner: "این گفتگو برای هماهنگی و پیگیری بین جلسه‌هاست. موضوع‌های درمانی عمیق را در جلسه مطرح کن. پاسخ فوری تضمین نمی‌شود.",
                chatThreadAria: "رشته پیام‌ها",
                chatDay1: "شنبه ۲۴ اسفند",
                chatDay2: "امروز",
                chatT1Time: "۱۸:۴۰",
                chatT1Body: "سلام نیلوفر. جلسهٔ اول را برای سه‌شنبه رزرو کردیم. اگر قبل از جلسه نکته‌ای برای هماهنگی داری، اینجا بنویس.",
                chatC1Time: "۱۹:۰۵",
                chatC1Body: "ممنون. فقط می‌خواستم تأیید کنم لینک ورود همان روز فعال می‌شود.",
                chatT2Time: "۱۰:۱۲",
                chatT2Body: "بله؛ لینک از حدود ۱۵ دقیقه قبل از شروع در جزئیات جلسه در دسترس است. اگر مشکلی پیش آمد، پشتیبانی هم کمکت می‌کند.",
                chatC2Time: "۱۱:۰۲",
                chatC2Body: "عالی، ممنون از توضیحت.",
                chatYou: "تو",
                chatRead: "خوانده شد",
                chatSent: "ارسال شد",
                chatNow: "اکنون",
                chatComposerAria: "ارسال پیام",
                chatInputLabel: "متن پیام",
                chatPlaceholder: "پیامت را بنویس… درمانگر بعداً پاسخ می‌دهد.",
                chatHint: "ارسال می‌شود و درمانگر وقتی فرصت داشت پاسخ می‌دهد.",
                chatSend: "ارسال پیام",
                chatNoContact: "هنوز مخاطب گفتگو در دسترس نیست.",
                chatEmpty: "هنوز پیامی در این گفتگو نیست.",
                chatLoadFail: "بارگذاری پیام‌ها ممکن نشد.",
                chatSendFail: "ارسال پیام ممکن نشد.",
                messengerUnavailable: "پیام‌رسان الان در دسترس نیست. بعداً دوباره امتحان کن یا از گفتگوی پنل استفاده کن.",
                supportMessengerNote: "این بخش برای درخواست‌های اجرایی است و از گفت‌وگو با درمانگر جداست. شرح بالینی ننویس. زمان پاسخ‌گویی در این بخش تضمین نشده است.",
                supportEmpty: "هنوز درخواست پشتیبانی ثبت نشده است.",
                letterTitle: "نامه‌ای که برای خودت نوشته بودی",
                letterSubtitle: "فقط برای تو نمایش داده می‌شود.",
                letterBody: "برای خودِ آینده‌ام نوشتم که چرا این مسیر را شروع کردم: می‌خواهم با آرامش بیشتری با خودم و اطرافیانم حرف بزنم و قدم‌به‌قدم جلو بروم.",
                letterMeta: "تاریخ نگارش: ۲۰ اسفند · فقط برای تو · بدون امکان ویرایش از این صفحه",
                letterBack: "بازگشت به روند من",
                sessionDetailTitle: "جزئیات جلسه",
                sessionDetail_therapist: "درمانگر",
                sessionDetail_therapistValue: "دکتر نسترن صادقی",
                sessionDetail_number: "شماره جلسه",
                sessionDetail_numberValue: "۱",
                sessionDetail_time: "زمان محلی",
                sessionDetail_timeValue: "سه‌شنبه ۲۸ اسفند · ۱۷:۳۰ · تهران",
                sessionDetail_status: "وضعیت",
                sessionDetail_statusValue: "ثبت نهایی شده",
                sessionReadyCta: "آماده‌شدن برای جلسه",
                sessionCancel: "لغو جلسه",
                requestTitle: "درخواست پشتیبانی",
                requestTopic: "موضوع عملیاتی",
                requestTopic_booking: "رزرو و تغییر زمان",
                requestTopic_payment: "پرداخت و بازگشت وجه",
                requestTopic_access: "مشکل ورود به جلسه",
                requestTopic_other: "سایر",
                requestTopic_delivery: "مشکل تحویل یا ارسال عملیاتی",
                requestTopic_dissatisfaction: "نارضایتی از جلسه یا درمانگر (تیم کیفیت)",
                requestBody: "پیام تو",
                requestPlaceholder: "یک پیام کوتاه برای پشتیبانی بنویس…",
                requestSubmit: "ثبت درخواست",
                requestCancel: "انصراف",
                requestSendFail: "ثبت درخواست پشتیبانی ممکن نشد و چیزی به‌عنوان تحویل‌شده ثبت نشد.",
                requestSavedLocal: "درخواست ثبت نشد.",
                requestStatusSent: "باز · ثبت‌شده در سرویس پشتیبانی",
                requestStatusLocal: "ثبت‌نشده",
                requestDetailTitle: "پیگیری درخواست",
                requestDetail_topic: "موضوع",
                requestDetail_topicValue: "هماهنگی تغییر ساعت",
                requestDetail_status: "وضعیت",
                requestDetail_statusValue: "باز · در انتظار پاسخ",
                requestDetail_last: "آخرین پیام",
                requestDetail_lastValue: "امروز · ۱۰:۱۲",
                requestDetail_reply: "پاسخ پشتیبانی",
                requestDetail_replyValue: "هنوز پاسخی ثبت نشده",
                requestDetailBack: "بازگشت به درخواست‌ها",
                receiptTitle: "رسید پرداخت",
                invoiceDetailsTitle: "جزئیات فاکتور",
                invoiceNotReceipt: "این فاکتور رسید پرداخت تأییدشده نیست. پیش از پرداخت دوباره، وضعیت آن را بررسی کن.",
                receipt_amount: "مبلغ",
                receipt_amountValue: "۴۸۰٬۰۰۰ تومان",
                receipt_date: "تاریخ",
                receipt_dateValue: "۲۵ اسفند ۱۴۰۴ · ۱۴:۲۲",
                receipt_method: "روش",
                receipt_methodValue: "پرداخت اینترنتی",
                receipt_code: "شماره رسید",
                receipt_codeValue: "RB-۲۵۸۴۴۱",
                receipt_session: "جلسه",
                receipt_sessionValue: "جلسه اول · دکتر نسترن صادقی",
                receipt_status: "وضعیت",
                receiptMethodOnline: "پرداخت اینترنتی",
                receiptMethodOffline: "پرداخت آفلاین",
                receiptMethodWallet: "تسویه کیف پول",
                receiptMethodUnknown: "—",
                receiptMissingTitle: "رسید پیدا نشد",
                receiptMissingBody: "این فاکتور در دسترس نیست. از فهرست پرداخت‌ها یکی را انتخاب کن.",
                receiptPayCta: "ادامه پرداخت",
                receiptBack: "بازگشت به پرداخت‌ها",
                refundTitle: "پیگیری بازپرداخت",
                refund_amount: "مبلغ",
                refund_amountValue: "۴۸۰٬۰۰۰ تومان",
                refund_dest: "مقصد",
                refund_destValue: "همان کارت پرداخت",
                refund_requestStatus: "وضعیت درخواست",
                refund_requestStatusValue: "بررسی‌شده",
                refund_execStatus: "وضعیت اجرا",
                refund_execStatusValue: "در صف واریز",
                refund_updated: "آخرین به‌روزرسانی",
                refund_updatedValue: "دیروز · ۱۶:۴۰",
                refundBack: "بازگشت به پرداخت‌ها",
                refundUnavailable: "پیگیری آنلاین بازپرداخت در دسترس نیست. برای بررسی پرداخت با پشتیبانی تماس بگیرید.",
                refundSupportCta: "کمک دربارهٔ پرداخت یا بازپرداخت",
                refundSupportIntro: "درخواست بررسی پرداخت را برای پشتیبانی بفرست. ثبت درخواست به معنی تأیید بازپرداخت یا تعیین زمان واریز نیست. پس از ثبت، درخواست و پاسخ‌ها قابل پیگیری‌اند.",
                refundSupportBody: "لطفاً پرداخت من را بررسی کنید و بگویید آیا بازپرداخت امکان‌پذیر است و مرحلهٔ بعد چیست.",
                refundSupportReference: "شمارهٔ رسید: {reference}",
            },

            consentSignerTitle: "شرکت‌کنندگان و رضایت",
            consentSignerBody: "برای این مسیر، امضای شرکت‌کنندگان لازم است. تا تکمیل وضعیت‌های لازم، دسترسی ورود باز نمی‌شود.",
            consentSignerColName: "نام",
            consentSignerColRole: "نقش",
            consentSignerColParty: "ذی‌نفع",
            consentSignerColSigner: "امضاکننده",
            consentSignerColStatus: "وضعیت",
            consentSignerRow: {
                client: {
                    name: "سارا م.",
                    role: "مراجع",
                    party: "خود",
                    signer: "لازم",
                    status: "در انتظار",
                },
                guardian: {
                    name: "ولی قانونی",
                    role: "سرپرست",
                    party: "نوجوان",
                    signer: "لازم",
                    status: "ناقص",
                },
            },
            consentSignerCta: "تأیید اطلاعات شرکت‌کنندگان",
            consentGuardTitle: "تأیید اختیار سرپرست",
            consentGuardBody: "برای مسیر نوجوان/کودک، اختیار سرپرست باید ثبت و بررسی شود. این صفحه مجوز حقوقی نیست؛ فقط جمع‌آوری اطلاعات طبق سیاست است.",
            consentGuardSummaryAria: "خلاصه اختیار سرپرست",
            consentGuardId: "هویت سرپرست",
            consentGuardIdValue: "در حال تکمیل",
            consentGuardParty: "ذی‌نفع",
            consentGuardPartyValue: "نوجوان · زیر ۱۸",
            consentGuardWitness: "شاهد اختیار",
            consentGuardWitnessValue: "مدارک پیوست‌نشده",
            consentGuardReview: "وضعیت بررسی",
            consentGuardReviewValue: "نیاز به ارسال",
            consentGuardCta: "ارسال اطلاعات اختیار",
            consentGuardLater: "فعلاً بعداً",
            consentTitle: "رضایت آگاهانه درمان",
            consentIntro: "این تأیید یک گیت واقعی است؛ تا ثبت نشود، ورود به جلسه ممکن نیست.",
            consentDocTitle: "رضایت‌نامهٔ درمان — رو‌برو",
            consentDocLead: "پیش از شروع، می‌خواهیم بدانی چه چیزی را می‌پذیری.",
            consentDraftLabel: "نسخهٔ آزمایشی پروتوتایپ:",
            consentDraftBody: "این متن پیش از انتشار باید به تأیید حقوقی و بالینی برسد.",
            consentSection1Title: "۱ · چه کسی درمانت را انجام می‌دهد",
            consentSection1Body: "تراپیست تو دارای پروانهٔ فعالیت است. اگر لازم شد تراپیستت عوض شود، با اطلاع و موافقت خودت انجام می‌شود.",
            consentSection2Title: "۲ · چه چیزی از تو ثبت می‌شود",
            consentSection2Body: "پاسخ‌های فرم ورود، نتیجهٔ پرسش‌نامه‌های کوتاه هر جلسه، یادداشت‌های بالینی تراپیست و سوابق مالی و ارتباطی.",
            consentSection3Title: "۳ · چه کسی چه چیزی را می‌بیند",
            consentSection3Body: "تراپیستت پروندهٔ بالینی تو را می‌بیند. تیم پشتیبانی فقط اطلاعات عملیاتی و مالی را می‌بیند و به پروندهٔ بالینی دسترسی ندارد. مدیر کیفیت داده‌های سنجش را برای پایش مسیر درمان می‌بیند. یادداشت‌های بالینی تراپیست فقط برای خود اوست.",
            consentSection4Title: "۴ · محرمانگی و مرزهایش",
            consentSection4Body: "آنچه در جلسه می‌گذرد محرمانه است، مگر در حالت‌هایی که قانون یا ایمنی تو یا دیگری اجازهٔ سکوت نمی‌دهد. مرزهای دقیق افشا، خطر برای خود یا دیگری و موارد مربوط به کودکان باید در نسخهٔ حقوقی نهایی شوند.",
            consentSection5Title: "۵ · ضبط",
            consentSection5Body: "جلسه‌ها ضبط نمی‌شوند، مگر با درخواست و موافقت صریح خودت.",
            consentSection6Title: "۶ · حق توقف",
            consentSection6Body: "هر وقت بخواهی می‌توانی درمان را متوقف کنی و لازم نیست دلیل بیاوری. شرایط لغو و بازپرداخت جلسه جداگانه نوشته شده است.",
            consentSection7Title: "۷ · وضعیت‌های اضطراری",
            consentSection7Body: "رو‌برو یک سرویس اورژانسی نیست و پاسخ‌گویی شبانه‌روزی ندارد. اگر در وضعیت فوری هستی، از شماره‌های زیر کمک بگیر.",
            consentAccept: "متن رضایت‌نامه را خوانده‌ام و می‌پذیرم.",
            consentHint: "تأیید تو همراه نسخهٔ متن و زمان ثبت نگه‌داری می‌شود.",
            consentSignatureLabel: "نام و نام خانوادگی",
            consentSubmit: "تأیید رضایت‌نامه و ادامه",
            consentSavedNote: "رضایت‌نامه ثبت شد. پرسش‌های آمادگی جلسه اول بعد می‌آید — هنوز متصل نیست.",
            baselineTitle: "پرسش‌های آمادگی",
            baselinePageIntro: "کمتر از یک دقیقه زمان می‌برد.",
            baselineSessionNumber: "شماره جلسه: {n}",
            baselineIntro: "در طول هفتهٔ گذشته تا امروز، در موقعیت‌های زیر در زندگی‌تان، میزان رضایت‌تان چگونه بوده است؟ برای هر موقعیت می‌توانید هرجای این خط‌کش را انتخاب کنید. سمت چپ خط نشان‌دهندهٔ کمترین میزان رضایت و سمت راست آن نشان‌دهندهٔ بیشترین میزان رضایت است.",
            baselineNote: "اگر این فرم را برای شخص دیگری که در جلسات درمان شرکت می‌کند پر می‌کنید، لطفاً نظر همان شخص را منتقل کنید.",
            baselineLow: "اصلاً راضی نبودم",
            baselineHigh: "کاملاً راضی بودم",
            baselineItemAria: "{label}؛ از {low} تا {high}",
            baselineItem: {
                ors1: "میزان رضایت از خود",
                ors2: "میزان رضایت از روابط با خانواده و نزدیکان",
                ors3: "میزان رضایت در موقعیت‌های اجتماعی (کار، مدرسه، دوستان)",
                ors4: "میزان رضایت به‌صورت کلی",
            },
            baselineSubmit: "ثبت و ادامه",
            letterTitle: "نامه اختیاری به خود آینده",
            letterIntro: "اختیاری است و فقط برای خود توست.",
            letterSeal: "برای خود آینده‌ات",
            letterLabel: "اگر دوست داری، چند خط برای خودت بنویس",
            letterBody: "می‌توانی بنویسی چرا این مسیر را شروع کردی یا دوست داری در پایان چه چیزی را به خاطر بیاوری. در انتهای مسیر تراپی، وقتی این نامه را دوباره می‌خوانی، به خودت و دستاوردهایی که به‌دست آوردی افتخار خواهی کرد.",
            letterPlaceholder: "من این مسیر را شروع کردم چون…",
            letterPrivacy: "این نامه وارد مچ یا ارزیابی درمانگر نمی‌شود و فقط برای خودت نگه داشته می‌شود.",
            letterSubmit: "ذخیره و رفتن به داشبورد",
            letterSkip: "فعلاً نمی‌نویسم",
            matchSlot: {
                sat1800: "دوشنبه، ۱۸:۳۰",
                sun1630: "سه‌شنبه، ۱۶:۳۰",
                tue1900: "پنجشنبه، ۲۰:۰۰",
            },
            therapist: {
                t1: {
                    name: "دکتر سارا نیک‌پی",
                    title: "روان‌درمانگر",
                    tier: "سطح ۲",
                    reason: "سارا رو برات انتخاب کردیم چون تجربه‌اش در اضطراب و خواب با چیزی که گفتی هم‌خونه است، و نزدیک‌ترین نوبتش هم با عصرهایی که ترجیح دادی جور می‌شود.",
                    evidence: "صلاحیت مستقیم در مسیر بزرگسال، شواهد معتبر برای اضطراب و فرسودگی، سقف شدت متناسب",
                    bio: "بیش از هشت سال تجربه در اضطراب، خواب و فشار شغلی. جلسات فردی با تمرکز روی مهارت‌های روزمره و پیگیری منظم.",
                },
                t2: {
                    name: "دکتر نازنین راد",
                    title: "روان‌شناس بالینی",
                    tier: "سطح ۱",
                    reason: "نازنین رو برات گذاشتیم چون تجربه‌اش در کار و فرسودگی با دغدغه‌ات جور است، و نزدیک‌ترین نوبتش با ترجیح زمانی‌ات هم‌خوانی دارد.",
                    evidence: "صلاحیت مستقیم در مسیر بزرگسال، پوشش چندموضوعی، زمان درخواستی موجود",
                    bio: "کمک می‌کند دغدغه‌های هم‌پوشان روشن شوند و قدم اول واضحی ساخته شود؛ با توجه به حال و فشار کار.",
                },
                t3: {
                    name: "دکتر الهام امینی",
                    title: "روان‌درمانگر",
                    tier: "سطح ۳",
                    reason: "الهام رو پیشنهاد کردیم چون تجربه‌اش با موضوع‌هایی که انتخاب کردی هم‌راستاست، و نزدیک‌ترین نوبتش با بازهٔ زمانی موردعلاقه‌ات جور می‌شود.",
                    evidence: "سقف پیچیدگی بالا، صلاحیت موضوعی مستقیم، ظرفیت شروع سریع",
                    bio: "تجربه کار با مسئله‌های طولانی‌تر که چند حوزه زندگی را درگیر کرده‌اند، با ریتم محتاط و نگاه آگاه به تروما.",
                },
            },
            topicGroup: {
                mood: "حال و خلق",
                anxiety: "اضطراب و نگرانی",
                trauma: "سوگ و تروما",
                relationship: "رابطه و صمیمیت",
                family: "خانواده",
                work: "کار و تحصیل",
                sleep: "خواب",
                substance: "عادت‌های اعتیادی",
                identity: "خودشناسی و هویت",
                body: "بدن و سلامت",
            },
            topic: {
                mood_depression: "افسردگی و بی‌حوصلگی",
                mood_swing: "نوسان خلق",
                mood_anhedonia: "بی‌انگیزگی",
                anxiety_worry: "نگرانی مداوم",
                anxiety_panic: "حملات وحشت",
                anxiety_phobia: "ترس‌های خاص",
                trauma_grief: "سوگ و فقدان",
                trauma_event: "تجربهٔ آسیب‌زا",
                trauma_flashback: "کابوس و یادآوری",
                relationship_conflict: "تعارض با شریک",
                relationship_trust: "اعتماد",
                relationship_loneliness: "تنهایی در رابطه",
                family_conflict: "تعارض خانوادگی",
                family_boundaries: "مرزها",
                family_caregiving: "مراقبت از دیگران",
                work_burnout: "فرسودگی",
                work_stress: "استرس شغلی",
                work_study: "عملکرد تحصیلی",
                sleep_insomnia: "بی‌خوابی",
                sleep_hypersomnia: "خواب بیش از حد",
                sleep_nightmare: "کابوس",
                substance_alcohol: "الکل",
                substance_drugs: "مواد",
                substance_compulsive: "رفتارهای تکراری آسیب‌زا",
                identity_confusion: "سردرگمی هویتی",
                identity_esteem: "عزت‌نفس",
                identity_meaning: "معنا",
                body_pain: "درد مزمن",
                body_image: "تصویر بدن",
                body_illness: "بیماری و سازگاری",
                unsure: "مطمئن نیستم",
            },
            participants: {
                SELF: "خودم",
                COUPLE: "زوج",
                CHILD: "کودک",
                TEEN: "نوجوان",
                PARENT: "مشاورهٔ والد",
                TOGETHER: "نوجوان",
                FAMILY: "خانواده",
            },
        },
        authUi: {
            brand: "روبه‌رو",
            back: "بازگشت",
            tabsLabel: "ورود یا ساخت حساب",
            tabLogin: "ورود",
            tabRegister: "ساخت حساب",
            loginTitle: "ورود به حساب",
            loginLead: "شمارهٔ موبایلی که با آن حساب ساختی را بنویس تا کد ورود برایت فرستاده شود.",
            loginIntro: "فقط همین شماره لازم است. اگر هنوز حساب نساخته‌ای، از ساخت حساب شروع کن.",
            loginTrust: "ورود فقط برای رسیدن کد تأیید است و رضایت درمان را خودکار ثبت نمی‌کند.",
            needAccount: "حساب نداری؟",
            haveAccount: "حساب داری؟",
            registerTitle: "ساخت حساب",
            registerLead: "برای شروع لازم نیست مسئله‌ات از قبل روشن باشد. اول حساب را می‌سازیم تا مسیر از همین‌جا قابل پیگیری بماند.",
            registerTrust: "فقط همان چیزی ثبت می‌شود که برای ساخت حساب و فرستادن کد لازم است. دریافت کد به‌معنای رضایت درمان نیست.",
            familyNote: "روبه‌رو از خانوادهٔ سیمیاروم است.",
            nameLabel: "نام و نام خانوادگی",
            nameHint: "همان نامی که در مسیر درمان با آن خطاب می‌شوی.",
            nameError: "نام را بنویس تا حساب به همان نام ساخته شود.",
            phoneLabel: "شمارهٔ موبایل",
            phoneHint: "کد فقط به همین شماره می‌رسد.",
            phoneError: "شماره را کامل بنویس؛ معمولاً با ۰۹ شروع می‌شود و ۱۱ رقم دارد.",
            phonePlaceholder: "۰۹۱۲۳۴۵۶۷۸۹",
            submitCode: "دریافت کد تأیید",
            formIncomplete: "چند مورد را کامل کن تا بتوانیم ادامه بدهیم.",
            termsUse: "شرایط استفاده",
            termsAnd: "و",
            termsPrivacy: "اطلاعیهٔ حریم خصوصی روبه‌رو",
            termsAfter: "را خوانده‌ام؛ شرایط استفاده را می‌پذیرم و از شیوهٔ پردازش داده‌ها اطلاع دارم.",
            termsError: "برای ساخت حساب، پذیرش شرایط استفاده و حریم خصوصی لازم است.",
            termsDialogTitle: "شرایط استفاده و حریم خصوصی",
            termsDialogBody: "شرایط استفاده و اطلاعیهٔ حریم خصوصی را بخوانید. رضایت آگاهانهٔ درمان پیش از ورود به جلسه جداگانه گرفته می‌شود.",
            termsDialogOk: "متوجه شدم",
            verifyTitle: "تأیید شماره موبایل",
            verifyLead: "کدی که برایت فرستاده شد را وارد کن. لازم نیست عجله کنی؛ شماره همان می‌ماند.",
            phoneSent: "کد به این شماره فرستاده شد",
            changeNumber: "تغییر شماره",
            otpLabel: "کد ۶ رقمی",
            otpHint: "اعتبار کد ۶ رقمی دو دقیقه است. بعد از دو دقیقه، اقدام به دریافت کد جدید کن",
            otpDigit: "رقم {n} از ۶",
            otpInvalid: "این کد درست نیست. دوباره وارد کن، یا اگر منقضی شده کد تازه بخواه.",
            otpSaveRetry: "اطلاعاتت ذخیره نشد. پیش از پایان مهلت کد، با همین کد دوباره تلاش کن.",
            otpIncomplete: "لطفا کد ۶ رقمی را وارد کنید",
            resendIn: "ارسال دوباره تا {seconds} ثانیه دیگر",
            resendReady: "می‌توانی کد تازه بفرستی؛ شماره همان می‌ماند.",
            resend: "ارسال دوباره کد",
            verifyContinue: "تأیید و ادامه",
            verifyTrust: "فقط برای تأیید همین شماره استفاده می‌شود.",
            passwordTitle: "کلمه عبور",
            passwordLead: "برای تکمیل ورود، کلمه عبور و تکرار آن را وارد کنید.",
            passwordSubmit: "ثبت کلمه عبور و ادامه",
            testimonialLabel: "نظر کاربران ناشناس",
            testimonialTitle: "شنیده‌های برخی از مراجعین",
            testimonialTitleBrand: "پلتفرم روبه‌رو",
            afterSessionPrefix: "بعد از",
            afterSessionSuffix: "جلسه",
            afterOne: "یک",
            afterTwo: "دو",
            afterThree: "سه",
            afterFour: "چهار",
            afterSix: "شش",
            anonymous: "ناشناس",
            quoteDot: "نظر {n}",
            quote1: "اولین بار بود که بدون عجله و قضاوت حرف زدم؛ حس امنیت از همان اول پیدا بود.",
            quote2: "شماره‌ام فقط برای ورود ماند و بقیه‌اش آروم و خصوصی جلو رفت.",
            quote3: "به‌جای شلوغی، قدم‌به‌قدم پیش رفتم و هر مرحله واضح بود.",
            quote4: "حس کردم کسی واقعاً گوش می‌دهد؛ نه اینکه فقط فرم پر کنم.",
            quote5: "از ورود تا تأیید شماره ساده بود؛ نگران لو رفتن هویت نبودم.",
        },
        newPreSession: {
            step1: "ثبت نام",
            step2: "انتخاب زمان",
            step3: "اطلاعات شخصی",
            sessionInfo: "اطلاعات جلسه",
            sessionDuration: "مدت جلسه:",
            sessionOrganizer: "برگزارکننده:",
            sessionLocation: "محل برگزاری:",
            selectTime: "انتخاب زمان",
            selectTimezone: "انتخاب محدوده زمانی",
            preferredTime: "چه زمانی رو برای جلسه پیش‌مشاوره ترجیح می‌دی؟",
            closestTime: "نزدیک‌ترین زمان برای گفت‌وگو:",
            calendarAndOtherTimes: "مشاهده تقویم و انتخاب زمان های دیگر",
            timeSlots: "نوبت های موجود",
            noAvailableTimes: "متاسفانه در حال حاضر همه‌ی زمان‌های جلسه ارزیابی پر شدن.",
            timesUpdated: "این زمان به تازگی به روز شده",
            contactViaWhatsApp: "همچنین، در صورت تمایل می‌توانید از طریق واتس‌اپ با کارشناس ما در ارتباط باشید تا اولین تایم خالی را برایتان رزرو کند.",
            reserveOnWhatsApp: "رزرو نوبت در واتس‌اپ",
            confirmAndContinue: "تایید و ادامه",
            chooseDateTime: "یک تاریخ و زمان انتخاب کنید",
            selectTimeError: "لطفا یک زمان را انتخاب کنید",
            recentUpdateError: "این زمان به تازگی به روز شده، لطفا دوباره تلاش کنید.",
            noEmptySlots: "در این بازه زمان خالی وجود ندارد.",
            morning: "صبح",
            afternoon: "عصر",
            evening: "شب",
            withChat: "با یه گپ دوستانه، قدمی برای خودت بردار",
            sessionHelpText: "در این جلسه‌ی 15 دقیقه ای بهت کمک می‌کنیم تا مناسب‌ترین روان‌درمانگر رو انتخاب کنی",
            min: "دقیقه",
            sessionOrganizerText: "کارشناس ارشد ارزیابی و ارجاع",
            sessionLocationText: "آنلاین در Microsoft Teams",
            times: "نوبت",
            notifyNewSlot: "به محض آزاد شدن نوبت جدید، بهت اطلاع میدیم.",
            contactViaWhatsAppText: "همچنین، در صورت تمایل می‌توانید از طریق واتس‌اپ با کارشناس ما در ارتباط باشید تا اولین تایم خالی را برایتان رزرو کند.",
            selectTimezoneError: "لطفا منطقه ی زمانی خود را انتخاب کنید",
            hourText: "ساعت",
            selectTimeErrorText: "لطفا یک زمان را انتخاب کنید",
            somethingWentWrong: "مشکلی رخ داد، دوباره امتحان کنید.",

            name: "نام",
            family: "نام خانوادگی",
            personalInformation: "اطلاعات شخصی",
            howToAddressYou: "دوست داری چطور صدات کنیم؟",
            otherInfoHelp: "اطلاعات دیگه ای که به شناخت بهتر ما کمک میکنه:",
            countryLabel: "کشور",
            howHeardAboutUs: "نحوه آشنایی با ما",
            userFeedback: "۹۳٪ کاربران بعد از جلسه ارزیابی، حس بهتری داشتن",
            chooseDateAndTime: "یک تاریخ و زمان انتخاب کنید",
            submitAndContinue: "ثبت و ادامه",
            fillRequiredFields: "لطفا فیلد های اجباری را پر کنید.",
            slotUnavailable: "این نوبت دیگر در دسترس نیست؛ لطفاً زمان دیگری انتخاب کنید.",
            serverError: "خطا در ارتباط با سرور.",

            successMessage: "آفرین! یک گام مهم برای تغییر برداشتی!",
            supportMessage: "ما همراهت هستیم تا حس خوب رو تجربه کنی.",
            sessionScheduled: "جلسه ی ارزیابی با موفقیت برای شما تنظیم شد",
            evaluationSpecialist: "کارشناس ارزیابی:",
            evaluationSession: "جلسه ارزیابی",
            yourEvaluationSession: "جلسه ارزیابی شما در",
            platform: "پلتفرم",
            heldIn: "برگزار می‌شود",
            downloadLinksMessage: "برای دانلود Teams، می‌تونید از لینک‌های زیر متناسب با تلفن همراه خود، استفاده کنید:",
            messageFirst: "متوجه شدم باید اول به کارشناس پیام بدم.",
            messageForSession: "لطفاً همین حالا اپلیکیشن Teams رو نصب کن و از لینکی که توی واتس‌اپ یا ایمیل برات فرستادیم، به کارشناس ارزیابی پیام بده و خودت رو معرفی کن. زمان جلسه، می‌تونی باهاش تماس بگیری و گفت‌وگو رو شروع کنی...",
            addTo: "افزودن به",
            confirmMessage: "متوجه شدم باید اول به کارشناس پیام بدم.",
            downloadModalTitle: "لینک دانلود پلتفرم Teams",
            sessionInfoView: "مشاهده اطلاعات جلسه",
            timezoneLabel: "محدوده زمانی:",
            changeTimezone: "تغییر",
            timezoneModalTitle: "تغییر محدوده زمانی",
            "stepOneTitle": "انتخاب زمان",
            "stepTwoTitle": "اطلاعات شخصی",
            "stepOneSubtitle": "مرحله بعدی: اطلاعات شخصی",
            "stepTwoSubtitle": "مرحله بعدی: تجربه حال بهتر",
            "promoStepOne": "با یه گپ دوستانه، قدمی برای خودت بردار",
            "promoStepTwo": "۹۳٪ کاربران بعد از جلسه ارزیابی، حس بهتری داشتن",
            downloadLink: "لینک دانلود",
            in: "در"

        },

        "sessionCard": {
            "info_title": "نکات شرکت در جلسه",
            "info_intro": "چند نکته برای اینکه جلسه ارزیابی بهتری داشته باشی:",
            "info_1": "زمان جلسه بر اساس تایم زون خودت تنظیم شده؛ منتظر دیدارت هستیم.",
            "info_2": "قبل از جلسه، کمی با خودت خلوت کن و هدفت از مشاوره رو مرور کن.",
            "info_3": "هر سوالی که داری رو یادداشت کن تا چیزی از قلم نیفته.",
            "info_4": "توی یک فضای آرام و راحت بشین تا با تمرکز بیشتری حرف بزنیم.",
            "info_5": "راستی، خیالت راحت باشه، هر حرفی که به ما می‌گی کاملاً محرمانه و بین خودمون می‌مونه.",
            "countdown": "تا شروع مسیر...",
            "assessment_session": "جلسه ارزیابی",
            "session_link": "لینک برگزاری جلسه",
            "add_to_calendar": "افزودن به گوگل کلندر",
            "info_link_text": "نکاتی که قبل از شرکت در جلسه لازم است بدانید",
            "evaluator_label": "کارشناس ارزیابی:",
            "therapist_label": "مشاور:",
            "show_resume": "نمایش رزومه",
            "skype_link": "لینک اسکایپ مشاور",
            "request_session": "درخواست جلسه",
            "used": "برگزار شده",
            "download_invoice": "دانلود فاکتور",
            "can_enter": "جلسه آماده شروع است",
            "date_fixed": "جلسه زمان‌بندی شده است",
            "reminder_call": "لطفا راس ساعت تنظیم شده، در اسکایپ تماس بگیرید.",
            "presession": "پیش مشاوره",
            "extra_time": "بابت دقایق اضافی",
            "missing_time": "بابت دقایق کسری",
            session: "جلسه",
            therapist_name: "نام تراپیست :",
            download_starting: "دانلود فاکتور جلسه به‌زودی شروع می‌شود",
            scheduled_success: "جلسه شما تنظیم شد.",
            upcoming_sessions: "جلسات پیش رو"

        },


        back: "بازگشت",
        inBasketError: 'این بسته در حال حاضر در سبد خرید شما وجود دارد.',
        termsTitle: "شرایط و قوانین ",
        offlinePayment: {
            title: "شیوه پرداخت خود را انتخاب کنید",
            close: "بستن",
            action: "ثبت پرداخت",
            desc: "هزینه بسته مشاوره خود را به حساب فوق واریز نمایید و پس از پرداخت، اطلاعات پرداخت را در فیلد های زیر وارد کنید، و منتظر تایید بخش مالی و تماس از طرف ما باشید.",
            paymentDate: "تاریخ پرداخت",
            currency: "ارز پرداختی",
            PaymentAmount: "مبلغ پرداختی",
            return: "بازگشت به شیوه های پرداخت",
        },
        next: "بعدی",
        yes: 'بله',
        no: 'خیر',
        today: "امروز",
        emptyStateCart: "سبد خرید شما خالی است!",

        menu: {
            factor: "تاریخچه خرید",
            dashboard: 'داشبورد',
            messenger: 'پیام‌ها',
            sessions: 'تاریخچه جلسات',
            tasks: 'تسک‌ها',
            packages: 'خرید جلسه',
            profile: 'پروفایل',
            file: 'پرونده',
            setting: " اطلاعات کاربری",
            wallet: "کیف‌پول",
            close: "بستن",
            finance: "امور‌مالی",
            session_request: "درخواست جلسه",
            tests: 'تست‌ها',
            invitees: 'دعوت‌ها',
            feedback: "بازخورد",
            exit: "خروج",
        },
        cycle: "دوره",
        section_title: {
            gift_code: "ورود کد هدیه",
            wallet: "تراکنش های کیف پول",
            personalInfo: "اطلاعات شخصی شما",
            moreInfo: "سایر اطلاعات",
            cycles: "دوره های شما",
            location: 'اطلاعات مکانی',
            password: 'رمز عبور'
        },
        cycles: {
            'Individual': 'درمان فردی',
            'Couple': 'زوج درمانی',
            'Family': ' مشاوره خانواده',
            'Child and Adolescent': 'کودک و نوجوان',
            'Sex Therapy': 'مشاوره جنسی',
            'Psychiatry': 'روان پزشکی',
            'Children and adolescents, growth, parenting': 'کودک و نوجوان',
            'Couple therapy': 'زوج درمانی',
            'Sex therapy': 'زوج درمانی'
        },
        clientStepper: {
            dataTime: {
                hint: "لطفا همه زمانهای مناسب برای برگزاری جلسه آینده (به زمان محلی ) را وارد نمایید، هرچه تعداد بیشتری زمان وارد کنید، امکان برگزاری سریع تر جلسه را به تیم پشتیبانی میدهید.",
                typeCalender: [
                    'تقویم میلادی',
                    '              تقویم شمسی'
                ],
                periods: [
                    'صبح',
                    'ظهر',
                    'عصر',
                    'شب'
                ],
                changeTimeZone: "تغییر زمان محلی",
                changeTimeZoneHint: "با تغییر زمان محلی، باید زمانهای مناسب برگزاری جلسه را مجددا وارد کنید",
                tables: {
                    header: [
                        'تاریخ',
                        'ساعت ها',
                    ],
                }
            }
        },
        education: {
            NoDegree: 'فاقد مدرک',
            Diploma: 'دیپلم',
            AssociateDegree: 'فوق دیپلم',
            Bachelor: 'لیسانس',
            MA: 'فوق لیسانس',
            PHD: 'دکتری',
        },
        new_onboarding: {
            idicatior: {
                info: " تکمیل اطلاعات ",
                location: " پیش مشاوره",
                cycle: "علت مراجعه",
                final: 'ثبت'
            },
            new_indicator: {
                info: "مشخصات",
                title_session: "موضوع مشاوره",
                register: "ثبت نام"
            },
            next: "مرحله بعد",
            prev: "مرحله قبل",
            stepOne: {
                text: [
                    "خیلی خوشحالیم در کنارتیم.بعد از تکمیل اطلاعات جلسه‌ شما تنظیم می‌شه.",
                    ' جلسات به صورت آنلاین و با',
                    'Skype',
                    ' برگزار می‌شه.',
                    'راستی قبل از شروع جلسات اصلی، یک پیش مشاوره هم داری.'

                ],
                action: "تکمیل اطلاعات"
            },
            stepTwo: {
                title: "در صورت تمایل می‌توانید از نام و نام‌خانوادگی مستعار خود استفاده کنید.",
                name: "نام",
                family: "نام خانوادگی ",
                tell: "شماره واتساپ و تلگرام",
                birthDay: "تاریخ تولد"
            },
            stepThree: {
                title: " اطلاعات مکانی تنها برای تطبیق بهتر زمان برگزاری جلسات شما دریافت می‌شود. ",
                country: "کشور",
                city: 'شهر',
                tz: 'محدوده زمانی',
                kenway: 'نحوه آشنایی با ما'
            },
            stepFour: {
                title: " لطفا موضوع مشاوره خود را انتخاب کنید.",
                category: "موضوع مراجعه",
                desc: "شرح مساله"
            },
        },
        knway: {
            youtube: "یوتیوب",
            google: "جست و جو در  گوگل",
            twitter: "توییتر",
            instagram: "اینستاگرام",
            podcast: "پادکست",
            bannerads: "تبلیغات بنری",
            friends: "معرفی دوستان و آشنایان",
            site: "سایت",
            other: "موارد دیگر",
        },
        giftCode: {
            title: "افزودن کد هدیه",
            placeholder: "کد هدیه"
        },
        notifications: {
            needBuy: {
                title: "بسته‌ای وجود ندارد",
                description: "برای رزرو جلسه جدید بسته تهیه کنید",
                action: "تهیه بسته"
            },
            reserveSession: {
                title: "رزرو وقت جلسه",
                description: "جلسه جدید برای خود رزرو کنید",
                action: "رزرو وقت جلسه"
            }
        },
        payableFactor: {
            title: "شما یک فاکتور قابل پرداخت به مبلغ",
            mobTitle: "شما یک فاکتور در انتظار پرداخت دارید!",
            have: "دارید",
            details: 'جزئیات'
        },
        notify: {
            completeProfile: "لطفا فرایند ثبت‌نام خود تکمیل کنید",
            giftCodeSubmit: "کد هدیه اعمال شد",
            couponCodeSubmit: "کد تخفیف اعمال شد",
            deleteCouponCode: "کد تخفیف با موفقیت از روی سبد خرید شما حذف شد",
            notFoundCoupon: "کد تخفیف پیدا نشد",
            enterCouponCode: "کد تخفیف را وارد کنید",
            failed: "فرایند با مشکل مواجه شد. دوباره تلاش کنید",
            passwordChange: "رمز عبور تغیر کرد",
        },
        sidebar: {
            calender: {
                title: "جلسات تنظیم شده",
            },
            chat: "چت با پشتیبان اختصاصی",
            localClock: "وقت محلی",
        },
        layout: {
            footer: {
                charge: "افزایش اعتبار",
                reserve: "رزرو وقت",
                wallet: "کیف‌پول"
            }
        },
        months: {
            Farvardin: 'فروردین',
            farvardin: 'فروردین',
            Ordibehesht: 'اردیبهشت',
            ordibehesht: 'اردیبهشت',
            Khordaad: "خرداد",
            khordad: "خرداد",
            Tir: 'تیر',
            tir: 'تیر',
            Amordaad: "مرداد",
            mordard: "مرداد",
            Shahrivar: "شهریور",
            sharivar: "شهریور",
            Mehr: "مهر",
            mehr: "مهر",
            Aabaan: "آبان",
            aban: "آبان",
            Aazar: "آذر",
            azar: "آذر",
            Dey: "دی",
            day: "دی",
            Bahman: "بهمن",
            bahman: "بهمن",
            Esfand: "اسفند",
            esfand: "اسفند",
        },
        days: {
            Saturday: "شنبه",
            Sunday: "یکشنبه",
            Monday: "دوشنبه",
            Tuesday: "سه شنبه",
            Wednesday: "چهار شنبه",
            Thursday: "پنج شنبه",
            Friday: "جمعه",
        },
        mobileMenu: {
            dashboard: "داشبورد",
            tasks: "تسک‌ها",
            sessions: "جلسات",
            packages: "بسته‌ها",
            profile: "پرونده‌",
            setting: "تنظیمات",
            wallet: "تراکنش‌ها/کیف پول",
            account_manager: "پشتیبان اختصاصی",
            chat: "پیام‌رسان",
            terms: "حریم خصوصی",
            about: "درباره ما",
            logout: "خروج از حساب"
        },
        sessions: {
            empty: "وقت جلسه‌ تنظیم نشده",
            action: "رزرو‌وقت",
            loading: "درحال دریافت اطلاعات",
            title: "جلسات شما",
            session_request: "درخواست جلسه جدید",
            modal: {
                title: " جلسه مشاوره شما با موفقیت ثبت شد ",
                subtitle: " کارشناسان ما تا 48 ساعت اینده با شما تماس خواهند گرفت ",
                action: "متوجه شدم"
            }
        },
        packages: {
            perSession: "برای هر جلسه",
            buy: "خرید بسته",
        },
        tasks: {
            empty: {
                title: "تمرینی و آزمونی برای شما ارسال نشده است!",
                text: "بر حسب نیاز تمرین و آزمون برای شما ارسال می‌شود"
            }
        },

        resetPassword: {
            title: "تغییر رمز عبور",

        },
        profileView: {
            name_hint: 'می‌توانید از نام مستعار استفاده کنید',
            en_name: 'نام انگلیسی',
            en_family: 'نام خانوادگی انگلیسی',
            email: "ایمیل",
            contact: "اطلاعات تماس",
            username: "کاربر",
            job: "شغل",
            sex: "جنسیت",
            moreInfo: "سایر اطلاعات",
            non: "گفته نشده",
            child: "فرزند",
            noKid: "فرزند ندارم",
            age: "سن",
            localTime: "زمان‌محلی",
            moreInfoModal: {
                countryLabel: "کجا هستید؟",
                localTime: "زمان محلی خود را انتخاب کنید",
                childCount: "چند فرزند دارید؟",
                noChild: "فرزند ندارم"
            },
            jobModal: {
                title: "وضعیت شغلی شما چیست؟",
            },
            birthDate: "تاریخ‌تولد",
            name: "نام",
            family: "نام خانوادگی",
            jobStatus: "وضعیت شغلی",
            tell: "شماره واتساپ و تلگرام",
            childOrder: "چندمین فرزند خانواده",
            maritalStatus: "وضعیت تاهل",
            childCount: "تعداد فرزند",
            degree: 'مدرک تحصیلی'
        },
        userOnboarding: {
            specifications: "مشخصات",
            childOrder: "فرزند چندم خانواده هستید؟",
            maritalStatus: "وضعیت تاهل شما چگونه است؟",
            childCount: "چند فرزند دارید؟",
            jobStatus: "وضعیت شغلی شما چگونه است؟",
            country: "کشور محل اقامت",
            tz: "منطقه‌زمانی",
            city: "شهر محل اقامت...",
            notify: {
                done: "ویرایش پروفایل انجام شد",
                tz: "منظقه زمانی ویرایش شد"
            }
        },
        medicalView: {
            history: "قبلا بیماری خاصی داشتید؟",
            servicesHistory: "تا کنون از این خدمات استفاده کرده‌اید؟",
            yes: "بلی",
            no: "خیر",
            psychology: "روانشناسی",
            psychiatry: "روانپزشکی",
            mentalHospital: "بیمارستان روانی",
            HistorySuicide: "تا کنون سابقه خودکشی داشته‌اید؟",
            suicide: {
                yes: 'بله',
                yesDesc: "سابقه‌دارم",
                no: "خیر",
                noDesc: "سابقه ندارم"
            }
        },
        messenger: {
            fileToolTip: "ارسال فایل",
            imgToolTip: "ارسال عکس",
            messagePlaceholder: "یک پیام بنویسید",
            emptyState: "هنوز چیزی نوشته نشده"
        },
        jobs: {
            employed: 'شاغل',
            unemployed: 'فاقد‌ شغل',
            housewife: 'خانه‌دار',
            student: 'دانشجو',
            other: 'دیگر'
        },
        country: "کشور",
        city: "شهر",
        edit: "ذخیره تغییرات",
        knwayLabel: 'نحوه آشنایی با ما',
        tz: "زمان محلی",
        noTz: "بدون‌زمان",
        child: "فرزند",
        marital: "تاهل",
        reserve_session: {
            segment_control: {
                title: "موضوع",
                time: "زمان",
                reserve: "رزرو",
            }
        },
        segments: {
            reserve: 'رزرو وقت',
            packages: "بسته‌ها",
            profile: "اطلاعات عمومی",
            medicalInfo: "اطلاعات پزشکی",
            factors: "فاکتور",
            wallet: "کیف‌پول"
        },
        session_wallet: {
            empty: {
                title: "!اعتبار یا بسته برای رزرو وقت جلسه وجود ندارد",
                action: "افزایش اعتبار"
            },
            general_session: "جلسات عمومی",
            therapy_session: "جلسه روانشناسی",
            action: "رزرو وقت"
        },
        select_cycle: {
            titleSection: "موضوع مشاوره",
            title: "موضوع",
            package: "بسته‌روانشناسی",
            duration: "45 دقیقه",
            description: "شرح مسئله",
            selectCycle: "انتخاب از موضوع مشاوره‌ی دیگر",
            action: "تایید و ادامه",
            notify: {
                selectCycle: "لطفا یک دوره درمان انتخاب کنید",
                addit: "دوره‌درمان اضافه شد"
            },
            modal: {
                modalTitle: "موضوع مشاوره",
                description: "موضوع خود را انتخاب کنید",
                select_title: "موضوع جدید خود را انتخاب کنید",
                add_cycle: "افزودن موضوع جدید",
                add_description: "افزودن توضیحات",
            },
            cycle_heading: {
                Individual: {
                    title: "فردی",
                    description: "برای خودم"
                },
                Couple: {
                    title: "روابط و زوج",
                    description: "برای من و همسرم"
                },
                family: {
                    title: "خانواده",
                    description: "برای خانواده‌ام"
                },
                kid: {
                    title: "کودک و نوجوان",
                    description: "برای فرزندم"
                },
                sex: {
                    title: "سکس تراپی",
                    description: "برای من و شریک عاطفی"
                },
                Psychiatry: {
                    title: "روانپزشکی",
                    description: "برای تجویز دارو"
                }
            }
        },
        download: "دانلود",
        description: "توضیحات",
        select_time: {
            title: "زمان‌های ترجیحی خود را انتخاب کنید",
            description: "چه ساعاتی در طول هفته برای برگزاری جلسه شما مناسب است؟",
            continue: "تایید و ادامه",
        },
        accept_request: {
            title: "درخواست شما ثبت شد",
            newUser: "بعد از هماهنگی با تراپیست، وقت شما رزرو خواهد شد.\n" +
                "              در ساعت آینده تیم پشتیبانی ما یک جلسه کوتاه برای شما تنظیم می‌کند. طی این جلسه بهترین تراپیست برای شما\n" +
                "مشخص\n" +
                "و پیشنهاد می‌شود",
            oldUser: "بعد از هماهنگی با تراپیست، وقت شما رزرو خواهد شد"
        },
        factors: {
            datatable: {
                date: "تاریخ",
                type: "نوع",
                state: "وضعیت",
                amount: "مبلغ"
            },
            factorState: {
                paid: "پرداخت شده",
                submit: "پیش‌فاکتور",
                canPay: "قابل پرداخت",
                partially_paid: "پیش‌پرداخت"
            },
            paymentState: {
                awaiting: "در انتظار بازگشت از بانک",
                paid: 'پرداخت شده',
                wallet_pending: "",
                failed: "لغو شده",
                debit_checkout: "پرداخت نشده",
                rejected: "لفو شده",
                pending: "درانتظار تایید"
            },
            paymentType: {
                wallet_debt_settlement: "بابت بدهی کیف پول",
                online: "آنلاین",
                wallet_payment: "کیف‌پول",
                offline: "آفلاین"
            }
        },
        product: "محصول",
        details: "جزئیات",
        factor: "فاکتور",
        close: "بستن",
        packageFactor: " : بسته‌های این فاکتور  ",
        paymentDetail: " : جزئیات پرداخت ",

        //ssssss

        account: 'داشبورد',
        nextLabel: 'بعدی',
        Paid: 'پرداخت شده',
        return: "بازگشت",
        confirm: 'تایید',
        confirmAndContinue: "تایید و ادامه",
        assignconsult: {
            successMessage: "پرداخت با موفقیت انجام شد",
            failedMessage: "پرداخت با شکست مواجه شد",
            code: "شماره پیگیری",
            sorry: 'متأسفیم',
            oldUserTexts: [
                'در سیستم پرداخت، یک مشکل فنی رخ داده است.',
                'نگران نباشید، برای تکمیل فرایند رزرو، به تیم پشتیبانی از طریق واتس اپ یا تلگرام پیام دهید.',
            ],
            newUserTexts: [
                'در سیستم پرداخت، یک مشکل فنی رخ داده است.',
                'نگران نباشید، برای تکمیل فرایند رزرو و هماهنگی جهت برگزاری جلسه پیش مشاوره به تیم پشتیبانی ما از طریق واتس اپ یا تلگرام پیام دهید.',
            ]
        },
        sentEmailLink: 'ایمیل حامل لینک برای شما ارسال شد',
        dontHaveAccount: 'حساب کاربری ندارید؟',
        phoneLabel: 'تلفن همراه',
        matchMaking: {
            pleaseAnswer: 'لطفا به سوال پاسخ دهید.',
            lastMessage: [
                'تقریبا',
                ' از افرادی که پاسخ‌هایی مشابه شما داده‌اند، به موارد زیر در جلسه پیش مشاوره و جلسات ابتدایی تراپی (مشاوره روانشناسی) اشاره کرده‌اند:\n',
                ' در ادامه، شما می‌توانید پس از تکمیل فرآیند ثبت نام، در جلسه پیش مشاوره، تمامی شرایط و نیازهای خود را با متخصص روانشناسی ما در میان بگذارید.',
            ],
            chooseFieldQuestion: 'موضوع مشاوره خود را مشخص کنید',
            chooseGenderQuestion: 'ترجیح میدهم جنسیت تراپیست من …… باشد',
            buttons: {
                submit: 'سوال بعد',
                firstStep: 'اولین قدم'
            },
            registerForm: {
                registerByGoogle: 'ثبت نام با حساب گوگل',
                registerByEmail: 'ثبت نام با ایمیل'
            },
            primaryResult: {
                1: [
                    '1. به‌ هم ریختگی در روتین خواب و اشتها',
                    '2. احساس خستگی ناشی از مداومت استرس',
                    '3. مشکل در مدیریت هیجان به ویژه خشم',
                    '4.  ابتلا به مشکلات قلبی-عروقی',
                ],
                2: [
                    '1. اختلال در مدیریت هیجان به ویژه خشم',
                    '2. افزایش نشانه های اضطرابی',
                    '3. چالش در ارتباطات بین فردی و زوجی',
                ],
                3: [
                    '1. مشکل در توجه و تمرکز',
                    '2. اهمال کاری',
                    '3. اختلالات یادگیری و حافظه',
                    '4. چالش در ارتباطات بین فردی'
                ],
                4: [
                    '1. اختلالات اضطرابی',
                    '2. اهمال‌کاری',
                    '3. مشکل در توجه و تمرکز',
                ],
                5: [
                    '1. اختلالات اضطرابی',
                    '2. چالش در ارتباطات بین فردی',
                    '3. کمال‌گرایی',
                    '4. اهمکال کاری',
                ],
                6: [
                    '1.تجربه خلق پایین و افسردگی',
                    '2. مشکل در مدیریت موقعیت‌های استرس‌زا',
                    '3. چالش در هویت و دلبستگی',
                ],
                7: [
                    '1. مواجهه با مشکلات در روابط عاطفی',
                    '2. افزایش نشانه های اضطرابی',
                ],
                8: [
                    '1. افزایش نشانه های اضطرابی',
                    '2. نشخوار و وسواس‌های فکری',
                    '3. احتمال چالش با اطرافیان و خانواده',
                ],
                9: [
                    '1. تجربه خلق پایین و افسردگی',
                    '2. مشکلات تفاوت بین فرهنگی و بین نسلی',
                    '3. مشکلات جنسی',
                ],
                10: [
                    '1. اختلالات اضطرابی',
                    '2. نشخوار و وسواس‌های فکری',
                    '3. مشکلات زوجی',
                ],
                11: [
                    '1.ایجاد مشکل در کنترل هیجان',
                    '2. سوء مصرف مواد',
                    '3. چالش در روابط بین فردی',
                ],
                12: [
                    '1. بروز مشکل در روابط بین فردی',
                    '2. مشکلات شغلی-تحصیلی',
                    '3.  اهمال‌کاری',
                    '4. افکار خودکشی',
                ],
                13: [
                    '1. ابتلا به اختلالات افسردگی',
                    '2. نشخوار فکری و نگرانی‌ها در مورد تغییرات فیزیکی بدن',
                    '3. اختلالات اضطرابی',
                ],
                14: [
                    '1. اختلالات اضطرابی از جمله حمله پنیک',
                    '2. خلق پایین و افسردگی',
                    '3. چالش در روابط بین فردی',
                    '4. مشکلات در حوزه شغلی-تحصیلی',
                ]
            },
            profile: 'پروفایل',
            selectTherapist: 'انتخاب تراپیست',
            register: 'ثبت‌نام',
            description: 'توضیحات',
            hintTitle: 'سپاس از همراهی شما در پاسخگویی به سوالات',
            hintDescription: [
                'اطلاعات تکمیلی در جلسه پیش مشاوره و جلسات درمانی به صورت کامل دریافت و بررسی خواهند شد',
                'از شما می‌خواهیم به چند سوال عمومی نیز برای کمک بیشتر به تحلیل داده‌ها، پاسخ دهید.'
            ],
        },

        signUpLabel: "ثبت‌نام",
        accountRecoveryCaption: 'برای بازیابی حساب کاربری، ایمیل خود را وارد کنید.',
        accountRecovery: 'بازیابی حساب کاربری',
        emailErrRequired: 'پر کردن  فیلد ایمیل الزامی است',
        passwordLabel: 'کلمه عبور',
        passwordConfirmationLabel: 'تکرار کلمه عبور',
        emailLabel: 'ایمیل',
        register: "ثبت نام",
        loginComponent: {
            preparing: "درحال‌ آماده سازی",

            whatsAppLogin: "ورود با واتس‌اپ",
            googleLogin: "ورود با گوگل",
            googleSignIn: "ثبت نام با گوگل",
            telegramUnavailable: "اتصال حساب تلگرام فعلاً در دسترس نیست. ورود به حساب امکان‌پذیر است.",
            changingEmail: "تغییر ایمیل",
            enterVerificationCode: "کد تایید را وارد کنید",
            or: "یا",
            verificationCodeSent: " کد تایید برای شما در",
            whatsapp: "واتس‌اپ",
            sent: "ارسال شد",
            resendingCode: "ارسال مجدد کد",
            testLogin: "برای دریافت تفسیر تست خود با حساب کاربری خود وارد شوید یا ثبت‌نام کنید",
            login: "ورود",
            title: " خوش آمدید",
            subTitle: "برای خرید لطفا ابتدا وارد شوید",
            form: [
                'آدرس ایمیل',
                'کلمه عبور'
            ],
            registerLabel: "ثبت نام نکرده اید؟",
            noAccount: "حساب کاربری ندارم!",
            haveAccount: "حساب کاربری دارم!",
            registerAction: "ثبت نام کنید",
            resetPassword: "رمز عبور را فراموش کرده اید؟",
            forgetPassword: {
                title: "بازیابی حساب کاربری",
                subTitle: "لینک ساخت کلمه عبور جدید به ایمیل شما ارسال خواهد شد.",
                action: "دریافت‌لینک",
                loginLabel: "حساب کاربری دارید؟",
                loginAction: "وارد شوید",
            },
            checkEmail: "ایمیل خود را چک کنید",
            postOtpPasswordHint: "برای تکمیل ورود، کلمه عبور و تکرار آن را وارد کنید.",
            postOtpPasswordSubmit: "ثبت کلمه عبور و ادامه",
        },
        onboarding: {
            country: {
                title: "لطفا کشور محل زندگی خود را انتخاب کنید",
                subTitle: "این سوال به جهت پیشنهاد زمان‌های مناسب برای برگزاری جلسات شما پرسیده می‌شود."
            },
            startStep: {
                title: "مشاوره روانشناسی ما چگونه کار می کند؟",
                reasons: [
                    '1 - یک نام کاربری و رمز عبور خصوصی برای دسترسی به سیستم ایجاد کنید.',
                    '2 - اطلاعات پرداخت را وارد و مبلغ پکیج مورد نظر را پرداخت نمایید.',
                    '3 - یک جلسه پیش مشاوره برای شناخت بیشتر و تعیین درمانگر شما برگزار می‌شود.',
                    '4 - بر اساس نتیجه جلسه پیش مشاوره، ما شما را با یک درمانگر متخصص هماهنگ می‌کنیم.'
                ]
            },
            stepTwo: {
                title: "چه چیزی باعث شد که به فکر مشاوره باشید؟",
                why: {
                    1: "احساس افسردگی کرده ام",
                    2: "احساس اضطراب یا مستاصل شدن (پریشان حالی و کلافه بودن) می کنم",
                    3: "خلق و خوی من با عملکرد شغلی / درسی ام تداخل دارد",
                    4: "با ایجاد یا حفظ روابط چالش دارم",
                    5: "نمی توانم هدف و معنایی در زندگی ام پیدا کنم",
                    6: "غصه میخورم",
                    7: "ضربه (چالش) روحی را تجربه کرده ام",
                    8: "باید درباره یک چالش یا موقعیت خاص صحبت کنم",
                    9: "می خواهم اعتماد به نفسم را تقویت کرده یا بدست بیاورم",
                    10: "می خواهم خودم را بهبود بخشم اما نمی دانم از کجا شروع کنم",
                    11: "استفاده از خدمات مشاوره به من توصیه شده است (دوست، خانواده، دکتر)",
                    12: "سایر",
                }
            },
            stepThree: {
                title: "انتظارات شما از درمانگرتان چیست؟ درمانگری که…",
                exception: {
                    1: ' شنونده خوبی باشد',
                    2: ' تجربه سکونت در خارج از کشور را داشته باشد',
                    3: ' گذشته من را کاوش می کند',
                    4: ' به من مهارت های جدیدی می آموزد',
                    5: ' باورهای من را به چالش می کشد',
                    6: ' به من تکلیف می دهد',
                    7: ' مرا برای تعیین اهداف راهنمایی می کند',
                    8: ' فعالانه با من در ارتباط بوده و مسائل من را چک می کند',
                    9: ' سایر',
                    10: ' من نمی دانم',
                }
            },
            hint: {
                title: "مشاوره روانشناسی و طول دروه درمان من چند جلسه خواهد بود؟",
                hints: [
                    'حداقل زمانی که شما نیاز دارید تا از اثر مشاوره در زندگی خود آگاه شوید، 5 جلسه است.',
                    'طبق آمار جهانی،',
                    'حداقل میانگین دوره درمان',
                    'روانشناسی 13 تا 16 جلسه بوده،',
                    'اما تعداد این جلسات براساس نوع روش درمان و میزان شدت مسائل ',
                    'شما می‌تواند بیشتر یا کمتر باشد.',
                    '                      بخاطر داشته باشید مسائلی که مدت زیادی شما را دچار چالش کرده است نیازمند زمان جهت بهبود می‌باشد.'
                ]
            }
        },
        addCycleModal: {
            add: "افزودن",
            title: "موضوع مشاوره و شرح مشکل خود را وارد نمایید",
            countChar: "حداقل 20 کاراکتر",
            categories: [
                'فردی',
                'روابط و زوج',
                'خانواده',
                'کودک و نوجوان',
                'سکس تراپی',
                'روانپزشکی',
            ],
            categorySession: "موضوع مشاوره",
            problemDescription: "شرح مساله",
            problemDescriptionPlaceHolder: "به اختصار موضوع مورد نظر خود را برای مشاور شرح دهید.",
            selectCycle: {
                title: "انتخاب دوره مشاوره",
                selecting: "لطفا دوره مشاوره خود را انتخاب نمایید",
                confirm: "تایید درخواست جلسه",
                action: "افزودن دوره جدید",
                issueSession: "موضوع دوره :",
                totalSession: "تعداد کل جلسات",
                request: "درخواست جلسه",
            },
        },
        loginLabel: 'ورود',
        emailErrValid: 'یک ایمیل معتبر وارد کنید',
        passwordErrRequired: 'پر کردن  فیلد کلمه عبور الزامی است',
        onBoarding: {

            titleLogin: "ثبت نام کاربر جدید",
            continue: 'ادامه',
            next: "بعدی",
            prev: "قبلی",
            selectedPackage: 'انتخاب بسته',
            stepOne: {
                title: 'در مسیر یک حال بهتر',
                subTitle: "پاسخ به چند سوال کوتاه جهت آشنایی بیشتر مشاور با شما",
                start: "شروع",
                skip: "تمایلی  ندارم"
            },
            stepTwo: {
                title: "موضوع مشاوره خود را انتخاب کنید",
                subtitle: "در صورت نیاز می توانید اطلاعات خود را در پنل کاربری تغییر دهید.",
                cycles: [
                    'فردی',
                    ' رابطه و زوج',
                    'کودک و نوجوان',
                    'خانواده',
                    'روانپزشکی',
                    'سکس تراپی'
                ],
            },
            stepThree: {
                title: "جنسیت خود را انتخاب کنید",
                gender: [
                    'مرد',
                    'زن',
                    'موارد دیگر'
                ]
            },
            stepFour: {
                title: 'چند سال دارید؟',
                agePeriods: [
                    'کمتر از 18',
                    'بیشتر از 60'
                ]
            },
            history: "آیا سابقه استفاده از خدمات روانشناسی را دارید؟ ",
            stepFive: {
                title: "وضعیت تاهل شما چگونه است؟",
                relationships: [
                    'مجرد',
                    'متاهل',
                    'شریک عاطفی',
                    'موارد دیگر'
                ]
            },
            loginWithDifferentWay: "ورود یا ثبت نام با روش دیگر",
            loginItems: [
                'حساب گوگل',
                'حساب فیسبوک'
            ],
            terms: "شرایط و قوانین ",
            wizardLogin: [
                'ثبت نام',
                'ورود'
            ],
            wizardStepIndicator: {
                info: "مشخصات",
                register: "ثبت نام",
                pay: "پرداخت",
                selectConsulter: "انتخاب زمان و مشاور"
            },
            whoSelectDoctor: {
                title: "چگونه تراپیست شما انتخاب شود؟",
                items: [
                    'خودم انتخاب میکنم',
                    'متخصصان ما انتخاب کنند',
                ]
            },
            selectDoctor: {
                searchPlaceHolder: 'جستجوی نام مشاور',
                labelCategory: 'تخصص',
                all: 'همه'
            },
            selectTime: {
                title: "زمان‌های ترجیحی خود را انتخاب کنید",
                subTitle: " چه ساعاتی در طول هفته برای برگزاری جلسه شما مناسب است؟"
            },
            testimonial: {
                sweden: {
                    title: 'سیما از سوئد',
                    desc: "من خیلی خوشحالم که پیداتون کردم و تونستم تو شرایطی که به کسی نمی‌تونستم اعتماد کنم، از شما و آقای دکتر کمک گرفتم. الان حالم خیلی بهتره و واقعا ازتون ممنونم."
                },
                usa: {
                    title: 'ابراهیم از آمریکا',
                    desc: "دید من به زندگی بعد از تراپیم عوض شد. خیلی چیزهایی که زجر آور بودن دیگه نیستن و این به‌خاطر نگاهی بود که تو تراپی به دست آوردم. ممنون از شما و مشاوراتون."
                },
                germany: {
                    title: 'دلناز از آلمان',
                    desc: "    من تجربه تراپی بد رو داشتم و می‌دونم چقدر می‌تونه به آدم بیشتر آسیب بزنه و از شروع مجدد می‌ترسیدم. تا این که دوستم شما رو معرفی کرد و نمی‌دونید چقدر ممنونتونم به خاطر حال خوب الانم."

                },
                usaTwo: {
                    title: 'شادی از آمریکا',
                    desc: "خیلی ممنون از سیمیاروم و مشاوراش. من واقعا این سیستم رو هرجایی ندیدم که وقتی ببینن با مشاورت به نتیجه دلخواه نمی‌رسی، مشاور رو تغییر بدن تا بهترین نتیجه رو بگیری‌."
                },
                canada: {
                    title: 'محمدحسین از کانادا',
                    desc: '    پلتفرمی نبود که من رزومه مشاوراش رو واسه پیدا کردن مشاور نگشته باشم. تا این که به سایت شما رسیدم و دیدم هر مشاوری که هست هم رزومه معتبری داره، هم تجربه‌ زیاد. به خاطر همین به شما اعتماد کردم و از این اعتماد هم راضی‌ام.'
                },
                netherlands: {
                    title: "آرتمیس از هلند",
                    desc: '    من واقعا مشکل خاصی نداشتم؛ اما بعد مهاجرتم احساس می‌کردم یه چیزی کمه و اعتماد به نفسم پایین اومده بود. به خاطر همین دنبال یه تراپیست خوب گشتم و سر از سایت شما درآوردم.  الان همه چی عالیه و من از مشاورم و سیمیاروم خیلی ممنونم.'
                }
            },
            priceBox: {
                select: 'انتخاب مدل مشاوره',
                title: ' پکیج مورد نظرتان را انتخاب نمایید ',
                subtitle: 'شما می‌توانید مناسب ترین مدل مشاوره را برای مشاوره انتخاب کنید',
                labelPopular: 'محبوب ترین',
                labelBestPrice: 'بهترین‌قیمت',
                hint: ' ضمانت بی‌قید و شرط بازگشت وجه درصورت عدم رضایت',
                emptyBasket: 'هنوز بسته‌ای انتخاب نکرده‌اید'

            }
        },
        signupComponent: {
            title: "ایجاد حساب",
            subTitle: "لطفا اطلاعات زیر را وارد نمایید",
            form: [
                "تلفن همراه (با کد کشور)"
            ],
            terms: "شرایط و ضوابط",
            accept: 'را خوانده و میپذیرم.',
            inviter: "کد معرف شما :",
            action: "ثبت نام",
            loginLabel: " حساب کاربری دارید؟",
            loginAction: "وارد شوید",
            phone: "تلفن همراه",
            registerAction: "ثبت‌نام",
            acceptTerms: 'شرایط و قوانین را قبول دارم',
            loginActionOnBoarding: "ورود با حساب کاربری",
        },
        logout: 'خروج از حساب کاربری',
        showAccount: "حساب کاربری",
        $vuetify: {
            noDataText: 'مورد وجود ندارد'
        },
        gender: {
            female: 'زن',
            FEMALE: 'زن',
            male: 'مرد',
            MALE: 'مرد',
            OTHER: 'موارد دیگر',
            other: 'موارد دیگر',
        },
        relationships: {
            single: 'مجرد',
            married: 'متاهل',
            inrel: 'شریک عاطفی',
            other: 'موارد دیگر',
            divorced: "طلاق‌گرفته",
            partner: 'موارد دیگر',
            rel: "شریک عاطفی",
            otherState: "موارد دیگر"
        },
        errors: {
            fillIsRequired: 'پر کردن این فیلد الزامی است',
            passwordLengthError: "رمز عبور باید بیش از ۸ کارکتر باشد",
            emailErrValid: "ایمیل را با ساختار مناسب وارد کنید",
            phoneNumberError: "شماره تماس را با ساختار درست وارد کنید."
        },
        Notify: {
            without_cycle: "برای ثبت درخواست جلسه، ابتدا اطلاعات خود را کامل کنید",
            session_request: "درخواست جلسه با موفقیت ثبت شد",
            medicalEdit: "با موفقیت اضافه شد",
            requiredChat: "لطفا متن پیام خود را وارد کنید",
            loginSuccess: "با موفقیت وارد شدید",
            loginFailed: "نام کاربری یا کلمه عبور شما اشتباه می باشد",
            doctorTime: [
                'شما نمیتوانید بیشتر از',
                ' ساعت انتخاب کنید',
            ],
            successfully: "با موفقیت به ثبت رسید",
            successfullyV2: "عملیات با موفقیت انجام شد.",
            successfullyWithRedirect: "با موفقیت به ثبت رسید، به صفحه داشبورد منتقل میشوید",
            successfullyRequest: 'درخواست شما با موفقیت به ثبت رسید',
            successDateTime: "اطلاعات شما با موفقیت ثبت شد، تا 24 ساعت آینده با شما تماس خواهیم گرفت",
            changePassword: "کلمه عبور با موفقیت تغییر یافت",
            packageComponent: "لطفا حداقل یک ارز پرداختی برای بسته انتخاب نمایید",
            changeStatus: 'لطفا یکی از زمانهای مشاور را انتخاب نمایید',
            DateTime: {
                selectDateFirst: "لطفا ابتدا تاریخ مورد نظر را انتخاب نمایید",
                selectDay: "لطفا حداقل یک روز و زمانهای آن را انتخاب نمایید",
                success: "زمانهای پیشنهادی شما با موفقیت به ثبت رسیدند",
            },
            clientSessionRequest: 'لطفا یکی از زمانهای مشاور را انتخاب نمایید',
            signupComponent: {
                phoneNumberError: "شماره تلفن معتبر نیست.",
                success: "ثبت نام با موفقیت انجام شد",
                emailError: "ایمیل وارد شده تکراری است",
                passwordError: "کلمه عبور را با دقت وارد کنید",
                passwordLengthError: "حداقل طول کلمه عبور 8 کاراکتر می باشد",
                rePasswordRequired: "پر کردن فیلد تکرار کلمه عبور الزامیست",
                rePasswordCheck: 'کلمه عبور و تکرار آن یکسان نمی باشند',
                phoneRequired: "وارد کردن تلفن الزامیست",
                countrySelected: "کد کشور را انتخاب نمایید",
                passwordRequired: "پر کردن فیلد تکرار کلمه عبور الزامیست",
                notSamePassword: 'کلمه عبور و تکرار آن یکسان نمی باشند',
            },
            login: {
                password: {
                    passwordRequired: "وارد کردن کلمه عبور الزامیست",
                    minLength: "حداقل طول کلمه عبور 8 کاراکتر می باشد"
                }
            },
            acceptTerms: "موافقت با شرایط و ضوابط الزامیست.",
            calender: {
                successDelete: "با موفقیت حذف شد"
            },

            error: 'خطایی رخ داده است',
            connectionError: "لطفا اینترنت خود را بررسی کنید",
            addNewCycle: {
                oneSession: "حداقل تعداد جلسات 1 می باشد.",
                notEnough: "تعداد جلسات شما کافی نیست.",
            },
            clientDashboard: {
                selectTime: "لطفا یکی از زمانهای مشاور را انتخاب نمایید",
                selectOne: "لطفا یک گزینه را انتخاب نمایید",
                success: "زمان های شما با موفقیت به ثبت رسیدند",
                selectTimespan: "لطفا حداقل یک بازه را انتخاب نمایید"
            },
            ExperssComponent: {
                selectOne: "لطفا یکی از زمانهای مشاور را انتخاب نمایید",
                successSelect: "زمانهای پیشنهادی شما با موفقیت به ثبت رسیدند"
            },
            CartComponent: {
                enterCode: "کد تخفیف را وارد نمایید",
                needLogin: "برای خرید ابتدا وارد شوید یا ثبت نام کنید"
            },
            cycle: {
                needPackage: "شما بسته ای برای درخواست جلسه ندارید، ابتدا خرید کنید"
            },
            sessionCountRules: {
                countRequired: "تعداد جلسات را وارد نمایید",
                minCount: "تعداد جلسات حداقل باید 1 باشد",
                buyError: "تعداد جلسات درخواستی باید از جلسات خریداری شده شما کمتر باشد"
            },
            successCopy: 'کپی شد'
        },
        currency: {
            longRial: 'تومان',
            rial: 'T',
            dollar: '$',
            dollarCanada: 'Dollar canada',
            dollarAustralia: 'Dollar australia',
            Euro: '€',
            pound: 'pound',
            dollarLong: "دلار",
            dollarCanadaLong: "دلار کانادا",
            dollarAustraliaLong: "دلار استرالیا",
            EuroLong: "یورو",
            poundLong: "پوند",
            try: 'لیر',
        },
        welcome: 'به پنل کاربری خود خوش آمدید',
        routes: {
            dashboard: 'داشبورد',
            sessions: 'جلسات',
            wallet: 'تراکنش‌ها/کیف پول',
            index: 'صفحه اصلی',
            packages: 'بسته‌ها',
            messenger: "اتاق‌گفتگو",
            profile: 'پرونده من',
            tasks: 'آزمون‌ها/تمرین‌ها',
            userInfo: 'مشخصات و تنظیمات',
            basket: 'سبد‌خرید',
            factor: 'فاکتور',
            factors: "فاکتور‌ها",
            personal_profile: "اطلاعات شخصی",
            roots_in_soil: 'ریشه در خاک',
            tests: "تست‌ها",
            test_history: "تاریخچه‌ تست‌ها",
            chatGpt: 'هوش مصنوعی',
            invitees: 'لیست دعوت‌ها',
            presession_detail: 'جزئیات پیش‌مشاوره',
            myoutcome: 'بازخورد',
            freeSession:'جلسه رایگان حمایتی'
        },
        wallet: {
            charge: 'واریز',
            use: 'برداشت',
            balance: 'موجودی',
            wallet: 'کیف‌پول',
            transaction: 'تراکنش‌ها',
            title: "سبد خرید",
        },
        basket: {
            title: "بسته‌(های) انتخاب شده",
            addGiftCode: "افزودن کد هدیه",
            factor: "مبلغ",
            discountCode: "کد‌تخفیف",
            off: "تخفیف و کد هدیه",
            wallet: "کیف پول",
            finalPrice: "مبلغ نهایی",
            onlinePay: "پرداخت آنلاین",
            offlinePay: "پرداخت آفلاین",
            datatable: {
                title: "نوع بسته",
                qty: "تعداد",
                price: "قیمت‌واحد",
                totalAmount: "قیمت‌واحد"
            }
        },
        singleFactor: {
            title: "بسته‌(های) انتخاب شده",
            addGiftCode: "افزودن کد هدیه",
            factor: "فاکتـــور",
            discountCode: "کد‌تخفیف",
            off: "تخفیف و کد هدیه",
            wallet: "کیف پول",
            remaining: "باقی مانده",
            finalPrice: "مبلغ نهایی",
            onlinePay: "پرداخت آنلاین",
            offlinePay: "پرداخت آفلاین",
            payments: "پرداخت‌ها",
            datatable: {
                title: "نوع بسته",
                qty: "تعداد",
                price: "قیمت‌واحد",
                totalAmount: "قیمت‌کل",
                off: "تخفیف"
            }
        },
        socialLogin: {
            enterNumber: "لطفا شماره تماس خود را وارد کنید"
        },

        dashboard: {
            modal: {
                title: "اطلاعات شما با موفقیت ثبت شد.",
                desc: "",
                action: "تایید"
            },
            chat: {
                action: "شروع گفتگو",
                title: "چت با پشتیبان",
                placeholder: "چیزی بنویسید"
            },
            wallet_status: {
                title: "جلسات باقی مانده از بسته مشاوره:",
                subtitle: " برای خرید بسته از اینجا اقدام کنید ",
                action: " خرید بسته"
            },
            session_status: {
                title: "وضعیت جلسه بعدی شما",
                subtitle: " کیف جلسات شما خالی است. لطفا بسته خریداری کنید ",
                action: "خرید بسته"
            },
            sessions: {
                title: ' جلسات پیش رو ',
                subtitle: ' جلساتی که رزرو وقت شده ',
                action: " جزئیات ",

            },
            walletSession: {
                title: 'مانده اعتبار جلسات شما',
                subtitle: ' تعداد جلساتی که از اعتبار باقی مانده ',
                action: 'خرید بسته'
            },
            wallet: {
                title: 'تراکنش‌ها/کیف پول',
                subTitle: 'افزودن کد هدیه'
            },
            inviteFriends: {
                title: ' حال خوب به دوستت هدیه بده !',
                subtitle: " ‏10 دلار اعتبار هدیه ",
                action: 'معرفی به دوستان'
            },
            tests: {
                title: 'تست‌های رایگان',
                viewAll: 'تست ها',
            }
        },
        CartComponent: {
            PreSessionModal: {
                title: 'پیش‌مشاوره',
                continue: "تایید و ادامه",
                text: {
                    leadUser: [
                        'از آنجایی که انتخاب بهترین مشاور، متناسب با موضوع درخواستی شما، یکی از مهمترین عوامل در نتیجه گیری از جلسات تراپی است، ما مانند بسیاری از مراکز مشاوره تخصصی در دنیا، با برگزاری یک جلسه 15 دقیقه ای پیش مشاوره، تمامی انتظارات شما از مشاور و  جلسات تراپی شنیده شده و بهترین مشاور متخصص به شما معرفی می شود.',
                        ' این جلسه رایگان خواهد بود و طی ۲۴ ساعت آینده همکاران ما برای تعیین زمان مناسب با شما تماس میگیرند.'
                    ],
                    oldUser: [
                        'جهت دریافت لینک پرداخت لطفا به تیم پشتیبانی پیام دهید.',
                        'تماس با تیم پشتیبانی'
                    ]
                },
                action: 'متوجه شدم'
            },
            payByWallet: "کسر از کیف پول",
            clearingWallet: 'تسویه کیف پول',
            invoiceItems: {
                total: "جمع کل",
                offer: "تخفیف",
                finalPrice: "مبلغ نهایی"
            },
            title: "بسته های انتخاب شده",
            price: "مبلغ",
            headersTable: [
                'آیتم',
                'قیمت واحد',
                'تعداد',
                'قیمت کل',
                'حذف'
            ],
        }
    }
}
